<template>
  <div class="content">
    <PageHeader title="出力">
      <template #title-header-content>
        <!-- 「出力」と「書類設定」の切り替え（登録済み一覧の軸のタブと同じ見た目）。初期値は「出力」 -->
        <div class="axis-tabs">
          <button
            v-for="m in visibleModes"
            :key="m.key"
            type="button"
            class="axis-tab"
            :class="mode === m.key ? 'active' : ''"
            @click="mode = m.key"
          >
            <Icon :iconName="m.icon" class="w-4 h-4 mr-1" />{{ m.name }}
          </button>
        </div>

        <!-- 「検索」と更新の矢印は、どのタブでも取得中・出力中などの途中（searchBusy）は押せない（ユーザー指示） -->
        <div v-if="mode === 'output'" class="flex-1 flex items-center justify-end mr-2">
          <PrimaryButton text="検索" class="w-32 search-btn" :disabled="searchBusy" @click="getList(1)" />
          <div class="ml-4 text-lg font-bold text-white">{{ records }} 人</div>
          <Icon
            iconName="Refresh"
            :clickable="false"
            :class="{ 'animate-spin': loading, 'refresh-off': searchBusy }"
            class="ml-4 cursor-pointer text-white hover:text-gray-300"
            @click.native="searchBusy || getList()"
          />
        </div>

        <!-- 給与奉行のタブも同じ場所に検索と人数（出力タブにそろえる。ユーザー指示） -->
        <div v-else-if="mode === 'bugyo'" class="flex-1 flex items-center justify-end mr-2">
          <PrimaryButton text="検索" class="w-32 search-btn" :disabled="searchBusy" @click="bugyoGetList(1)" />
          <div class="ml-4 text-lg font-bold text-white">{{ bugyo.records }} 人</div>
          <Icon
            iconName="Refresh"
            :clickable="false"
            :class="{ 'animate-spin': bugyo.loading, 'refresh-off': searchBusy }"
            class="ml-4 cursor-pointer text-white hover:text-gray-300"
            @click.native="searchBusy || bugyoGetList()"
          />
        </div>

        <!-- 契約書返送のタブも同じ場所に検索と件数 -->
        <div v-else-if="mode === 'contractReturn'" class="flex-1 flex items-center justify-end mr-2">
          <PrimaryButton text="検索" class="w-32 search-btn" :disabled="searchBusy" @click="crGetList(1)" />
          <div class="ml-4 text-lg font-bold text-white">{{ cr.records }} 通</div>
          <Icon
            iconName="Refresh"
            :clickable="false"
            :class="{ 'animate-spin': cr.loading, 'refresh-off': searchBusy }"
            class="ml-4 cursor-pointer text-white hover:text-gray-300"
            @click.native="searchBusy || crGetList()"
          />
        </div>
      </template>

      <template v-if="mode === 'output' || mode === 'bugyo' || mode === 'contractReturn'" #page-header-content>
        <!-- 契約書返送の絞り込み：未返送のみ（初期 ON。未返送が 1 つでもある清掃員の行を全部出す）・出力日が○○以降・名前 -->
        <div v-if="mode === 'contractReturn'" class="flex items-center justify-end gap-2 flex-wrap">
          <label class="seal-toggle bugyo-chk" :class="cr.filter.notReturned ? 'is-on' : ''"><input v-model="cr.filter.notReturned" type="checkbox" class="rounded" @change="crGetList(1)" />未返送の清掃員のみ</label>
          <div class="age-filter" :class="cr.filter.outputFrom ? 'filled' : ''" title="出力日がその日以降の契約書に絞る">
            <span class="age-text">出力日が</span>
            <WmsDateField name="crOutputFrom" caption="" v-model="cr.filter.outputFrom" :clearable="true" class="age-date" @change="crGetList(1)" />
            <span class="age-text">以降</span>
          </div>
          <input
            v-model="cr.filter.name"
            type="search"
            placeholder="清掃員名・フリガナ・コード 検索"
            class="filter-input w-64 rounded border border-gray-300 px-2 text-sm"
            :class="cr.filter.name ? 'filled' : ''"
            @keydown.enter="crGetList(1)"
          />
        </div>

        <!-- 給与奉行の絞り込み：未出力の清掃員のみ（初期 ON）・入社日が○○以降・名前。Enter か検索で取り直す -->
        <div v-else-if="mode === 'bugyo'" class="flex items-center justify-end gap-2 flex-wrap">
          <!-- ON のときは藍色の地に白い太字（この画面の切り替え（会社印あり・枚数・使う）と同じ見た目。黄色だと青いチェックと合わない。ユーザー指示） -->
          <label class="seal-toggle bugyo-chk" :class="bugyo.filter.notExported ? 'is-on' : ''"><input v-model="bugyo.filter.notExported" type="checkbox" class="rounded" @change="bugyoGetList(1)" />未出力の清掃員のみ</label>
          <div class="age-filter" :class="bugyo.filter.hireFrom ? 'filled' : ''" title="入社日がその日以降の人に絞る">
            <span class="age-text">入社日が</span>
            <WmsDateField name="bugyoHireFrom" caption="" v-model="bugyo.filter.hireFrom" :clearable="true" class="age-date" @change="bugyoGetList(1)" />
            <span class="age-text">以降</span>
          </div>
          <input
            v-model="bugyo.filter.name"
            type="search"
            placeholder="清掃員名・フリガナ・コード 検索"
            class="filter-input w-64 rounded border border-gray-300 px-2 text-sm"
            :class="bugyo.filter.name ? 'filled' : ''"
            @keydown.enter="bugyoGetList(1)"
          />
        </div>

        <!-- 絞り込み：清掃員名・コード・カナ、エリア、「●以降に●歳以上になる」、範囲（在職者・退職者・全員）。何かで絞っている欄は黄色 -->
        <div v-else class="flex items-center justify-end gap-2 flex-wrap">
          <input
            v-model="filter.name"
            type="search"
            placeholder="清掃員名・フリガナ・コード 検索"
            class="filter-input w-64 rounded border border-gray-300 px-2 text-sm"
            :class="filter.name ? 'filled' : ''"
            @keydown.enter="getList(1)"
          />

          <!-- エリア（AKBS の稼働管理・月次確認と同じ。エリア管理のエリアを複数選べる。終わっていない雇用指示の物件がそのエリアに当たる人）
               ヘッダーの高さを変えないよう、欄はボタン1つにして、押すとチェックの一覧を画面の上に重ねて出す（閉じたときに検索） -->
          <button
            ref="areaBtn"
            type="button"
            class="filter-input area-btn"
            :class="filter.areas.length ? 'filled' : ''"
            title="エリアで絞る（複数可）"
            @click.stop="toggleAreaPanel"
          >
            {{ areaLabel }}
            <Icon iconName="ChevronDown" class="w-4 h-4 ml-1" />
          </button>

          <!-- 「この日以降に ○歳以上になる」＝ その日の時点で ○歳以上の人（70歳以上に書類を送るときなど）。日と歳の両方が入ったときだけ効く -->
          <div class="age-filter" :class="filter.ageDate && filter.age !== '' ? 'filled' : ''" title="その日の時点で、その歳以上の人に絞る">
            <WmsDateField name="ageDate" caption="" v-model="filter.ageDate" :clearable="true" class="age-date" @change="getList(1)" />
            <span class="age-text">以降に</span>
            <input v-model="filter.age" type="number" min="0" max="120" class="age-input" placeholder="歳" @wheel="$event.target.blur()" @keydown.enter="getList(1)" @change="getList(1)" />
            <span class="age-text">歳以上になる</span>
          </div>

          <select v-model="filter.scope" class="filter-input rounded border-gray-300 text-sm" :class="filter.scope !== 'all' ? 'filled' : ''" title="範囲" @change="getList(1)">
            <option v-for="s in scopes" :key="s.value" :value="s.value">{{ s.name }}</option>
          </select>
        </div>
      </template>
    </PageHeader>

    <!-- エリアの選択（ヘッダーのボタンの下に重ねて出す。外を押すと閉じて検索） -->
    <div v-if="areaPanel.open" class="area-panel" :style="{ top: areaPanel.top + 'px', left: areaPanel.left + 'px' }" @click.stop>
      <div class="area-panel-head">
        <span>エリア（複数可）</span>
        <button type="button" class="link-btn" :disabled="!filter.areas.length" @click="filter.areas = []">全部外す</button>
      </div>
      <div class="area-panel-list">
        <label v-for="a in areas" :key="a.areaId" class="area-item" :class="filter.areas.includes(String(a.areaId)) ? 'is-on' : ''">
          <input type="checkbox" :value="String(a.areaId)" v-model="filter.areas" />
          {{ a.areaName }}
        </label>
        <div v-if="!areas.length" class="text-gray-400 text-sm px-2 py-3">候補にありません</div>
      </div>
      <div class="area-panel-foot">
        <PrimaryButton text="閉じて検索" size="sm" class="w-28" @click="closeAreaPanel(true)" />
      </div>
    </div>

    <!-- 出力中の白いマスク（進み具合の % 付き。終わるまで画面は触れない） -->
    <div v-if="progress.active" class="progress-mask">
      <div class="progress-box">
        <div class="progress-text">{{ progress.text }}</div>
        <!-- 進み具合が分からない処理（全員を選ぶなど）はバーを出さず、文だけ -->
        <template v-if="progress.percent !== null">
          <div class="progress-bar"><div class="progress-fill" :style="{ width: progress.percent + '%' }"></div></div>
          <div class="progress-percent">{{ progress.percent }}%</div>
        </template>
        <div v-else class="progress-percent">しばらくお待ちください…</div>
        <!-- PDF を作っている間は中止できる（受け取りに入ったら出さない）。押してから、描き始めた分が終わるまで少し待つ -->
        <button v-if="progress.jobId" type="button" class="progress-cancel" :disabled="progress.cancelling" @click="cancelPdfJob">
          {{ progress.cancelling ? '中止しています…' : '中止' }}
        </button>
      </div>
    </div>

    <!-- ========== 出力 ========== -->
    <main v-if="mode === 'output'" class="absolute top-32 -mt-1 bottom-4 w-screen">
      <div class="h-full px-4 flex flex-col gap-2">

        <!-- 出力する書類の選択。一式（入社・契約変更…）の名前を押すとその中の書類が全部 ON／OFF、書類の札は1つずつ ON／OFF
             名札・雇用契約書・業務報告書は入社と契約変更で同じ書類なので、どちらで押しても同じ ON になる
             業務報告書は期間、代勤賃金簿は枚数が要るので、ON のときだけ入力欄を出す -->
        <div class="doc-box">
          <div class="doc-box-head">
            <div class="doc-box-title">出力する書類</div>
            <div class="doc-box-note">一式の名前を押すとまとめて選べます。書類の札は1つずつ押せます。</div>
            <div class="flex-1"></div>
            <!-- 会社印が要る得意先の設定（業務報告書にアポロ管財の会社印を入れる得意先。基本は無し。雇用契約書は得意先と関係なく常に印を入れる） -->
            <button type="button" class="seal-btn" title="業務報告書に会社印を入れる得意先を設定します" @click="openSeal">
              <Icon iconName="PencilAlt" class="w-4 h-4 mr-1" />会社印必要な得意先設定
            </button>
            <!-- 排紙が上向き（印刷面が上）のプリンターだと最後のページが一番上になるので、出す書類のページを逆順にできる（初期は ON）
                 雇用契約書（別 PDF・両面）と宛名シール（全員 1 本・片面）には効かせないので、それ以外の書類が ON のときだけ出す（ユーザー指示） -->
            <label v-if="reverseApplies" class="reverse-input" :class="reverse ? 'is-on' : ''" title="清掃員ごとに、最後のページから並べます（印刷面が上に出るプリンター用）。清掃員の並びは変わりません。雇用契約書（別PDF・両面）と宛名シール以外の書類全部に効きます">
              <input v-model="reverse" type="checkbox" />
              印刷用に逆順<span class="reverse-note">（雇用契約書・宛名シール以外適用）</span>
            </label>
            <button v-if="checkedCount || docCount" type="button" class="link-btn" @click="clearAll">全部外す</button>
            <!-- 検索（青）と見分けがつくよう緑にする。選んだ人数はボタンの中に出す（選んでいなければ「出力実行」だけ） -->
            <PrimaryButton :text="checkedCount ? `出力実行（${checkedCount}人選択）` : '出力実行'" class="run-btn" :disabled="!checkedCount || !docCount || downloading" @click="download">
              <Icon slot="before" iconName="Download" class="w-4 h-4 mr-1" />
            </PrimaryButton>
          </div>
          <!-- 雇用契約書は同じものを2枚送る（1枚は本人の控え、1枚は返送）。両面印刷するので、ほかの書類とは別の PDF にする。
               出力実行ボタンの下に右寄せで赤字 -->
          <div v-if="isDocOn('contract')" class="contract-note">
            ※雇用契約書は同じものを必ず2枚セットで出力します。両面印刷のため、ほかの書類とは別のPDFになります。
          </div>
          <!-- サブタスクから開いたときの知らせ（金額のみ変更の雇用契約書の ON／OFF など） -->
          <div v-if="linkNotice" class="contract-note">※{{ linkNotice }}</div>

          <!-- 1行目：一式のボタン（横並び）と、間を空けて宛先（書類ではないので色を変える）
               2行目：書類の札を書類設定の並びで（折り返し）。同じ書類は1つだけ。入社と契約変更で共通のものはどちらの一式でも ON になる -->
          <div class="doc-sets">
            <button
              v-for="set in docSets"
              :key="set.key"
              type="button"
              class="set-btn"
              :class="setState(set)"
              :title="set.docs.map(k => docName(k)).join('・')"
              @click="toggleSet(set)"
            >
              <span class="set-check">{{ setState(set) === 'all' ? '✓' : '' }}</span>
              {{ set.name }}
            </button>

            <div class="addr-group">
              <span class="addr-group-name">宛先</span>
              <button
                v-for="key in addrKeys"
                :key="key"
                type="button"
                class="addr-chip"
                :class="isDocOn(key) ? 'is-on' : ''"
                @click="toggleDoc(key)"
              >
                {{ docName(key) }}
              </button>
            </div>
          </div>
          <div class="doc-chips-area">
            <div class="doc-chips">
              <div class="doc-row">
                <button
                  v-for="key in docList"
                  :key="key"
                  type="button"
                  class="doc-chip"
                  :class="isDocOn(key) ? 'is-on' : ''"
                  :title="setNamesOf(key)"
                  @click="toggleDoc(key)"
                >
                  {{ docName(key) }}
                </button>
                <span v-if="!docList.length" class="text-sm text-gray-400">書類設定に「使う」書類がありません</span>
              </div>
            </div>
          </div>

          <!-- 選んだ書類に要る入力（業務報告書（予定・実績）：期間、雇用契約書：契約期間、枚数の入力欄が「必要」の書類（代勤賃金簿）：枚数） -->
          <div v-if="needReportPeriod || isDocOn('contract') || copiesDocs.length || isDocOn('addrLabel')" class="doc-inputs">
            <!-- 雇用契約書の契約期間（自動で入れる。直せる）。雇用そのものの期間なので、人ごとに、開始日は入社日が後ならそちら、終了日は退職日が前ならそちら（物件の作業開始・終了は見ない）。
                 既定は 7/1〜翌 6/30、70 歳以上（開始日時点）は 7/1〜12/31（送付が 7 月・12 月なので。お客様に確認中の仮の決まり） -->
            <div v-if="isDocOn('contract')" class="doc-input doc-input-period" :class="contractPeriodOk ? '' : 'missing'">
              <div class="doc-input-title"><span class="doc-input-name">雇用契約書の契約期間</span><span class="doc-input-small">物件・契約コードごと</span></div>
              <div class="doc-input-fields">
                <WmsDateField name="contractFrom" caption="" v-model="docInput.contractFrom" class="date-field" />
                <span>〜</span>
                <WmsDateField name="contractTo" caption="" v-model="docInput.contractTo" class="date-field" />
                <span class="doc-input-sub">70歳以上の終了日</span>
                <WmsDateField name="contractTo70" caption="" v-model="docInput.contractTo70" class="date-field" />
                <span v-if="!contractPeriodOk" class="doc-input-hint">開始日・終了日・70歳以上の終了日を入れてください（開始は終了より前）</span>
              </div>
              <!-- 契約期間の決め方（入社日・退職日・70 歳の判定）を欄の下に ※ で知らせる -->
              <div class="doc-input-note">
                ※入社日・退職日を考慮&#x3000;※契約期間の開始日の時点で70歳以上なら「70歳以上の終了日」
              </div>
              <div class="doc-input-note">
                ※時給・通勤手当は契約期間の開始日の時点の金額
              </div>
            </div>
            <!-- 期間は年月（日は要らない。ユーザー指示）。予定と実績で期間は別（予定は 5 月に全員分を送る用で一番近い 4 月まで、実績は退職のときにハローワークへ出す用で過去 1 年分。ユーザー決定）
                 予定の開始の初期値は雇用契約書の契約期間の開始と同じ年月、終了は一番近い 4 月。空にした欄は赤くする
                 年齢で期間は変えない（70 歳以上の終了月は無し。全員同じ期間。ユーザー決定） -->
            <div v-if="isDocOn('report')" class="doc-input doc-input-period" :class="reportPeriodOk ? '' : 'missing'">
              <div class="doc-input-title"><span class="doc-input-name">業務報告書（予定）の期間</span><span class="doc-input-small">物件・契約コードごと</span></div>
              <div class="doc-input-fields">
                <input v-model="docInput.reportFrom" type="month" class="month-input" :class="{ empty: !docInput.reportFrom }" />
                <span>〜</span>
                <input v-model="docInput.reportTo" type="month" class="month-input" :class="{ empty: !docInput.reportTo }" />
                <!-- 期間の月数（1 勤務あたりの枚数の目安） -->
                <span v-if="reportMonths" class="doc-input-months">{{ reportMonths }}か月</span>
                <span v-if="reportPeriodHint" class="doc-input-hint">{{ reportPeriodHint }}</span>
              </div>
              <!-- 用紙を作る月の決め方（作業開始日・作業終了日・退職日、効いている日が無い月）を欄の下に ※ で知らせる -->
              <div class="doc-input-note">
                ※作業開始日・作業終了日・退職日を考慮（勤務が1日も無い月は作りません）
              </div>
              <!-- 業務報告書（予定）は代勤専門の勤務には作らないので、代わりに代勤賃金簿（1 枚＝1 か月）を期間の月数分、その人の束の最後に付ける（初期 ON。外せる。ユーザー決定）
                   用紙は書類設定の専用の行「業務報告書に付ける代勤賃金簿」の PDF（札の「代勤賃金簿」とは別の置き場。札と関係なく付ける）。PDF が無ければ切り替えの代わりに知らせる -->
              <template v-if="isDocOn('report')">
                <div v-for="a in autoSheets" :key="a.key" class="doc-input-note op-sheets-row">
                  <label v-if="a.ready" class="seal-toggle op-sheets" :class="a.on ? 'is-on' : ''">
                    <input v-model="docInput.autoSheets[a.key]" type="checkbox" />
                    {{ a.label }}
                  </label>
                  <!-- 札の「代勤賃金簿」も ON だと代勤専門の人に 2 回付くので知らせる（札かどうかは札の文字で見る。ユーザー決定） -->
                  <span v-if="a.key === 'opSheet' && opSheetDouble" class="op-sheets-none">※札の「{{ opSheetDouble }}」も ON です。代勤専門の人には両方が付きます</span>
                  <span v-if="!a.ready" class="op-sheets-none">※「{{ a.label }}」の PDF がありません。書類設定の「{{ a.name }}」に PDF を登録すると付けられます</span>
                </div>
              </template>
            </div>
            <!-- 業務報告書（実績）の期間（初期は過去 1 年分：今月を含めて 12 か月。実績は今日までしか無い） -->
            <div v-if="isDocOn('reportActual')" class="doc-input doc-input-period" :class="actualPeriodOk ? '' : 'missing'">
              <div class="doc-input-title"><span class="doc-input-name">業務報告書（実績）の期間</span><span class="doc-input-small">物件・契約コードごと</span></div>
              <div class="doc-input-fields">
                <input v-model="docInput.actualFrom" type="month" class="month-input" :class="{ empty: !docInput.actualFrom }" />
                <span>〜</span>
                <input v-model="docInput.actualTo" type="month" class="month-input" :class="{ empty: !docInput.actualTo }" />
                <span v-if="actualMonths" class="doc-input-months">{{ actualMonths }}か月</span>
                <span v-if="actualPeriodHint" class="doc-input-hint">{{ actualPeriodHint }}</span>
              </div>
              <div class="doc-input-note">
                ※作業開始日・作業終了日・退職日を考慮（今日までに勤務が1日も無い月は作りません）
              </div>
              <!-- 業務報告書（実績）の形式：PDF（書類一式の中）か Excel（別ファイル。1 シート＝1 人の 1 か月、1 ページに収まる印刷設定。ハローワーク用に手で直せる）。初期は Excel -->
              <div class="doc-input-note op-sheets-row">
                <span class="fmt-caption">形式</span>
                <button v-for="f in actualFormats" :key="f.key" type="button" class="fmt-btn" :class="docInput.actualFormat === f.key ? 'is-on' : ''" @click="docInput.actualFormat = f.key">{{ f.name }}</button>
                <span v-if="docInput.actualFormat === 'excel'" class="fmt-note">1シート＝1人のひと月分</span>
              </div>
            </div>
            <!-- 宛名シール（12 面）の印字開始位置：シートの図（2 列 × 6 段）を押して選ぶ（AKBS の宛名印刷と同じ。ユーザー決定） -->
            <div v-if="isDocOn('addrLabel')" class="doc-input doc-input-period label-input" :class="docInput.labelStart === null ? 'missing' : ''">
              <div class="doc-input-title"><span class="doc-input-name">宛名シールの印字開始位置</span></div>
              <div class="doc-input-fields">
                <!-- 初期値は無し。選ぶまで赤で「未選択」（ユーザー指示：必ず選ばせる） -->
                <span v-if="docInput.labelStart === null" class="label-start label-start-none">未選択</span>
                <span v-else class="label-start">{{ docInput.labelStart + 1 }} 面目から</span>
                <button type="button" class="link-btn" @click.stop="labelPick = !labelPick">{{ labelPick ? '閉じる' : (docInput.labelStart === null ? '選ぶ' : '選び直す') }}</button>
              </div>
              <!-- シートの図（2 列 × 6 段。縦のまま）は選ぶときだけ枠に重ねて出す（常に出すと枠が高くなる。ユーザー指示）。面を押すと閉じる -->
              <div v-if="labelPick" class="label-pop" @click.stop>
                <div class="label-pop-title">1 ページ目をどの面から印字しますか（2 ページ目以降は 1 から）</div>
                <div class="label-grid">
                  <button
                    v-for="i in 12"
                    :key="i"
                    type="button"
                    class="label-cell"
                    :class="docInput.labelStart === i - 1 ? 'is-on' : ''"
                    @click="pickLabelStart(i - 1)"
                  >{{ i }}</button>
                </div>
              </div>
            </div>
            <!-- 枚数（書類設定で「枚数の入力欄：必要」の書類ごと。空のまま始めて赤くする） -->
            <div v-for="k in copiesDocs" :key="k" class="doc-input doc-input-period" :class="copiesOkOf(k) ? '' : 'missing'">
              <span class="doc-input-name">{{ docName(k) }}の枚数</span>
              <div class="doc-input-fields">
                <input :value="docInput.copies[k]" type="number" min="1" max="99" class="sheets-input" :class="{ empty: !copiesOkOf(k) }" @input="$set(docInput.copies, k, $event.target.value)" @wheel="$event.target.blur()" />
                <span>枚 / 人</span>
              </div>
            </div>
          </div>
        </div>

        <div class="flex-1 min-h-0 overflow-auto rounded-md border border-gray-200 bg-white">
          <!-- 出す書類と必要な入力がそろうまで一覧は出さない（書類 → 期間などの入力 → 人、の順で確実に選んでもらう。ユーザー 2026-10-09） -->
          <div v-if="!readyForList" class="flex h-full items-center justify-center font-bold text-red-600 text-center leading-8">
            {{ docCount ? notReadyReason : '出力する書類を選んでください' }}
          </div>

          <div v-else-if="loading" class="flex h-full items-center justify-center font-bold text-gray-400">
            Searching...
          </div>

          <div v-else-if="!list.length" class="flex h-full items-center justify-center font-bold text-gray-400">
            対象データがありません。
          </div>

          <table v-else class="staff-table">
            <thead>
              <tr>
                <!-- 見出しのチェックで、絞り込みに当たる全員（ページをまたいで）を選ぶ・外す（「このページだけ」はまず無い。ユーザー指示） -->
                <th class="check">
                  <input type="checkbox" :checked="allFilteredChecked" :indeterminate.prop="someCheckedInPage && !allFilteredChecked" :disabled="selectingAll" :title="`検索にヒットした${records}人全員を選ぶ・外す`" @change="toggleAllFiltered($event.target.checked)" />
                </th>
                <th>清掃員コード</th>
                <th class="text-left">清掃員名</th>
                <th class="text-left">エリア</th>
                <th>生年月日</th>
                <th>顔写真</th>
                <!-- 物件名・契約コード（その人の終わっていない雇用指示）は常に出す。業務報告書（予定）・雇用契約書は勤務ごとに出すので、そのときだけチェックできる -->
                <th class="text-left emp">
                  <!-- 見出しのチェックで、このページの全員の勤務を全部 ON（人も ON）／全部 OFF -->
                  <input v-if="needEmployment" type="checkbox" class="mr-2" :checked="allEmpCheckedInPage" :indeterminate.prop="someEmpCheckedInPage && !allEmpCheckedInPage" title="このページの全員の勤務を全部選ぶ・外す" @change="toggleAllEmpInPage($event.target.checked)" />
                  物件名・契約コード<span v-if="needEmployment" class="th-note">（業務報告書・雇用契約書・名札用）</span>
                </th>
                <th class="text-left memo">出力に関する備考（自動で保存）</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="row in list"
                :key="row.staffDailyCleaning"
                :class="[isChecked(row) ? 'checked' : '', isRetired(row) ? 'retired' : '']"
                @click="toggle(row)"
              >
                <td class="check" @click.stop>
                  <input type="checkbox" :checked="isChecked(row)" @change="setChecked(row, $event.target.checked)" />
                </td>
                <td class="code">{{ row.staffDailyCleaning }}</td>
                <!-- 名前は太字・青で、押すと登録済み一覧の清掃員の内容が別タブで開く。カナは名前の上に小さく -->
                <td class="text-left" @click.stop>
                  <div class="kana">{{ row.nameKanaStaff }}</div>
                  <a :href="staffLink(row)" target="_blank" rel="noopener" class="name-link" title="登録済み一覧で清掃員の内容を別タブで開く">{{ row.nameStaff }}</a>
                  <span v-if="row.isCancelled" class="tag cancelled">入社取消</span>
                  <span v-else-if="isRetired(row)" class="tag retired">退職</span>
                </td>
                <td class="text-left area-cell">{{ row.area || '-' }}</td>
                <td class="birthday">
                  <template v-if="row.birthday">{{ slash(row.birthday) }}<span class="age">（{{ age(row.birthday) }}歳）</span></template>
                  <template v-else>-</template>
                </td>
                <!-- 顔写真（名札の野村・三菱に入る）。あり／なしと、登録・差し替え・削除。押したらその場で保存（ユーザー指示：登録は出力画面で） -->
                <td class="photo" @click.stop>
                  <!-- 写真が要る人（野村・三菱の物件で働いている）で無ければ赤く「なし・必要」。要らない人の「なし」は灰色 -->
                  <span
                    :class="row.hasPhoto ? 'photo-yes' : row.needsPhoto ? 'photo-need' : 'photo-no'"
                    :title="row.needsPhoto ? '野村・三菱の名札に顔写真が入るので必要です' : '野村・三菱の物件が無いので今は要りません'"
                  >{{ row.photoSaving ? '保存中…' : row.hasPhoto ? 'あり' : row.needsPhoto ? 'なし・必要' : 'なし' }}</span>
                  <div class="photo-btns">
                    <label class="link-btn" :title="row.hasPhoto ? '顔写真を別のものに変更します' : '顔写真を登録します（JPEG・PNG。名札用に小さくして保存します）'">
                      <input type="file" accept=".jpg,.jpeg,.png" class="hidden" :disabled="row.photoSaving" @change="pickPhoto(row, $event)" />{{ row.hasPhoto ? '変更' : '登録' }}
                    </label>
                    <button v-if="row.hasPhoto" type="button" class="link-btn danger" :disabled="row.photoSaving" @click="deletePhoto(row)">削除</button>
                  </div>
                </td>
                <!-- 物件名・契約コード（＋開始日）。常に出し、業務報告書（予定）・雇用契約書が ON のときだけチェックできる（行のチェックと同じ見た目。ユーザー指示 2026-10-09）
                     チェックは人の選択（行のチェック）と連動する -->
                <!-- 出すのは、業務報告書が ON ならその期間に効いている雇用指示、そうでなければ終わっていない雇用指示（ユーザー指示 2026-10-09） -->
                <td class="emp" @click.stop>
                  <div v-if="!empVisible(row).length" class="emp-none">{{ (row.employments || []).length && empPeriods.length ? '期間に勤務なし' : '勤務なし' }}</div>
                  <div v-else class="emp-list">
                    <!-- 「代勤」の枚（実績だけ）と代勤専門の雇用指示（契約書用。startDate がある）は物件名の代わりに説明を出す。契約コードは無い -->
                    <label
                      v-for="e in empVisible(row)"
                      :key="empKey(e)"
                      class="emp-item"
                      :class="[isEmpOn(row, e) ? 'is-on' : '', needEmployment ? '' : 'view', e.isOp ? 'op' : '']"
                      :title="e.isOp ? (e.startDate ? '代勤専門の雇用指示。雇用契約書・名札に使います。業務報告書（予定）は作らず、代わりに代勤賃金簿を期間の月数分付けます（期間の枠の下の切り替えが ON のとき）。業務報告書（実績）が ON なら「代勤」の枚もこれで出します' :'業務報告書（実績）だけ。その人が別の物件で働いた代勤（●：代勤OK・スポット作業）と、代勤専門の有休を 1 枚に。期間に無ければ用紙は作られません') : ''"
                    >
                      <input v-if="needEmployment" type="checkbox" :checked="isEmpOn(row, e)" @change="toggleEmp(row, e)" />
                      <span class="emp-site">{{ e.siteName }}</span>
                      <!-- 開始日・終了日は出さない（同じ物件・契約コードの一番早い開始日なので、終わった雇用指示の日付になり紛らわしい。ユーザー指示） -->
                      <span v-if="!e.isOp" class="emp-con">契約{{ e.cdContract }}</span>
                    </label>
                  </div>
                </td>
                <!-- 備考はその場で直せる（欄を離れたら保存。この画面だけの覚え書きで、AKBS には書かない） -->
                <td class="memo" @click.stop>
                  <!-- 改行した分だけ高さが自動で伸びる（CSS の field-sizing: content。ほかの画面と同じ） -->
                  <textarea
                    v-model="row.memo"
                    rows="1"
                    class="memo-input"
                    placeholder="出力メモ"
                    @focus="row.memoBefore = row.memo"
                    @blur="saveMemo(row)"
                  ></textarea>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- ページネーション（登録済み一覧と同じ部品。ページを変えても選んだ人は残る） -->
        <div class="my-1 w-fit mx-auto">
          <pagination id="pagenation" v-model="page" :per-page="perPage" :records="records" class="text-center" @paginate="getList()" />
        </div>
      </div>
    </main>

    <!-- ========== 給与奉行 ==========
         給与奉行インポート（清掃員情報）の CSV。入社した人を複数人まとめて出す（ユーザー指示）。在職者だけ
         出した人は RP の tbl_akbs_bugyo_export に控え、初期値は「まだ出していない人」。同じ人をもう一度出せば出力日が上書き -->
    <main v-else-if="mode === 'bugyo'" class="absolute top-32 -mt-1 bottom-4 w-screen">
      <div class="h-full px-4 flex flex-col gap-2">
        <div class="doc-box">
          <div class="doc-box-head bugyo-head">
            <div class="doc-box-title">給与奉行インポート（清掃員情報）</div>
            <div class="doc-box-note">
              入社した人の情報を給与奉行に取り込む CSV（Shift-JIS）です。
              <!-- 出せるのは人事総務経理（兼任含む）と管理者だけ（ユーザー指示）。ほかの人は一覧を見るだけ -->
              <span v-if="!bugyoCanExport" class="bugyo-no-auth">CSV を出せるのは人事総務経理に所属している人だけです（閲覧のみ）。</span>
            </div>
            <div class="flex-1"></div>
            <!-- 所属部門（ECHA003）の決め方は一覧には出さず、ここから見る（ユーザー指示） -->
            <button type="button" class="link-btn dept-help-btn" :title="bugyoDeptHelpText" @click="bugyoDeptHelp">
              <Icon iconName="QuestionMarkCircle" class="w-4 h-4 mr-1" />所属部門の決め方
            </button>
            <!-- 住民税・提出先（ERET002）の市町村マスタ。給与奉行に登録してある市町村と合わせる（見るのは誰でも、直すのは人事総務経理） -->
            <button type="button" class="seal-btn" @click="bugyoOpenCity">
              <Icon iconName="OfficeBuilding" class="w-4 h-4 mr-1" />提出先市町村
            </button>
            <template v-if="bugyoCanExport">
              <div class="checked-count" :class="bugyoCheckedCount ? 'is-on' : ''">選択 {{ bugyoCheckedCount }} 人</div>
              <button v-if="bugyoCheckedCount" type="button" class="link-btn" @click="bugyo.checked = {}">全部外す</button>
              <PrimaryButton text="CSV を出す" class="w-36 run-btn" :disabled="!bugyoCheckedCount" @click="bugyoDownload">
                <Icon slot="before" iconName="Download" class="w-4 h-4 mr-1" />
              </PrimaryButton>
            </template>
          </div>
        </div>

        <div class="flex-1 min-h-0 overflow-auto rounded-md border border-gray-200 bg-white">
          <div v-if="bugyo.loading" class="flex h-full items-center justify-center font-bold text-gray-400">Searching...</div>
          <div v-else-if="!bugyo.list.length" class="flex h-full items-center justify-center font-bold text-gray-400">対象データがありません。</div>
          <table v-else class="staff-table bugyo-table">
            <thead>
              <tr>
                <th class="check">
                  <input v-if="bugyoCanExport" type="checkbox" :checked="bugyoAllInPage" :indeterminate.prop="bugyoSomeInPage && !bugyoAllInPage" title="このページの全員を選ぶ・外す" @change="bugyoToggleAllInPage($event.target.checked)" />
                </th>
                <th>清掃員コード</th>
                <th class="text-left name">清掃員名</th>
                <th>入社日</th>
                <th class="text-left">物件名</th>
                <th class="text-left">出力</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in bugyo.list" :key="row.staffDailyCleaning" :class="[bugyoIsChecked(row) ? 'checked' : '', row.exportDate ? 'exported' : '', bugyoCanExport ? '' : 'view']" @click="bugyoCanExport && bugyoToggle(row)">
                <td class="check" @click.stop>
                  <input v-if="bugyoCanExport" type="checkbox" :checked="bugyoIsChecked(row)" @change="bugyoSetChecked(row, $event.target.checked)" />
                </td>
                <td class="code">{{ row.staffDailyCleaning }}</td>
                <!-- 名前の列は一番長い名前の幅だけ（広いと、つい名前の所を押してしまう。ユーザー指示）。行のクリックを止めるのはリンクの文字だけで、カナや余白を押せばチェックが切り替わる -->
                <td class="text-left name">
                  <div class="kana">{{ row.nameKanaStaff }}</div>
                  <a :href="staffLink(row)" target="_blank" rel="noopener" class="name-link" title="登録済み一覧で清掃員の内容を別タブで開く" @click.stop>{{ row.nameStaff }}</a>
                </td>
                <td class="birthday">{{ row.dateHire ? slash(row.dateHire) : '-' }}</td>
                <td class="text-left">{{ row.sites || '-' }}</td>
                <!-- 出した日時と誰が。出していなければ赤字で「未」 -->
                <td class="text-left">
                  <span v-if="row.exportDate" class="export-done">{{ row.exportDate }}<span class="export-user">{{ row.exportUserName }}</span></span>
                  <span v-else class="export-not">未</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="my-1 w-fit mx-auto">
          <pagination id="bugyoPagenation" v-model="bugyo.page" :per-page="bugyo.perPage" :records="bugyo.records" class="text-center" @paginate="bugyoGetList()" />
        </div>
      </div>
    </main>

    <!-- ========== 契約書返送 ==========
         雇用契約書を出力した 1 通ごとの記録（RP の tbl_akbs_contract_output）。清掃員から返送されたら「返送済み」にする（押した瞬間に保存）
         未返送が 1 つでもある清掃員は、その人の行を全部出す（返送済みの行は薄いグレー）。備考は行ごと（欄を離れたら保存）
         契約書の QR コード（バーコードリーダー・スマホのカメラ）を読むと、その契約書が返送済みになる -->
    <main v-else-if="mode === 'contractReturn'" :class="isMobileOrTablet ? 'cr-main-mobile' : 'absolute top-32 -mt-1 bottom-4 w-screen'">
      <div class="h-full px-4 flex flex-col gap-2">
        <div class="doc-box">
          <div class="doc-box-head bugyo-head">
            <div class="doc-box-title">雇用契約書の返送確認</div>
            <div class="doc-box-note">
              出力した雇用契約書が1通＝1行で表示（同じ契約期間の場合、出力しなおしても行は追加されません）<br />
              契約書の QR コードを読むと、その契約書を返送済みにできます。
            </div>
            <div class="flex-1"></div>
            <!-- 返送の確認の入口。「出力実行」と同じ色・大きさ（ユーザー指示） -->
            <PrimaryButton text="QR コード読込" class="w-44 whitespace-nowrap run-btn" @click="crOpenQr">
              <Icon slot="before" iconName="Qrcode" class="w-6 h-6 mr-2" />
            </PrimaryButton>
          </div>
        </div>

        <div class="flex-1 min-h-0 overflow-auto rounded-md border border-gray-200 bg-white">
          <div v-if="cr.loading" class="flex h-full items-center justify-center font-bold text-gray-400">Searching...</div>
          <div v-else-if="!cr.list.length" class="flex h-full items-center justify-center font-bold text-gray-400">対象データがありません。</div>
          <table v-else class="staff-table cr-table">
            <thead>
              <tr>
                <th>清掃員コード</th>
                <th class="text-left">清掃員名</th>
                <th class="text-left">物件名・契約コード</th>
                <th>契約期間</th>
                <th class="text-left">出力</th>
                <th class="text-left">返送</th>
                <th class="text-left memo">備考</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in cr.list" :key="row.id" :class="[row.returnedDate ? 'returned' : '', isRetired(row) ? 'retired' : '']">
                <td class="code">{{ row.staffDailyCleaning }}</td>
                <td class="text-left">
                  <div class="kana">{{ row.nameKanaStaff }}</div>
                  <a :href="staffLink(row)" target="_blank" rel="noopener" class="name-link" title="登録済み一覧で清掃員の内容を別タブで開く">{{ row.nameStaff }}</a>
                  <span v-if="row.isCancelled" class="tag cancelled">入社取消</span>
                  <span v-else-if="isRetired(row)" class="tag retired">退職</span>
                </td>
                <td class="text-left">
                  {{ row.siteName || '-' }}<span v-if="row.cdContract" class="cr-contract">{{ row.cdContract }}</span>
                  <!-- 返送済みの行でも、同じ清掃員・同じ契約開始日（同じ回の送付）にまだ返送されていない契約書があれば赤で知らせる（物件名の下。返送の列に置くと表が広がる。ユーザー指示） -->
                  <div v-if="row.returnedDate && row.sameStartUnreturned" class="cr-other-unreturned">返送未確認の契約書がほかにあります</div>
                </td>
                <td class="birthday">{{ slash(row.startDate) }} ～ {{ row.endDate ? slash(row.endDate) : '' }}</td>
                <!-- 出力した人は画面に出さない（表には残る。ユーザー指示） -->
                <td class="text-left"><span class="export-done">{{ row.outputDate }}</span></td>
                <!-- 返送済み：押した瞬間に保存。戻すときは確認 -->
                <td class="text-left">
                  <label class="seal-toggle cr-ret" :class="row.returnedDate ? 'is-on' : ''">
                    <input type="checkbox" :checked="!!row.returnedDate" :disabled="row.saving" @change="crSetReturned(row, $event.target.checked, $event.target)" />
                    {{ row.returnedDate ? '返送済み' : '未返送' }}
                  </label>
                  <!-- 返送済みにした人は画面に出さない（日付だけ。表には残る。ユーザー指示） -->
                  <div v-if="row.returnedDate" class="cr-ret-info">{{ slash(row.returnedDate) }}</div>
                </td>
                <td class="memo">
                  <textarea
                    v-model="row.memo"
                    rows="1"
                    class="memo-input"
                    placeholder="備考"
                    @focus="row.memoBefore = row.memo"
                    @blur="crSaveMemo(row)"
                  ></textarea>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="my-1 w-fit mx-auto">
          <pagination id="crPagenation" v-model="cr.page" :per-page="cr.perPage" :records="cr.records" class="text-center" @paginate="crGetList()" />
        </div>
      </div>
    </main>

    <!-- ========== 書類設定 ==========
         出力画面に出す書類を全部 1 つの表で持つ。名前・どの一式に入るか・並び・使う／使わない。この並びがそのまま出力画面の札の順（ユーザー指示）
         総務が用意する固定の書類は PDF をアップする（S3）。システムが作る書類（業務報告書・雇用契約書・名札…）は同じ表に並ぶがファイルは無く、消せない
         保存の処理はこれから。今は見た目だけ -->
    <main v-else class="absolute top-20 -mt-1 bottom-4 w-screen">
      <div class="h-full px-4 flex flex-col gap-2">
        <div class="doc-box">
          <div class="doc-box-head">
            <div class="doc-box-title">書類設定</div>
            <div class="doc-box-note">「出力」に出す書類の名前・一式・並びを決めます。「出力」の書類は PDF のみ保存可能です。この並びが出力時に適用されます。<br />保存は行ごとです（▲▼の並び替えと削除はすぐ保存されます）。</div>
            <div class="flex-1"></div>
            <button type="button" class="seal-btn" @click="addSettingDoc">
              <Icon iconName="Plus" class="w-4 h-4 mr-1" />行を追加
            </button>
          </div>
        </div>

        <div class="flex-1 min-h-0 overflow-auto rounded-md border border-gray-200 bg-white">
          <table class="setting-table">
            <thead>
              <tr>
                <th class="order">並び</th>
                <th class="text-left name">書類名</th>
                <th class="text-left">一式（どの一式を押したときに ON になるか）</th>
                <th class="text-left file"><span class="file-note">出力したい資料はPDF、原本として保存したい資料はWord/Excel</span></th>
                <th class="add">追加</th>
                <th>枚数の入力欄</th>
                <th>出力で使う</th>
                <th class="save">保存</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(d, i) in setting.docs" :key="d.key" :class="[d.system ? 'system' : '', d.autoSheet ? 'op-sheet' : '', d.enabled ? '' : 'off', d.dirty ? 'dirty' : '']">
                <!-- 並び（出力画面の札の順） -->
                <td class="order">
                  <button type="button" class="order-btn" :disabled="i === 0" title="上へ" @click="moveSettingDoc(i, -1)">▲</button>
                  <button type="button" class="order-btn" :disabled="i === setting.docs.length - 1" title="下へ" @click="moveSettingDoc(i, 1)">▼</button>
                </td>
                <td class="name">
                  <input v-model="d.name" type="text" class="setting-input" :class="{ empty: !String(d.name || '').trim() }" placeholder="書類名（出力の札に出ます）" @input="d.dirty = true" />
                  <span v-if="d.system" class="tag-system" title="システムが中身を作る書類です。名前・一式・並びは変えられます">システム</span>
                  <span v-if="d.autoSheet" class="tag-system" :title="autoSheetOf(d.autoSheet).tip">業務報告書用</span>
                </td>
                <!-- 一式：入社・契約変更・物件終了・退職のどれに入るか（複数可。どれにも入れなければ札を 1 つずつ押す書類）
                     業務報告書に自動で付ける書類は札に出さないので一式は無く、何に使うかを書く -->
                <td v-if="d.autoSheet" class="sets">
                  <span class="file-system">{{ autoSheetOf(d.autoSheet).use }}</span>
                </td>
                <td v-else class="sets">
                  <label v-for="s in docSets" :key="s.key" class="set-chip" :class="d.sets.includes(s.key) ? 'is-on' : ''">
                    <input v-model="d.sets" type="checkbox" :value="s.key" @change="d.dirty = true" />
                    {{ s.name }}
                  </label>
                </td>
                <!-- ファイル：今のファイル（名前・大きさ・更新日・更新者）と、差し替えるファイルの選択。PDF だけ。システムが作る書類はファイル無し -->
                <td v-if="d.system" class="file" colspan="2">
                  <span class="file-system">システムが作ります</span>
                </td>
                <!-- 1 つの書類にファイルを複数持てる。PDF は並び（▲▼）の順に出力でつなげる。Word・Excel は原本として置くだけ（出力には使わない。ユーザー決定）
                     差し替え・削除は 1 つずつ、追加は複数選べる -->
                <template v-else>
                  <td class="file">
                    <div v-if="!d.files.length" class="file-none">ファイルがまだありません</div>
                    <div v-for="(f, fi) in d.files" :key="f.no || ('n' + fi)" class="file-row" :class="[f.newFile ? 'is-new' : '', f.isPdf ? '' : 'is-source']">
                      <span class="file-order">
                        <button type="button" class="order-btn" :disabled="fi === 0" title="上へ" @click="moveSettingFile(d, fi, -1)">▲</button>
                        <button type="button" class="order-btn" :disabled="fi === d.files.length - 1" title="下へ" @click="moveSettingFile(d, fi, 1)">▼</button>
                      </span>
                      <Icon iconName="DocumentText" class="w-4 h-4 mr-1" :class="f.isPdf ? 'text-red-500' : 'text-gray-400'" />
                      <span class="file-name">{{ f.newFile ? f.newFile.name : f.fileName }}</span>
                      <!-- 大きさ・更新日・上げた人は出さない（ユーザー指示）。表には残している -->
                      <span v-if="f.newFile" class="file-new">{{ f.no ? '差し替え' : '追加' }}</span>
                      <label class="file-pick small" title="このファイルを別のものに差し替えます">
                        <input type="file" :accept="FILE_ACCEPT" class="hidden" @change="pickSettingFile(d, f, $event)" />差し替え
                      </label>
                      <!-- PDF は別のタブで確認、Word・Excel はダウンロード（ブラウザでは開けない） -->
                      <button v-if="f.no && !f.newFile && f.isPdf" type="button" class="link-btn" title="別のタブで PDF を開きます" @click="openSettingFile(d, f)">確認</button>
                      <button v-if="f.no && !f.newFile && !f.isPdf" type="button" class="link-btn" @click="downloadSettingFile(d, f)">ダウンロード</button>
                      <button type="button" class="link-btn danger" @click="removeSettingFile(d, fi)">削除</button>
                    </div>
                    <!-- Word・Excel があるときだけ（ユーザー指示） -->
                    <div v-if="d.files.some(f => !f.isPdf)" class="file-hint">●Word・Excel は出力画面では使用されません。</div>
                  </td>
                  <!-- ファイルの追加は別の列（ファイルの数が違っても、どの行でもボタンが同じ位置にあるように。ユーザー OK）。複数選べる -->
                  <td class="add">
                    <label class="file-pick" title="この書類にファイルを追加します（複数選べます。保存で反映）">
                      <input type="file" :accept="FILE_ACCEPT" class="hidden" multiple @change="addSettingFiles(d, $event)" />
                      <Icon iconName="Upload" class="w-4 h-4 mr-1" />資料追加
                    </label>
                  </td>
                </template>
                <!-- 業務報告書に自動で付ける書類は枚数（期間の月数で決まる）も「使う」も無い -->
                <template v-if="d.autoSheet">
                  <td><span class="file-system">期間の月数分</span></td>
                  <td><span class="file-system">—</span></td>
                </template>
                <template v-else>
                  <!-- 代勤賃金簿のように、出力のときに 1 人あたりの枚数を入れる書類 -->
                  <td>
                    <label class="seal-toggle" :class="d.copiesInput ? 'is-on' : ''" title="出力のときに 1 人あたりの枚数を入れる欄を出します">
                      <input v-model="d.copiesInput" type="checkbox" @change="d.dirty = true" />
                      {{ d.copiesInput ? '必要' : '不要' }}
                    </label>
                  </td>
                  <!-- 使わない書類は出力画面に出さない（消さずに残せる） -->
                  <td>
                    <label class="seal-toggle" :class="d.enabled ? 'is-on' : ''">
                      <input v-model="d.enabled" type="checkbox" @change="d.dirty = true" />
                      {{ d.enabled ? '使う' : '使わない' }}
                    </label>
                  </td>
                </template>
                <!-- 保存は行ごと（変更があるときだけ押せる。ファイルはこの行の分だけ送る）。削除はすぐ保存 -->
                <td class="save">
                  <div class="save-cell">
                    <PrimaryButton text="保存" size="sm" class="w-20 run-btn" :disabled="!d.dirty || d.saving" @click="saveSettingDoc(d, i)" />
                    <!-- 削除の場所は常に確保（システムの行に無くても保存ボタンの位置がそろうように） -->
                    <span class="del-slot">
                      <button v-if="!d.system && !d.autoSheet" type="button" class="link-btn danger" :disabled="d.saving" @click="removeSettingDoc(i)">削除</button>
                    </span>
                  </div>
                </td>
              </tr>
              <tr v-if="!setting.docs.length">
                <td colspan="8" class="text-gray-400 text-center py-6">書類がありません。「行を追加」で登録してください。</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </main>

    <!-- 会社印の得意先（モーダル）：得意先を探して「会社印あり」を付ける・外す。変えた行はすぐ保存
         出力の main と書類設定の main（v-if／v-else）の間には置かない（v-else がここに付いてしまう） -->
    <div v-if="seal.open" class="seal-overlay" @click.self="seal.open = false">
      <div class="seal-box">
        <div class="seal-head">
          <div class="seal-title">会社印必要な得意先設定</div>
          <div class="seal-desc">業務報告書にアポロ管財の会社印を入れる得意先に「会社印あり」を付けます。基本は無しです。<br />押すとその場で保存されます（保存ボタンはありません）。</div>
          <button type="button" class="seal-close" @click="seal.open = false">×</button>
        </div>
        <div class="seal-filter">
          <input v-model="seal.name" type="search" placeholder="得意先名・コード 検索" class="filter-input w-72 rounded border border-gray-300 px-2 text-sm" @keydown.enter="getClients" />
          <label class="flex items-center gap-1 text-sm cursor-pointer select-none"><input v-model="seal.sealOnly" type="checkbox" class="rounded" @change="getClients" />会社印ありのみ</label>
          <PrimaryButton text="検索" size="sm" class="w-20 search-btn" :disabled="seal.loading" @click="getClients" />
          <!-- 一覧は先頭の 300 件まで（得意先が多いので全部は出さない）。300 件に達していたら、その先は検索で絞ってもらう -->
          <span v-if="seal.list.length >= 300" class="text-xs text-gray-500 seal-limit">300件だけ表示しています。<br />得意先名で絞ってください。</span>
          <span v-else class="text-xs text-gray-500">{{ seal.list.length }} 件</span>
        </div>
        <div class="seal-list">
          <div v-if="seal.loading" class="text-gray-400 font-bold text-center py-6">Searching...</div>
          <table v-else class="seal-table">
            <thead>
              <tr><th>得意先コード</th><th class="text-left">得意先名</th><th>会社印</th></tr>
            </thead>
            <tbody>
              <tr v-for="c in seal.list" :key="c.cdBillingAddress" :class="c.useSeal ? 'is-on' : ''">
                <td class="code">{{ c.cdBillingAddress }}</td>
                <td class="text-left">{{ c.name }}</td>
                <td class="center">
                  <label class="seal-toggle" :class="c.useSeal ? 'is-on' : ''">
                    <input type="checkbox" :checked="c.useSeal" :disabled="c.saving" @change="saveSeal(c, $event.target.checked)" />
                    {{ c.useSeal ? '会社印あり' : 'なし' }}
                  </label>
                </td>
              </tr>
              <tr v-if="!seal.list.length"><td colspan="3" class="text-gray-400 text-center py-4">対象データがありません。</td></tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- 所属部門の決め方（モーダル）：給与奉行の ECHA003 の対応表。コード・名前・住所（区・県）の表 -->
    <div v-if="bugyo.helpOpen" class="seal-overlay" @click.self="bugyo.helpOpen = false">
      <div class="seal-box dept-box">
        <div class="seal-head">
          <div class="seal-title">所属部門の決め方</div>
          <div class="seal-desc">所属部門（ECHA003）は、勤務する物件の住所で決めます。</div>
          <button type="button" class="seal-close" @click="bugyo.helpOpen = false">×</button>
        </div>
        <div class="seal-list">
          <table class="seal-table dept-table">
            <thead>
              <tr><th>コード</th><th class="text-left">所属</th><th class="text-left">物件の住所</th></tr>
            </thead>
            <tbody>
              <tr v-for="d in bugyo.deptTable" :key="d.code">
                <td class="code">{{ d.code }}</td>
                <td class="text-left dept-name">{{ d.name }}</td>
                <td class="text-left">
                  <span v-for="a in d.areas" :key="a" class="dept-area">{{ a }}</span>
                </td>
              </tr>
            </tbody>
          </table>
          <ul class="dept-notes">
            <li v-for="n in bugyo.deptNotes" :key="n">※{{ n }}</li>
          </ul>
        </div>
      </div>
    </div>

    <!-- 提出先市町村（モーダル）：給与奉行の住民税・提出先（ERET002）に使う市町村の一覧。給与奉行に登録してある市町村と同じにしておく
         人事総務経理は追加・変更・削除できる（行ごとに保存。削除はすぐ）。ほかの人は見るだけ -->
    <div v-if="bugyo.city.open" class="seal-overlay" @click.self="bugyo.city.open = false">
      <div class="seal-box city-box">
        <div class="seal-head">
          <div class="seal-title">提出先市町村（住民税）</div>
          <div class="seal-desc">
            CSV の「住民税・提出先」は、清掃員の住所をもとにこちらの一覧から取得します。<br />
            コードは全国地方公共団体コード（6 桁）、市町村名は郡を付けずに入れます（例：三芳町）。一覧に無い市町村の人は空で出ます。
            <span v-if="!bugyoCanExport" class="bugyo-no-auth">直せるのは人事総務経理に所属している人だけです（閲覧のみ）。</span>
          </div>
          <button type="button" class="seal-close" @click="bugyo.city.open = false">×</button>
        </div>
        <div class="seal-filter">
          <input v-model="bugyo.city.name" type="search" placeholder="市町村名・コード 検索" class="filter-input w-72 rounded border border-gray-300 px-2 text-sm" @keydown.enter="bugyoGetCities" />
          <PrimaryButton text="検索" size="sm" class="w-20 search-btn" :disabled="bugyo.city.loading" @click="bugyoGetCities" />
          <span class="text-xs text-gray-500">{{ bugyo.city.list.length }} 件</span>
          <div class="flex-1"></div>
          <button v-if="bugyoCanExport" type="button" class="seal-btn" @click="bugyoAddCity">
            <Icon iconName="Plus" class="w-4 h-4 mr-1" />行を追加
          </button>
        </div>
        <div class="seal-list">
          <div v-if="bugyo.city.loading" class="text-gray-400 font-bold text-center py-6">Searching...</div>
          <table v-else class="seal-table city-table">
            <thead>
              <tr>
                <th class="text-left code-col">市町村コード</th>
                <th class="text-left">市町村名</th>
                <th v-if="bugyoCanExport" class="save">保存</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(c, i) in bugyo.city.list" :key="c.key" :class="c.dirty ? 'dirty' : ''">
                <template v-if="bugyoCanExport">
                  <td class="text-left code-col"><input v-model="c.code" type="text" maxlength="6" class="setting-input code-input" :class="{ empty: !/^\d{6}$/.test(c.code || '') }" placeholder="6 桁" @input="c.dirty = true" /></td>
                  <td class="text-left"><input v-model="c.name" type="text" class="setting-input" :class="{ empty: !String(c.name || '').trim() }" placeholder="市町村名（郡なし）" @input="c.dirty = true" /></td>
                </template>
                <template v-else>
                  <td class="text-left code code-col">{{ c.code }}</td>
                  <td class="text-left">{{ c.name }}</td>
                </template>
                <td v-if="bugyoCanExport" class="save">
                  <div class="save-cell">
                    <PrimaryButton text="保存" size="sm" class="w-20 run-btn" :disabled="!c.dirty || c.saving" @click="bugyoSaveCity(c)" />
                    <span class="del-slot">
                      <button type="button" class="link-btn danger" :disabled="c.saving" @click="bugyoDeleteCity(c, i)">削除</button>
                    </span>
                  </div>
                </td>
              </tr>
              <tr v-if="!bugyo.city.list.length"><td :colspan="bugyoCanExport ? 3 : 2" class="text-gray-400 text-center py-4">対象データがありません。</td></tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- QR コード読込（モーダル）：雇用契約書の QR を読んで返送済みにする
         バーコードリーダーは入力欄にキーボードとして文字を打ち込むので、欄にフォーカスを置いて Enter で確定
         スマホ・カメラ付き PC は「カメラで読む」（getUserMedia ＋ jsQR）。読んだ結果は下に積み上げる（束を続けて読める） -->
    <div v-if="cr.qr.open" class="seal-overlay" @click.self="crCloseQr">
      <div class="seal-box qr-box">
        <div class="seal-head">
          <div class="seal-title">QR コード読込（雇用契約書）</div>
          <div class="seal-desc">
            返送された契約書の 2 ページ目にある「返送確認用」の QR コードを読むと、その契約書が返送済みになります（1 ページ目の QR コードは使えません）。読んだら次の契約書をそのまま続けて読めます（この画面を閉じるまで何通でも）。<br />
            バーコードリーダーは下の欄に向けてそのまま読んでください。スマホ・カメラ付きのパソコンは「カメラで読む」を押してください。
          </div>
          <button type="button" class="seal-close" @click="crCloseQr">×</button>
        </div>
        <div class="qr-body">
          <div class="qr-input-row">
            <input
              ref="qrInput"
              v-model="cr.qr.text"
              type="text"
              class="filter-input qr-input rounded border border-gray-300 px-2 text-sm"
              placeholder="ここにバーコードリーダーで読む"
              autocomplete="off"
              @keydown.enter.prevent="crReadQrText"
              @input="crOnQrInput"
            />
            <!-- 「確定」のボタンは無い（読んだ文字は自動で確定。手で打つことは無い：契約書を見てもコードは分からない。ユーザー指示）。カメラが動いている間は緑 -->
            <PrimaryButton :text="cr.qr.camera ? 'カメラを止める' : 'カメラで読む'" class="w-40 whitespace-nowrap" :class="cr.qr.camera ? 'run-btn' : ''" @click="cr.qr.camera ? crStopCamera() : crStartCamera()">
              <Icon slot="before" iconName="Camera" class="w-5 h-5 mr-1" />
            </PrimaryButton>
          </div>
          <div v-if="cr.qr.cameraError" class="qr-error">{{ cr.qr.cameraError }}</div>
          <div v-show="cr.qr.camera" class="qr-video-wrap" :class="cr.qr.flash ? 'flash' : ''">
            <video ref="qrVideo" class="qr-video" playsinline muted></video>
            <div class="qr-video-note">QR コードを映してください。読めると下に結果が出て、カメラはそのまま次を待ちます</div>
          </div>
          <!-- 読んだ数（束を続けて読むときの目安） -->
          <div class="qr-count">読み込み：<b>{{ crQrOkCount }}</b> 件<span v-if="crQrNgCount" class="qr-count-ng">（読めなかったもの {{ crQrNgCount }} 件）</span></div>
          <div class="qr-results">
            <div v-if="!cr.qr.results.length" class="qr-empty">まだ読んでいません</div>
            <div v-for="(r, i) in cr.qr.results" :key="i" class="qr-result" :class="r.error ? 'ng' : 'ok'">
              <div class="qr-result-time">{{ r.time }}</div>
              <div class="qr-result-body">
                <div v-if="r.error">{{ r.error }}</div>
                <div v-for="x in r.rows" :key="x.id">
                  清掃員名：<b>{{ x.staffName }}</b>、物件名：<b>{{ x.siteName }}</b><span v-if="x.cdContract">（契約コード {{ x.cdContract }}）</span>
                  <span class="qr-result-state" :class="x.alreadyReturned ? 'already' : ''">{{ x.alreadyReturned ? 'すでに返送済み' : '✓ 返送済み' }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 退職日の確認（モーダル）：退職届を出す人のうち退職が未承認（AKBS にまだ退職日が無い）の人に、退職日を入れてもらう。全員入ると「出力する」が押せる -->
    <div v-if="retire.open" class="seal-overlay">
      <div class="seal-box retire-box">
        <div class="seal-head">
          <div class="seal-title retire-title">まだ退職の承認がされていません。出力しますか？</div>
          <div class="seal-desc">次の人は退職がまだ承認されていないため、AKBS に退職日がありません。退職届に載せる退職日を入れると出力できます。<br />承認済みの人は AKBS の退職日で作ります。</div>
          <button type="button" class="seal-close" @click="closeRetire(false)">×</button>
        </div>
        <div class="seal-list retire-list">
          <table class="seal-table">
            <thead>
              <tr><th>清掃員コード</th><th class="text-left">清掃員名</th><th>退職日</th></tr>
            </thead>
            <tbody>
              <tr v-for="r in retire.rows" :key="r.staffCode">
                <td class="code">{{ r.staffCode }}</td>
                <td class="text-left">{{ r.name }}</td>
                <td class="center retire-date">
                  <WmsDateField :name="`retireDate${r.staffCode}`" caption="" v-model="r.date" class="date-field" :class="r.date ? '' : 'empty'" />
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="retire-foot">
          <span v-if="!retireDatesOk" class="text-xs text-red-600">退職日を全員分入れてください。</span>
          <PrimaryButton text="戻る" size="sm" class="w-24 bg-gray-400" @click="closeRetire(false)" />
          <PrimaryButton text="出力する" size="sm" class="w-24 run-btn" :disabled="!retireDatesOk" @click="closeRetire(true)" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Icon from '@components/Icon.vue'
import PageHeader from '@components/PageHeader.vue'
import PrimaryButton from '@components/PrimaryButton.vue'
import WmsDateField from '@wmscomponents/WmsDateField.vue'
import Pagination from 'vue-pagination-2'
// 契約書の QR をカメラで読む（npm install jsqr）。どのブラウザでも同じに動くように BarcodeDetector は使わない
import jsQR from 'jsqr'
import * as backend from '@libs/backend'
import * as dialogs from '@libs/dialogs'
import * as utils from '@libs/utils'
import PageModel from '../../main/pages/PageModel.vue'

// 絞り込みを控えるセッションストレージのキー（登録済み一覧と同じ仕組み）
const SESSION_STORAGE_NAME = 'akbsOutputStorage'

// ヘッダーの切り替え
const MODES = [
  { key: 'output', name: '出力', icon: 'Download' },
  // 給与奉行インポート（清掃員情報）の CSV は、入社した人を複数人まとめて出すので「出力」の札ではなく別のタブ（ユーザー決定）
  { key: 'bugyo', name: '給与奉行', icon: 'UserAdd' },
  // 雇用契約書の出力の記録。返送されたら返送済みにする（ユーザー指示）
  { key: 'contractReturn', name: '契約書返送', icon: 'CheckCircle' },
  { key: 'setting', name: '書類設定', icon: 'Cog' }
]

// 給与奉行の所属部門（ECHA003）の決め方（RP_api の akbsBugyo.js の deptCode と同じ。資料：所属コード.xlsx）。「所属部門の決め方」の表で見せる
const BUGYO_DEPT_TABLE = [
  { code: '111', name: '神奈川', areas: ['神奈川県'] },
  { code: '112', name: '多摩', areas: ['東京都の23区以外'] },
  { code: '113', name: '東京西', areas: ['世田谷区', '大田区'] },
  { code: '121', name: '東京中央', areas: ['渋谷区', '港区', '新宿区', '豊島区', '目黒区', '品川区'] },
  { code: '122', name: '千葉', areas: ['千葉県'] },
  { code: '123', name: '埼玉', areas: ['埼玉県'] },
  { code: '124', name: '東京東', areas: ['中央区', '江東区', '葛飾区', '荒川区', '台東区', '墨田区', '江戸川区', '足立区', '北区', '板橋区', '文京区', '千代田区'] },
  { code: '125', name: '東京北', areas: ['杉並区', '中野区', '練馬区'] }
]
const BUGYO_DEPT_NOTES = [
  '物件が複数ある人は、終わっていない雇用指示のうち開始日が一番早い物件の住所',
  '代勤専門は清掃員の住所',
  '上に無い県・住所が無い人は空'
]
// ボタンに乗せたときの文（表は出せないので 1 行ずつ）
const BUGYO_DEPT_HELP = [
  '所属部門（ECHA003）は、勤務する物件の住所で決めます。',
  '',
  ...BUGYO_DEPT_TABLE.map(d => `${d.code} ${d.name}：${d.areas.join('・')}`),
  '',
  ...BUGYO_DEPT_NOTES.map(n => `※${n}`)
].join('\n')

// 範囲（値はバックの scope）
const SCOPES = [
  { value: 'active', name: '在職者' },
  { value: 'ended', name: '退職者' },
  { value: 'all', name: '全員' }
]

function createFilter () {
  // areas は選んだエリア ID（文字）の配列
  return { name: '', areas: [], ageDate: '', age: '', scope: 'active' }
}

// 出力できる書類（key は出力の処理で使う名前。shared は入社と契約変更で同じ書類）
// 書類設定（ユーザーが書類をアップする画面）ができたら、名前・並びはそちらから読む
const DOCS = {
  docsReturn: { name: '入社書類（返送）' },
  nameplate: { name: '名札', shared: true },
  contract: { name: '雇用契約書', shared: true },
  // 業務報告書は2種類：予定（雇用指示＋祝日勤務を考慮）と、実績（同じ形式で、雇用指示＋休み登録・代勤などの実績。前の「全勤務」はこれに含む。ユーザー決定 2026-10-09）
  // 実績は退職したときにハローワークへ持っていくためのもの
  report: { name: '業務報告書（予定）', shared: true },
  reportActual: { name: '業務報告書（実績）' },
  insteadCalc: { name: '代勤賃金簿' },
  // 雇用契約書は勤務内容が変わったときは必ず送る（ユーザー 2026-10-09）ので、案内は物件追加用に限らず「雇用契約締結案内」1つ
  guideAdd: { name: '雇用契約締結案内' },
  mitsuiReturn: { name: '三井様の返却物（送付状）' },
  retireReturn: { name: '返却物（送付状）' },
  retireNotice: { name: '退職届' },
  addrA4: { name: '宛先A4' },
  addrEnvelope: { name: '長形3号' },
  addrLabel: { name: '宛名シール' },
  // 業務報告書（予定）と一緒に期間の月数分付ける固定書類（札には出さない。書類設定で PDF を登録する専用の行。AUTO_SHEETS）
  opSheet: { name: '業務報告書に付ける代勤賃金簿' },
  workSheet: { name: '業務報告書に付ける勤務・交通費報告書' }
}

// 業務報告書（予定）と一緒に自動で付ける固定書類（1 枚＝1 か月。その人の束の最後。期間の枠の下の切り替えで外せる。初期 ON）
// 書類設定の専用の行（doc_key がこの key。バックの akbsOutput.js の AUTO_SHEET_KEYS と同じ）に PDF を登録する
// 札の固定書類とは別の置き場にする：札は名前も並びも変えられるので、並びや「枚数の入力欄」の印で探すと書類設定次第で付く書類が変わってしまう
//   opOnly：代勤専門の雇用指示が効いている月だけ数える（代勤賃金簿）。false なら選んだ勤務のどれかが効いている月（勤務・交通費報告書：1 か月の出勤日数・時間を書く用紙。全員）
//   label：期間の枠の下の切り替えの文字、use：書類設定の一式の欄に出す使い道、tip：書類設定の「業務報告書用」の札の説明
const AUTO_SHEETS = [
  {
    key: 'opSheet',
    opOnly: true,
    label: '代勤専門の人には代勤賃金簿を期間の月数分付ける',
    use: '業務報告書（予定）に付ける代勤専門の人用',
    tip: '出力の札には出しません。業務報告書（予定）を出すときに「代勤専門の人には代勤賃金簿を期間の月数分付ける」が ON なら、この PDF を 1 か月 1 枚でその人の束の最後に付けます'
  },
  {
    key: 'workSheet',
    opOnly: false,
    label: '全員に勤務・交通費報告書を期間の月数分付ける',
    use: '業務報告書（予定）に付ける全員用',
    tip: '出力の札には出しません。業務報告書（予定）を出すときに「全員に勤務・交通費報告書を期間の月数分付ける」が ON なら、この PDF を 1 か月 1 枚（勤務が 1 つでも効いている月）でその人の束の最後に付けます'
  }
]

/**
 * 自動で付ける書類の決まり（key で引く。無ければ null）
 */
function autoSheetOf (key) {
  return AUTO_SHEETS.find(a => a.key === key) || null
}

// 一式（名前を押すと中の書類がまとめて ON／OFF）。一式に入らない書類（業務報告書（実績）・給与奉行）は札を1つずつ押す
const DOC_SETS = [
  // 入社書類一式に「雇用契約締結案内（入社用）」は入れない：入社書類（返送）の1ページ目と中身がかぶるので、そちらにまとめる（ユーザー決定 2026-10-09）
  // 代勤賃金簿はどの一式にも入れない（ユーザー決定）。業務報告書（予定）の「代勤専門の人には…付ける」で付く
  { key: 'hire', name: '入社書類一式', docs: ['docsReturn', 'nameplate', 'contract', 'report'] },
  // 入社用具一式：入社のときに送る用具の書類。中の書類は書類設定で付ける（既定は空）。勤務の選び直しなどの特別な動きは無い
  { key: 'hireTool', name: '入社用具一式', docs: [] },
  // 契約変更一式（物件追加も含む勤務内容の変更。雇用契約書は変わったときは必ず送る。ユーザー決定 2026-10-09）
  { key: 'change', name: '契約変更一式', docs: ['guideAdd', 'nameplate', 'contract', 'report'] },
  { key: 'end', name: '物件終了一式', docs: ['mitsuiReturn'] },
  { key: 'retire', name: '退職一式', docs: ['retireReturn', 'retireNotice'] }
]

// 書類の札の並び（行ごと。ユーザー指示 2026-10-09。一式のボタンとの対応は DOC_SETS のまま）
// 業務報告書（実績）の形式（PDF：書類一式の中に入る。Excel：別ファイル。ハローワーク用に手で直せるように）
const ACTUAL_FORMATS = [
  { key: 'pdf', name: 'PDF' },
  { key: 'excel', name: 'Excel' }
]

// 業務報告書（実績）は退職のときにハローワークへ持っていくためのものなので最後の行（ユーザー指示 2026-10-09）
// 給与奉行インポート（清掃員情報）は札ではなく「給与奉行」タブ（書類設定の行も無い。ユーザー決定）
const DOC_ROWS = [
  ['docsReturn', 'contract', 'report', 'insteadCalc', 'nameplate'],
  ['guideAdd', 'retireReturn', 'mitsuiReturn', 'retireNotice'],
  ['reportActual']
]

// 宛先（書類ではないので、一式のボタンの行の右に色を変えて置く）
const ADDR_KEYS = ['addrA4', 'addrEnvelope', 'addrLabel']

// 勤務（物件・契約コード）ごとに出す書類（この書類が ON のとき、一覧に「出力する勤務」の列を出す）
const EMPLOYMENT_DOCS = ['report', 'reportActual', 'contract', 'nameplate']

// 業務報告書（実績）の「代勤」の枚（別の物件での代勤と、代勤専門の有休）。勤務の候補に 1 つ足して選べるようにする。物件コードは代勤専門と同じ
const OP_EMPLOYMENT = { cdSite: '000000000', cdContract: '', siteName: '代勤', isOp: true }

// 契約変更一式の初期値：開始日がこの日数前以降の勤務だけ ON（最近足した・変えた勤務。ユーザー OK 2026-10-09）
const CHANGE_RECENT_DAYS = 31

// 出力は何人ずつ別の PDF にするか（1 本が大きくなりすぎない・途中で止まっても続きから出せる。ユーザー決定）
// 業務報告書（1 勤務 12 枚）が入るときは 100 人、入らないときは 200 人（契約書だけの定期送付を少ない本数で）。書類一式と雇用契約書は同じ区切り（区切りがずれると止まったときに分かりにくい）
const OUTPUT_CHUNK_WITH_REPORT = 100
const OUTPUT_CHUNK = 200

// 業務報告書（実績）の Excel を受け取るときの種類
const XLSX_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'

// 総務が用意する固定の書類（書類設定で登録する。PDF を S3 に置く）。システムが作る書類はこれ以外
// 代勤賃金簿も固定書類（PDF）。ただし出力のときに 1 人あたりの枚数を聞く（copiesInput。ユーザー決定）
// 書類設定の保存ができるまでは、この並びを初期値に見せる
const UPLOAD_DOC_KEYS = ['docsReturn', 'guideAdd', 'insteadCalc', 'mitsuiReturn', 'retireReturn']

// システムが作る書類のうち、書類一式.pdf の中で（書類設定の順に）作るもの。別の PDF にするのは両面印刷の雇用契約書だけ（ユーザー決定）
// 業務報告書（予定・実績）＝選んだ勤務 × 期間の月。退職届＝縦書きの退職願（退職日は AKBS のもの。無ければ出力実行のときに入れてもらう retireDates）
// 名札＝出力する勤務の物件の得意先ごとに表と裏の 2 枚（得意先名で種類を決める。裏はどれも緊急連絡先。野村・三菱は顔写真入り）
const BUNDLE_SYSTEM_KEYS = ['report', 'reportActual', 'retireNotice', 'nameplate']

// 書類設定に置けるファイル（PDF は出力に使う。Word・Excel は原本として保存するだけ。ユーザー決定）
const FILE_EXTS = ['pdf', 'doc', 'docx', 'xls', 'xlsx']
const FILE_ACCEPT = FILE_EXTS.map(e => `.${e}`).join(',')

/**
 * ファイル名の拡張子（小文字）
 */
function extOf (fileName) {
  const m = String(fileName || '').match(/\.([A-Za-z0-9]+)$/)
  return m ? m[1].toLowerCase() : ''
}

/**
 * 書類設定の既定（表 tbl_akbs_output_doc がまだ空のとき。コードの DOCS・DOC_SETS・DOC_ROWS から）
 * 固定書類もシステムが作る書類も 1 つの表に並べ、この並びがそのまま出力画面の札の順になる（ユーザー指示）
 * システムが作る書類（system）はファイルを持たず、消せない。名前・一式・並び・使うは変えられる
 * 宛先（A4・長形 3 号・宛名シール）は書類ではないので書類設定には出さない（ユーザー指示）
 * 最後に「業務報告書に付ける代勤賃金簿」の行（札には出さない。PDF だけ登録する）
 * @returns 書類設定の行（docConf の形。id は無し。保存するとシステムの書類は docKey 付きで表に入る）
 */
function defaultDocConf () {
  return [...DOC_ROWS.flat().map(key => {
    const system = !UPLOAD_DOC_KEYS.includes(key)
    return {
      key,
      id: null,
      docKey: system ? key : null,
      name: DOCS[key].name,
      system,
      autoSheet: '',
      sets: DOC_SETS.filter(s => s.docs.includes(key)).map(s => s.key),
      enabled: true,
      // 出力のときに 1 人あたりの枚数を入れる（代勤賃金簿）
      copiesInput: key === 'insteadCalc',
      // PDF（複数可。並びの順に出力でつなげる）
      files: []
    }
  }), ...AUTO_SHEETS.map(a => defaultAutoSheetConf(a.key))]
}

/**
 * 業務報告書に自動で付ける書類の行の既定（表にまだ無いとき。書類設定で PDF を登録して保存すると doc_key = key で表に入る）
 * autoSheet にその key を持つ（札の書類・システムの書類は ''）
 */
function defaultAutoSheetConf (key) {
  return { key, id: null, docKey: key, name: DOCS[key].name, system: false, autoSheet: key, sets: [], enabled: true, copiesInput: false, files: [] }
}

/**
 * バックの書類設定の行 → 画面の形（key はシステムの書類ならそのキー、固定書類は「u」＋id）
 * 業務報告書に自動で付ける書類（doc_key が AUTO_SHEETS の key）は docKey があるがシステムの書類ではない（PDF を持つ。札には出さない）
 */
function toDocConf (row) {
  const autoSheet = autoSheetOf(row.docKey) ? row.docKey : ''
  const system = !!row.docKey && !autoSheet
  return {
    key: row.docKey ? row.docKey : `u${row.id}`,
    id: Number(row.id),
    docKey: row.docKey || null,
    name: row.name || '',
    system,
    autoSheet,
    sets: Array.isArray(row.setKeys) ? row.setKeys.map(String) : [],
    enabled: row.enabled !== false,
    copiesInput: !!row.copiesInput,
    // PDF の一覧（並びの順）。no は書類の中の番号（ダウンロード・差し替えのキー）
    files: (Array.isArray(row.files) ? row.files : []).map(f => ({
      no: Number(f.no),
      fileName: f.fileName || '',
      // PDF だけ出力に使う。Word・Excel は原本として置くだけ
      isPdf: (f.ext || 'pdf') === 'pdf',
      fileSize: f.fileSize ? formatFileSize(f.fileSize) : '',
      fileDate: f.updateDate || '',
      fileUser: f.updateUserName || ''
    }))
  }
}

/**
 * 書類設定の画面の行（docConf の行に、差し替え・追加のファイル（newFile）と変更の印を足す）
 */
function toSettingDoc (conf) {
  return { ...conf, sets: [...conf.sets], files: conf.files.map(f => ({ ...f, newFile: null })), dirty: false, saving: false }
}

/**
 * ファイルの大きさの見た目（「1.2 MB」「345 KB」）
 */
function formatFileSize (bytes) {
  const n = Number(bytes) || 0
  if (n >= 1024 * 1024) {
    return `${(n / 1024 / 1024).toFixed(1)} MB`
  }
  return `${Math.max(1, Math.round(n / 1024))} KB`
}

/**
 * PDF にセキュリティ（暗号化。/Encrypt）が設定されているか
 * 暗号化された PDF は出力（pdf-lib でページを写す）で中身を復号できず白紙になるので、登録のときに止める
 * 中身を読まずにファイルの文字列を探すだけ（暗号化された PDF は必ず /Encrypt を持つ。中身は圧縮されているので誤って当たることはまず無い）
 */
function isEncryptedPdf (file) {
  return new Promise((resolve) => {
    const reader = new FileReader()
    reader.onload = () => {
      const bytes = new Uint8Array(reader.result)
      let text = ''
      for (let i = 0; i < bytes.length; i += 8192) {
        text += String.fromCharCode.apply(null, bytes.subarray(i, i + 8192))
      }
      resolve(text.includes('/Encrypt'))
    }
    reader.onerror = () => resolve(false)
    reader.readAsArrayBuffer(file)
  })
}

/**
 * File → base64 の data URL（バックへ送って S3 に上げる）
 */
function readFileAsDataUrl (file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result)
    reader.onerror = () => reject(reader.error)
    reader.readAsDataURL(file)
  })
}

// 何日前（YYYY-MM-DD）
function daysAgo (n) {
  const d = new Date()
  d.setDate(d.getDate() - n)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

/**
 * 雇用契約書の契約期間の初期値（YYYY-MM-DD）：全部空（ユーザー指示）
 * 開始日を入れると、終了日は 1 年後、70 歳以上の終了日は半年後が自動で入る（watch。直せる）
 */
function defaultContractPeriod () {
  return { from: '', to: '', to70: '' }
}

/**
 * 業務報告書の期間の初期値（YYYY-MM）：全部空。雇用契約書の契約期間を入れるとそれに連動する（watch。ユーザー指示）
 * 年齢で期間は変えない（70 歳以上の終了月は持たない。全員同じ期間）
 */
function defaultReportPeriod () {
  return { from: '', to: '' }
}

/**
 * 業務報告書（実績）の期間の初期値（YYYY-MM）：過去 1 年分（今月を含めて 12 か月。ハローワーク用に過去 1 年分を出す。ユーザー決定）
 */
function defaultActualPeriod () {
  const to = daysAgo(0).slice(0, 7)
  return { from: monthAdd(to, -11), to }
}

/**
 * 開始日から期間の終了日を作る（開始日に months か月を足して 1 日戻す。2026-10-01 ＋ 12 か月 → 2027-09-30、＋ 6 か月 → 2027-03-31）
 * @param {String} from YYYY-MM-DD
 * @param {Number} months 足す月数
 * @returns {String} YYYY-MM-DD（開始日が日付でなければ空）
 */
/**
 * 年月（YYYY-MM）に月を足す（業務報告書の終了の自動入力用）
 */
function monthAdd (ym, months) {
  const [y, m] = ym.split('-').map(Number)
  const d = new Date(y, m - 1 + months, 1)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
}

/**
 * その年月以降で一番近い 4 月（YYYY-MM。4 月ならその月）
 */
function nextApril (ym) {
  const [y, m] = ym.split('-').map(Number)
  return `${m <= 4 ? y : y + 1}-04`
}

function periodEndOf (from, months) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(from || '')) {
    return ''
  }
  const [y, m, d] = from.split('-').map(Number)
  // 足した先の月に同じ日が無ければ（8/31 の半年後の 2/31 など）その月の末日にしてから 1 日戻す
  const target = new Date(y, m - 1 + months, 1)
  const last = new Date(target.getFullYear(), target.getMonth() + 1, 0).getDate()
  const end = new Date(target.getFullYear(), target.getMonth(), Math.min(d, last))
  end.setDate(end.getDate() - 1)
  return `${end.getFullYear()}-${String(end.getMonth() + 1).padStart(2, '0')}-${String(end.getDate()).padStart(2, '0')}`
}

/**
 * YYYY-MM か YYYY-MM-DD → その月の末日（YYYY-MM-DD）
 */
/**
 * 年月の期間（YYYY-MM）が入っているか（開始 ≦ 終了）
 */
function monthPeriodOk (from, to) {
  const ok = (x) => /^\d{4}-\d{2}$/.test(x || '')
  return ok(from) && ok(to) && from <= to
}

/**
 * 年月の期間の月数（両端を含む）
 */
function monthCount (from, to) {
  const [fy, fm] = from.split('-').map(Number)
  const [ty, tm] = to.split('-').map(Number)
  return (ty - fy) * 12 + (tm - fm) + 1
}

/**
 * 年月の期間の欄の横に出す案内（何が足りないかを具体的に。入っていれば空）
 */
function monthPeriodHint (from, to) {
  if (monthPeriodOk(from, to)) {
    return ''
  }
  if (!from) {
    return '開始の年月を入れてください'
  }
  if (!to) {
    return '終了の年月を入れてください'
  }
  return '開始は終了より前にしてください'
}

function monthEndOf (day) {
  const [y, m] = String(day || '').split('-').map(Number)
  if (!y || !m) {
    return ''
  }
  return `${y}-${String(m).padStart(2, '0')}-${String(new Date(y, m, 0).getDate()).padStart(2, '0')}`
}

export default {
  extends: PageModel,

  components: {
    Icon,
    PageHeader,
    PrimaryButton,
    WmsDateField,
    Pagination
  },

  data() {
    const reportPeriod = defaultReportPeriod()
    const actualPeriod = defaultActualPeriod()
    const contractPeriod = defaultContractPeriod()
    return {
      modes: MODES,
      mode: 'output',
      scopes: SCOPES,
      // エリアの選択肢（AKBS のエリア管理。開いたときに取る）
      areas: [],
      // エリアの選択の一覧（ボタンの下に重ねて出す。開いたときの選択を控えて、変わっていれば閉じたときに検索）
      areaPanel: { open: false, top: 0, left: 0, before: '' },

      filter: createFilter(),
      loading: false,
      list: [],
      page: 1,
      perPage: 50,
      records: 0,

      // 選んだ清掃員（清掃員コード → 行の控え）。ページを変えても残す。出力が終わったら空にする
      checked: {},
      // 見出しのチェック（絞り込んだ全員を選ぶ・外す）で全員分を取っている間
      selectingAll: false,
      downloading: false,
      // 出力中の白いマスク（active のあいだ画面を覆う。percent は進み具合）
      // jobId：作っている PDF のジョブ（あれば「中止」ボタンを出す）。cancelling：中止を押した
      progress: { active: false, percent: 0, text: '', jobId: null, cancelling: false },

      // 書類設定（保存済み。出力画面の札の名前・並び・一式はここから。開いたときにバックから読む。表が空なら既定）
      docConf: defaultDocConf(),
      // 宛先の札と、選んだ書類（key → true）
      addrKeys: ADDR_KEYS,
      docs: {},
      // 書類に要る入力（業務報告書の期間 YYYY-MM、契約期間、枚数（書類の key → 枚数））
      // 業務報告書の期間の初期値は雇用契約書の契約期間と同じ。枚数は空から始める（赤い欄にして必ず自分で入れてもらう）
      docInput: {
        reportFrom: reportPeriod.from,
        reportTo: reportPeriod.to,
        // 業務報告書（実績）の期間（予定とは別。初期は過去 1 年分）
        actualFrom: actualPeriod.from,
        actualTo: actualPeriod.to,
        contractFrom: contractPeriod.from,
        contractTo: contractPeriod.to,
        contractTo70: contractPeriod.to70,
        copies: {},
        // 宛名シール（12 面）の 1 ページ目の印字開始位置（0〜11。使いかけのシートの続きから印字するため。AKBS と同じ）
        // 宛名シールの印字開始位置（0 始まり）。初期値は無し＝必ず選んでもらう（ユーザー指示）
        labelStart: null,
        // 業務報告書（予定）と一緒に自動で付ける書類（AUTO_SHEETS の key → 付けるか。初期は全部 ON。ユーザー決定）
        autoSheets: Object.fromEntries(AUTO_SHEETS.map(a => [a.key, true])),
        // 業務報告書（実績）の形式（'pdf'：書類一式の中、'excel'：別ファイル）。初期は Excel（ユーザー指示）
        actualFormat: 'excel'
      },
      actualFormats: ACTUAL_FORMATS,
      // 宛名シールのシートの図を開いているか（宛名シールを ON にした直後は開く。面を押すと閉じる）
      labelPick: false,
      // 印刷用に逆順（雇用契約書（別 PDF・両面）以外の書類全部で、清掃員ごとにページを最後から並べる。清掃員の並びは変えない。初期 ON）
      reverse: true,

      // 出力する勤務の選択（清掃員コード → { 物件-契約コード: true }）。行のチェックとは別に持ち、ページを変えても残す
      empSel: {},

      // 会社印の得意先（モーダル）
      seal: { open: false, name: '', sealOnly: false, loading: false, list: [] },

      // 退職届の退職日（清掃員コード → YYYY-MM-DD）。AKBS にまだ退職日が無い人の分。URL（遷移元の画面）から来るか、確認のモーダルで入れる
      retireDates: {},
      // 退職日の確認（モーダル）：AKBS に退職日が無い人の一覧と、人ごとの退職日の欄。resolve は「出力する」「戻る」で結果を返す
      retire: { open: false, rows: [], resolve: null },
      // サブタスクから開いたときの知らせ（URL の notice。「金額のみ変更：交通費が下がっているので…」など。赤字で出す）
      linkNotice: '',

      // 書類設定の画面（docConf を元に行ごとに編集・保存）
      setting: { docs: defaultDocConf().map(toSettingDoc), loading: false, seq: 0 },
      FILE_ACCEPT,

      // 給与奉行のタブ（在職者の一覧。初期値は「まだ CSV を出していない人」。checked は清掃員コード → 行の控え）
      bugyo: {
        filter: { name: '', hireFrom: '', notExported: true },
        list: [],
        page: 1,
        perPage: 100,
        records: 0,
        loading: false,
        checked: {},
        // 所属部門の決め方（モーダル）
        helpOpen: false,
        deptTable: BUGYO_DEPT_TABLE,
        deptNotes: BUGYO_DEPT_NOTES,
        // 提出先市町村のマスタ（モーダル。list の行は { key, code, name, originalCode, dirty, saving }。更新日・更新者は表にはあるが画面には出さない）
        city: { open: false, name: '', loading: false, list: [], seq: 0 }
      },

      // 契約書返送のタブ（雇用契約書の出力の記録。返送・備考は行ごとに即保存なのでチェック列は無い。qr は読み取りのモーダル）
      cr: {
        filter: { name: '', outputFrom: '', notReturned: true },
        list: [],
        page: 1,
        perPage: 100,
        records: 0,
        loading: false,
        // okCount・ngCount は開いてから読めた数・読めなかった数（results は 30 件までなので別に数える）。flash は読めた瞬間に映像の枠を光らせる
        qr: { open: false, text: '', busy: false, camera: false, cameraError: '', results: [], read: false, okCount: 0, ngCount: 0, flash: false }
      }
    }
  },

  watch: {
    /**
     * タブを切り替えたら、そのタブの一覧を取り直す
     */
    mode(v) {
      // どのタブも開くたびに一覧を取り直す（ほかのタブや別の PC での変更が古いまま見えないように。ユーザー指示）。絞り込み・選択はそのまま
      if (v === 'output') {
        // 書類設定で変えた書類の名前・並びも札に反映する。人の一覧は今のページのまま（選んだ人はページをまたいで持っている）
        this.getDocs()
        this.getList()
      } else if (v === 'bugyo' && !this.bugyo.loading) {
        this.bugyoGetList(1)
      } else if (v === 'contractReturn' && !this.cr.loading) {
        this.crGetList(1)
      } else if (v === 'setting') {
        // 行ごとに保存する画面なので、保存していない行の入力は消える
        this.getDocs()
      }
    },

    /**
     * 雇用契約書の開始日を入れたら、終了日は 1 年後・70 歳以上の終了日は半年後を自動で入れる（ユーザー指示。そのあと手で直せる）
     * 業務報告書の開始の年月も連動させる（終了は業務報告書側で一番近い 4 月になる）
     */
    'docInput.contractFrom'(v) {
      if (!/^\d{4}-\d{2}-\d{2}$/.test(v || '')) {
        return
      }
      this.docInput.contractTo = periodEndOf(v, 12)
      this.docInput.contractTo70 = periodEndOf(v, 6)
      this.docInput.reportFrom = v.slice(0, 7)
    },

    /**
     * 業務報告書の開始の年月を入れたら、終了は一番近い 4 月（開始の月以降）を自動で入れる（そのあと手で直せる）
     * 毎年 5 月に全員分を送っているので、4 月までで足りる（ユーザー決定）。雇用契約書の終了日とは連動させない
     */
    'docInput.reportFrom'(v) {
      if (!/^\d{4}-\d{2}$/.test(v || '')) {
        return
      }
      this.docInput.reportTo = nextApril(v)
    }
  },

  computed: {
    checkedCount() {
      return Object.keys(this.checked).length
    },

    /**
     * 何かの途中か（一覧の取得・出力・全員選択・書類設定の読み込み・QR の送信）。どのタブでも、この間は「検索」と更新の矢印を押せない
     * （取得中にもう一度押すと古い結果が後から上書きすることがある。ユーザー指示）
     */
    searchBusy() {
      return this.loading || this.bugyo.loading || this.cr.loading || this.downloading || this.progress.active || this.selectingAll || this.setting.loading || this.cr.qr.busy
    },

    /**
     * 退職日の確認のモーダルで、全員の退職日が入っているか（YYYY-MM-DD）
     */
    retireDatesOk() {
      return this.retire.rows.length > 0 && this.retire.rows.every(r => /^\d{4}-\d{2}-\d{2}$/.test(r.date || ''))
    },

    /**
     * エリアのボタンの文字（選んでいなければ「エリア：全て」、1つなら名前、2つ以上なら「○件」）
     */
    areaLabel() {
      const sel = this.filter.areas || []
      if (!sel.length) {
        return 'エリア：全て'
      }
      if (sel.length === 1) {
        const a = this.areas.find(x => String(x.areaId) === String(sel[0]))
        return `エリア：${a ? a.areaName : sel[0]}`
      }
      return `エリア：${sel.length}件`
    },

    /**
     * 絞り込みに当たる全員が選ばれているか（このページが全部 ON で、選んだ人数が全件数以上）
     */
    allFilteredChecked() {
      return this.list.length > 0 && this.list.every(row => this.isChecked(row)) && this.checkedCount >= this.records
    },

    someCheckedInPage() {
      return this.list.some(row => this.isChecked(row))
    },

    /**
     * このページで勤務のある人の勤務が全部 ON か
     */
    allEmpCheckedInPage() {
      const rows = this.list.filter(row => this.empVisible(row).length)
      return rows.length > 0 && rows.every(row => this.isEmpAllOn(row))
    },

    someEmpCheckedInPage() {
      return this.list.some(row => this.empVisible(row).some(e => this.isEmpOn(row, e)))
    },

    /**
     * 業務報告書（予定）の期間（YYYY-MM-DD の初日と末日。予定が OFF か期間が入っていなければ null）
     * 20 日締めの得意先のずれ（前月 21 日〜）は見ない（候補を選ぶための目安なので）
     */
    reportPeriod() {
      if (!this.isDocOn('report') || !this.reportPeriodOk) {
        return null
      }
      return { start: `${this.docInput.reportFrom}-01`, end: monthEndOf(this.docInput.reportTo) }
    },

    /**
     * 業務報告書（実績）の期間（同上。実績が OFF か期間が入っていなければ null）
     */
    actualPeriod() {
      if (!this.isDocOn('reportActual') || !this.actualPeriodOk) {
        return null
      }
      return { start: `${this.docInput.actualFrom}-01`, end: monthEndOf(this.docInput.actualTo) }
    },

    /**
     * 雇用契約書の契約期間（画面の開始日〜「終了日」と「70歳以上の終了日」の遅い方。入っていなければ null）
     * 勤務の候補を選ぶための目安（人ごとの入社日・退職日はバックで見る）
     */
    contractPeriod() {
      if (!this.isDocOn('contract') || !this.contractPeriodOk) {
        return null
      }
      const d = this.docInput
      return { start: d.contractFrom, end: d.contractTo > d.contractTo70 ? d.contractTo : d.contractTo70 }
    },

    /**
     * 勤務の候補を絞る期間（業務報告書（予定）の期間・（実績）の期間・契約期間のうち ON で入っているもの。どれかに重なれば候補に出す）
     */
    empPeriods() {
      const list = []
      if (this.reportPeriod) {
        list.push(this.reportPeriod)
      }
      if (this.actualPeriod) {
        list.push(this.actualPeriod)
      }
      if (this.contractPeriod) {
        list.push(this.contractPeriod)
      }
      return list
    },

    /**
     * 選んだ書類の数（種類）
     */
    docCount() {
      return Object.keys(this.docs).length
    },

    /**
     * 「印刷用に逆順」を出すか：逆順が効く書類（雇用契約書・宛名シール以外）が 1 つでも ON のとき
     */
    reverseApplies() {
      return Object.keys(this.docs).some(k => k !== 'contract' && k !== 'addrLabel')
    },

    /**
     * 業務報告書（予定）と一緒に自動で付ける書類（期間の枠の下に出す順。決まり＋書類設定の行＋付けられるか）
     * ready：専用の行が保存されていて PDF がある。on：切り替えが ON
     */
    autoSheets() {
      return AUTO_SHEETS.map(a => {
        const conf = this.docConfMap[a.key] || null
        return { ...a, conf, name: conf ? conf.name : DOCS[a.key].name, ready: !!(conf && conf.id && conf.files.some(f => f.isPdf)), on: !!this.docInput.autoSheets[a.key] }
      })
    },

    /**
     * 自動で付ける書類のうち、業務報告書（予定）が ON で、切り替えが ON で、付けられるもの（出力で付ける）
     */
    activeAutoSheets() {
      return this.isDocOn('report') ? this.autoSheets.filter(a => a.on && a.ready) : []
    },

    /**
     * 「代勤専門の人には代勤賃金簿を…付ける」が ON で、札の「代勤賃金簿」も ON のときの札の名前（二重に付くので知らせる。無ければ ''）
     * 札かどうかは札の文字（名前に「代勤賃金簿」を含む）で見る（ユーザー決定）
     */
    opSheetDouble() {
      if (!this.activeAutoSheets.some(a => a.key === 'opSheet')) {
        return ''
      }
      const k = this.docList.find(key => this.isDocOn(key) && String(this.docConfMap[key].name || '').replace(/\s/g, '').includes('代勤賃金簿'))
      return k ? this.docConfMap[k].name : ''
    },

    /**
     * 書類設定（保存済み）を key で引く
     */
    docConfMap() {
      const map = {}
      for (const c of this.docConf) {
        map[c.key] = c
      }
      return map
    },

    /**
     * 出力画面に出す書類の札（書類設定の並び。「使う」だけ。業務報告書に付ける代勤賃金簿は札に出さない）
     */
    docList() {
      return this.docConf.filter(c => c.enabled && !c.autoSheet).map(c => c.key)
    },

    /**
     * 一式（入社・契約変更・物件終了・退職）と、その中の書類（書類設定で付けたもの。並びは書類設定の順）
     */
    docSets() {
      return DOC_SETS.map(s => ({ key: s.key, name: s.name, docs: this.docConf.filter(c => c.enabled && c.sets.includes(s.key)).map(c => c.key) }))
    },

    // ---- 給与奉行のタブ ----
    /**
     * スマホ・タブレットか（ほかの画面と同じ utils.deviceInfo）
     */
    isMobileOrTablet() {
      return !!(utils.deviceInfo && (utils.deviceInfo.isMobile || utils.deviceInfo.isTablet))
    },

    /**
     * ヘッダーに出すタブ。スマホ・タブレットは「契約書返送」だけ（出力・給与奉行・書類設定は小さい画面では使えない。ユーザー指示）
     */
    visibleModes() {
      return this.isMobileOrTablet ? this.modes.filter(m => m.key === 'contractReturn') : this.modes
    },

    // QR 読込で、開いてから読めた数・読めなかった数
    crQrOkCount() {
      return this.cr.qr.okCount
    },

    crQrNgCount() {
      return this.cr.qr.ngCount
    },

    /**
     * 給与奉行の CSV を出せるか：兼任を含めて人事総務経理に所属している人か、管理者権限がある人（賃金画面と同じ判定。ユーザー指示）。一覧は誰でも見られる
     */
    bugyoCanExport() {
      const auth = this.$store.getters.auth
      return utils.manager(this.$store.getters.dualPosition || []) || !!(auth && auth.adminAuth)
    },

    bugyoDeptHelpText() {
      return BUGYO_DEPT_HELP
    },

    bugyoCheckedCount() {
      return Object.keys(this.bugyo.checked).length
    },

    bugyoAllInPage() {
      return this.bugyo.list.length > 0 && this.bugyo.list.every(r => this.bugyo.checked[r.staffDailyCleaning])
    },

    bugyoSomeInPage() {
      return this.bugyo.list.some(r => this.bugyo.checked[r.staffDailyCleaning])
    },

    /**
     * 枚数の入力欄が要る書類のうち ON のもの（代勤賃金簿など）
     */
    copiesDocs() {
      return this.docList.filter(k => this.docConfMap[k].copiesInput && this.isDocOn(k))
    },

    /**
     * 枚数が全部入っているか（1 以上の整数）
     */
    copiesOk() {
      return this.copiesDocs.every(k => this.copiesOkOf(k))
    },


    /**
     * 業務報告書（予定・実績のどちらか）が ON で、期間が要るか
     */
    needReportPeriod() {
      return this.isDocOn('report') || this.isDocOn('reportActual')
    },

    /**
     * 一覧を出してよいか（書類が選ばれていて、必要な入力（業務報告書の期間・代勤賃金簿の枚数）が入っている）
     */
    readyForList() {
      return this.docCount > 0 && !this.notReadyReason
    },

    /**
     * 一覧を出せない理由（空なら出せる）
     */
    notReadyReason() {
      if (this.isDocOn('report') && !this.reportPeriodOk) {
        return '業務報告書（予定）の期間（開始・終了の年月）を入れてください。'
      }
      if (this.isDocOn('reportActual') && !this.actualPeriodOk) {
        return '業務報告書（実績）の期間（開始・終了の年月）を入れてください。'
      }
      if (this.isDocOn('contract') && !this.contractPeriodOk) {
        return '雇用契約書の契約期間（開始日・終了日・70歳以上の終了日）を入れてください。'
      }
      if (!this.copiesOk) {
        return `${this.copiesDocs.filter(k => !this.copiesOkOf(k)).map(k => this.docName(k)).join('・')}の枚数（1人あたり）を入れてください。`
      }
      if (this.isDocOn('addrLabel') && this.docInput.labelStart === null) {
        return '宛名シールの印字開始位置（何面目から）を選んでください。'
      }
      return ''
    },

    /**
     * 勤務ごとに出す書類（業務報告書（予定）・雇用契約書）が ON か（一覧に「出力する勤務」の列を出す）
     */
    needEmployment() {
      return EMPLOYMENT_DOCS.some(k => this.isDocOn(k))
    },

    /**
     * 業務報告書（予定）の期間が入っているか（開始 ≦ 終了）
     */
    reportPeriodOk() {
      return monthPeriodOk(this.docInput.reportFrom, this.docInput.reportTo)
    },

    /**
     * 業務報告書（予定）の期間の月数（開始〜終了。両端を含む。期間が入っていなければ 0）
     */
    reportMonths() {
      return this.reportPeriodOk ? monthCount(this.docInput.reportFrom, this.docInput.reportTo) : 0
    },

    /**
     * 予定の期間の欄の横に出す案内（何が足りないかを具体的に）
     */
    reportPeriodHint() {
      return monthPeriodHint(this.docInput.reportFrom, this.docInput.reportTo)
    },

    /**
     * 業務報告書（実績）の期間が入っているか・月数・案内（予定と同じ決まり）
     */
    actualPeriodOk() {
      return monthPeriodOk(this.docInput.actualFrom, this.docInput.actualTo)
    },

    actualMonths() {
      return this.actualPeriodOk ? monthCount(this.docInput.actualFrom, this.docInput.actualTo) : 0
    },

    actualPeriodHint() {
      return monthPeriodHint(this.docInput.actualFrom, this.docInput.actualTo)
    },

    /**
     * 雇用契約書の契約期間が入っているか（開始日・終了日・70 歳以上の終了日が全部あり、開始 ≦ 終了）
     */
    contractPeriodOk() {
      const f = this.docInput.contractFrom
      const t = this.docInput.contractTo
      const t70 = this.docInput.contractTo70
      const ok = (x) => /^\d{4}-\d{2}-\d{2}$/.test(x || '')
      return ok(f) && ok(t) && ok(t70) && f <= t && f <= t70
    },

  },

  async created() {
    this.getSessionStorage()
    this.getAreas()
    document.addEventListener('click', this.onDocumentClick)
    // URL に清掃員コードがあれば（サブタスク「●●送付」のボタンから。akbsOutput/taskLink が組む）、その人を検索してチェックした状態で開く
    //   set：ON にする一式（hire・hireTool・change・end・retire。hireTool はサブタスクの名前に「備品」を含むとき）。docs：一式ではなく直接 ON にする書類の札のキー（カンマ区切り）。retireDate（YYYY-MM-DD）：退職届の退職日の初期値（AKBS にまだ退職日が無いときに使う。退職一式も ON）
    //   contract=0：雇用契約書を OFF にする（金額のみ変更で時給・交通費が下がっていないとき）。contractFrom：契約期間の開始日の初期値。notice：赤字で出す知らせ
    //   emps：チェックを付ける物件・契約コード（勤務のキーをカンマ区切り。空なら 1 つも付けない。物件追加・契約内容変更・金額のみ変更で、雇用指示画面でした操作からバックが決める）
    // スマホ・タブレットは「契約書返送」だけ（出力・給与奉行・書類設定は画面が大きくないと使えない。ユーザー指示）。mode の watch が一覧を取る
    const q = this.$route.query || {}
    // URL に tab=contractReturn があれば（サブタスク「●●返送確認」のボタンから）、契約書返送のタブを、検索欄に清掃員コードを入れて検索した状態で開く（mode の watch が一覧を取る）
    if (q.tab === 'contractReturn' && q.staffCode) {
      this.cr.filter = { ...this.cr.filter, name: String(q.staffCode) }
    }
    if (this.isMobileOrTablet) {
      this.mode = 'contractReturn'
      return
    }
    if (q.tab === 'contractReturn') {
      this.mode = 'contractReturn'
      return
    }
    // URL に tab=bugyo があれば（サブタスク「給与奉行●●」のボタンから）、給与奉行のタブを開く。人の検索はせず「未出力の清掃員のみ」が ON の初期状態（mode の watch が一覧を取る）
    if (q.tab === 'bugyo') {
      this.mode = 'bugyo'
      return
    }
    if (q.staffCode) {
      // 初期値（書類・人・勤務）がそろうまで少し掛かるので、白いマスクで待ってもらう（出力中と同じ見た目。バーは出さない）
      this.progress = { active: true, percent: null, text: '出力画面の準備をしています' }
      try {
        await this.initFromTaskLink(q)
      } finally {
        this.progress = { active: false, percent: 0, text: '' }
      }
      return
    }
    this.getDocs()
    this.getList()
  },

  beforeDestroy() {
    document.removeEventListener('click', this.onDocumentClick)
    // QR 読込のカメラが動いていれば止める
    this.crStopCamera()
  },

  methods: {
    /**
     * サブタスク「●●送付」のボタンから開いたときの初期値を入れる（URL の query から。created の説明を参照）
     * @param {Object} q $route.query
     */
    async initFromTaskLink(q) {
      this.filter = { ...this.filter, name: String(q.staffCode), scope: 'all' }
      await this.getDocs()
      await this.getList(1)
      const row = this.list.find(r => String(r.staffDailyCleaning) === String(q.staffCode))
      if (row) {
        this.setChecked(row, true)
      }
      let setKey = ['hire', 'hireTool', 'change', 'end', 'retire'].includes(q.set) ? q.set : ''
      if (/^\d{4}-\d{2}-\d{2}$/.test(q.retireDate || '')) {
        this.$set(this.retireDates, String(q.staffCode), q.retireDate)
        setKey = 'retire'
      }
      const set = setKey ? this.docSets.find(s => s.key === setKey) : null
      if (set && this.setState(set) !== 'all') {
        this.toggleSet(set)
      }
      // 入社・再入社で代勤専門の雇用指示がある人の代勤賃金簿は、業務報告書（予定）の「代勤専門の人には…付ける」（初期 ON）で期間の月数分付くので、札の「代勤賃金簿」はここで ON にしない（二重に付かないように）
      // 一式ではなく書類を直接 ON にする（docs＝札のキーをカンマ区切り。金額のみ変更：雇用契約書と雇用契約締結案内だけ）
      for (const key of String(q.docs || '').split(',').filter(k => k)) {
        if (this.docConfMap[key] && this.docConfMap[key].enabled) {
          this.$set(this.docs, key, true)
        }
      }
      if (q.contract === '0') {
        this.$delete(this.docs, 'contract')
      }
      // 契約期間の開始日（入社日や変わった内容が効き始める日）。終了日・70 歳以上の終了日・業務報告書の期間は watch が自動で入れる
      if (/^\d{4}-\d{2}-\d{2}$/.test(q.contractFrom || '')) {
        this.docInput.contractFrom = q.contractFrom
      }
      this.linkNotice = String(q.notice || '')
      // 勤務のチェックを、申請で変えた物件・契約コードだけにする（人のチェックや一式が入れた初期値を入れ直す）
      // emps が無いとき（新規入社・退職・申請の無い古い雇い入れなど）は、ここまでの初期値のまま
      if (row && q.emps !== undefined) {
        const keys = String(q.emps || '').split(',').filter(k => k)
        const sel = {}
        for (const e of row.employments) {
          if (keys.includes(this.empKey(e))) {
            sel[this.empKey(e)] = true
          }
        }
        this.$set(this.empSel, row.staffDailyCleaning, sel)
      }
    },

    // ------------------------------------------------------------
    // エリアの選択の一覧
    // ------------------------------------------------------------

    toggleAreaPanel() {
      if (this.areaPanel.open) {
        this.closeAreaPanel(true)
        return
      }
      const btn = this.$refs.areaBtn
      const rect = btn ? btn.getBoundingClientRect() : { bottom: 100, left: 0 }
      this.areaPanel = { open: true, top: rect.bottom + 4, left: rect.left, before: (this.filter.areas || []).join(',') }
    },

    /**
     * エリアの一覧を閉じる（選択が変わっていれば検索する）
     */
    closeAreaPanel(search) {
      if (!this.areaPanel.open) {
        return
      }
      const changed = (this.filter.areas || []).join(',') !== this.areaPanel.before
      this.areaPanel = { ...this.areaPanel, open: false }
      if (search && changed) {
        this.getList(1)
      }
    },

    // 一覧の外を押したら閉じる（ボタンと一覧の中は click.stop で来ない）
    onDocumentClick() {
      this.closeAreaPanel(true)
      // 宛名シールのシートの図も、外を押したら閉じる（図の中は click.stop で来ない）
      this.labelPick = false
    },

    /**
     * エリアの選択肢を取る（AKBS のエリア管理の「給与以外」のエリア。稼働管理・月次確認と同じ）
     * API: akbsOutput/getAreas
     */
    async getAreas() {
      try {
        const res = await backend.searchData('akbsOutput/getAreas', {})
        this.areas = res.data && Array.isArray(res.data.data) ? res.data.data : []
        // 控えてあった選択は、今の候補にあるものだけ残す（エリアが消されていることがある）
        const ids = new Set(this.areas.map(a => String(a.areaId)))
        this.filter.areas = (this.filter.areas || []).map(String).filter(id => ids.has(id))
      } catch (e) {
        console.log(e)
        this.areas = []
      }
    },

    /**
     * 一覧を検索する
     * @param {Number} num 表示するページ（検索ボタンは1ページ目、ページの切り替えは指定なし）
     */
    async getList(num) {
      // 取得中はもう一度取りに行かない（Enter・絞り込みの変更・更新の矢印のどこから呼ばれても。先に始めた古い結果が後から上書きしないように）
      if (this.loading) {
        return
      }
      this.loading = true
      this.setSessionStorage()
      if (num) {
        this.page = num
      }
      try {
        const params = {
          name: this.filter.name,
          areaIds: (this.filter.areas || []).join(','),
          ageDate: this.filter.ageDate || '',
          age: this.filter.age === '' || this.filter.age === null ? '' : Number(this.filter.age),
          scope: this.filter.scope,
          page: this.page,
          perPage: this.perPage
        }
        const res = await backend.searchData('akbsOutput/getStaffList', params)
        const rows = res.data && Array.isArray(res.data.data) ? res.data.data : []
        // 備考の保存に使うキーを最初から持たせる。勤務の候補は配列にそろえる
        this.list = rows.map(r => ({ ...r, memo: r.memo || '', memoBefore: r.memo || '', memoSaving: false, photoSaving: false, employments: Array.isArray(r.employments) ? r.employments : [] }))
        this.records = this.list.length ? Number(this.list[0].totalSize) : 0
      } catch (error) {
        console.log(error)
        this.list = []
        this.records = 0
      } finally {
        this.loading = false
      }
    },

    // ------------------------------------------------------------
    // 選択（ページをまたいで持つ）
    // ------------------------------------------------------------

    isChecked(row) {
      return !!this.checked[row.staffDailyCleaning]
    },

    /**
     * 人のチェック（出力する勤務と連動。ユーザー決定 2026-10-09）
     * ON：その人の勤務を全部 ON にする。OFF：その人の勤務を全部 OFF にする
     * @param {Boolean} keepEmp 勤務の選択を変えない（勤務のチェックから人を ON にするとき）
     */
    setChecked(row, on, keepEmp) {
      if (on) {
        // 勤務の候補も控える（ほかのページへ移っても、出力のときにどの勤務を出すか分かるように）
        // 退職日も控える（退職届を出すとき、AKBS に退職日が無い人を確認するため）
        this.$set(this.checked, row.staffDailyCleaning, { staffDailyCleaning: row.staffDailyCleaning, nameStaff: row.nameStaff, nameKanaStaff: row.nameKanaStaff || '', dateResignation: row.dateResignation || null, employments: row.employments || [] })
        if (!keepEmp) {
          this.setEmpAll(row, true)
        }
      } else {
        this.$delete(this.checked, row.staffDailyCleaning)
        this.setEmpAll(row, false)
      }
    },

    toggle(row) {
      this.setChecked(row, !this.isChecked(row))
    },

    clearChecked() {
      this.checked = {}
      this.empSel = {}
    },

    /**
     * 見出しのチェック：絞り込みに当たる全員を（ページをまたいで）選ぶ・外す
     * 今の絞り込みで全件を 1 ページとして取り直し、1 人ずつ選ぶ（勤務も全部 ON）・外す（絞り込みの外で選んだ人はそのまま）
     * API: akbsOutput/getStaffList
     */
    async toggleAllFiltered(on) {
      if (this.selectingAll || !this.records) {
        return
      }
      this.selectingAll = true
      // 全員分を取るのに少し待つので、白いマスクで知らせる（バー無し）
      this.progress = { active: true, percent: null, text: on ? `検索にヒットした${this.records}人全員を選んでいます` : `検索にヒットした${this.records}人全員のチェックを外しています` }
      try {
        const params = {
          name: this.filter.name,
          areaIds: (this.filter.areas || []).join(','),
          ageDate: this.filter.ageDate || '',
          age: this.filter.age === '' || this.filter.age === null ? '' : Number(this.filter.age),
          scope: this.filter.scope,
          page: 1,
          perPage: this.records
        }
        const res = await backend.searchData('akbsOutput/getStaffList', params)
        const rows = res.data && Array.isArray(res.data.data) ? res.data.data : []
        for (const r of rows) {
          this.setChecked({ ...r, employments: Array.isArray(r.employments) ? r.employments : [] }, on)
        }
      } catch (e) {
        console.log(e)
        await dialogs.showErrorDialog('全員を選ぶ・外す', '全員分を取れませんでした。もう一度お試しください。')
      } finally {
        this.selectingAll = false
        this.progress = { active: false, percent: 0, text: '' }
      }
    },

    /**
     * 選んだ人と書類を全部外す
     */
    clearAll() {
      this.clearChecked()
      this.docs = {}
    },

    // ------------------------------------------------------------
    // 出力する書類の選択
    // ------------------------------------------------------------

    docOf(key) {
      return this.docConfMap[key] || DOCS[key] || { name: key }
    },

    /**
     * 業務報告書に自動で付ける書類の決まり（書類設定の行の札の説明・使い道に使う）
     */
    autoSheetOf(key) {
      return autoSheetOf(key) || { use: '', tip: '' }
    },

    /**
     * 宛名シールの印字開始位置を選んだ（0〜11）。選んだら図を閉じる
     */
    pickLabelStart(pos) {
      this.docInput.labelStart = pos
      this.labelPick = false
    },

    /**
     * その書類の枚数が入っているか（1 以上の整数）
     */
    copiesOkOf(key) {
      const n = Number(this.docInput.copies[key])
      return Number.isInteger(n) && n >= 1
    },

    docName(key) {
      return this.docOf(key).name
    },

    isDocOn(key) {
      return !!this.docs[key]
    },

    /**
     * その書類が入っている一式の名前（札のツールチップ。「入社書類一式・契約変更一式」など）
     */
    setNamesOf(key) {
      return this.docSets.filter(s => s.docs.includes(key)).map(s => s.name).join('・')
    },

    toggleDoc(key) {
      if (this.isDocOn(key)) {
        this.$delete(this.docs, key)
      } else {
        this.$set(this.docs, key, true)
        // 宛先A4 と長形3号は同じ封筒の選択なので片方だけ（ユーザー決定）
        if (key === 'addrA4') {
          this.$delete(this.docs, 'addrEnvelope')
        } else if (key === 'addrEnvelope') {
          this.$delete(this.docs, 'addrA4')
        }
        // 宛名シールを ON にしたら、印字開始位置を無しに戻してシートの図を開き、そのつど選んでもらう（前の選択を引きずらない。ユーザー指示）
        if (key === 'addrLabel') {
          this.docInput.labelStart = null
          this.labelPick = true
        }
        // 業務報告書（実績）を選んだら、実績の期間を過去 1 年分（今月まで）に戻す（前に直した期間を引きずらない）
        if (key === 'reportActual') {
          const p = defaultActualPeriod()
          this.docInput.actualFrom = p.from
          this.docInput.actualTo = p.to
        }
      }
    },

    /**
     * 一式の状態（all：中の書類が全部 ON、空：それ以外）
     * 一部だけ ON の印は出さない（入社書類一式を選ぶと共通の書類で契約変更一式が「一部」になって紛らわしい。ユーザー指示 2026-10-09）
     */
    setState(set) {
      const on = set.docs.filter(k => this.isDocOn(k)).length
      return on && on === set.docs.length ? 'all' : ''
    },

    /**
     * 一式の名前を押した：全部 ON なら全部 OFF、それ以外は全部 ON
     * ON にしたときは、チェック済みの人の出力する勤務を一式に合わせて入れ直す（入社＝全部、契約変更＝最近の開始日だけ。ほかの一式は変えない）
     * チェックしていない人は触らない（勤務を付けると人のチェックも付いてしまうため）
     */
    toggleSet(set) {
      const on = this.setState(set) !== 'all'
      for (const k of set.docs) {
        if (on) {
          this.$set(this.docs, k, true)
        } else {
          this.$delete(this.docs, k)
        }
      }
      if (on && (set.key === 'hire' || set.key === 'change')) {
        for (const c of Object.values(this.checked)) {
          this.applyEmpDefault(c, set.key === 'change' ? 'recent' : 'all')
        }
      }
    },

    // ------------------------------------------------------------
    // 出力する勤務（物件・契約コード）の選択
    // ------------------------------------------------------------

    empKey(e) {
      return `${e.cdSite}-${e.cdContract}`
    },

    /**
     * 画面に出す（選べる）勤務
     * 業務報告書（予定・実績）が ON：その期間に重なる雇用指示（開始日 ≦ 期間の末日、終了日が無いか ≧ 期間の初日）
     * 雇用契約書が ON：契約期間（開始日〜「終了日」と「70歳以上の終了日」の遅い方）に重なる雇用指示
     * 両方 ON：どちらかの期間に重なる雇用指示を全部（出力のときは書類ごとにその期間の分だけ使う。ユーザー決定）
     * それ以外：終わっていない雇用指示（終了日が無いか今日以降）
     * 代勤専門の雇用指示（isOp。物件コード 000000000）は雇用契約書・名札に使う。業務報告書（予定）では用紙は作らないが、代勤賃金簿を付ける切り替えが ON なら出す（選んだ分だけ付ける）
     * 業務報告書（実績）が ON のときは「代勤」の枚（OP_EMPLOYMENT）も最後に足す（別の物件での代勤・代勤専門の有休。無ければ用紙は作られない）
     *   代勤専門の雇用指示と同じキー（000000000-）なので、その人に既にあれば足さない
     * @param {Object} row 一覧の行（または checked の控え）
     */
    empVisible(row) {
      const emps = row.employments || []
      const periods = this.empPeriods
      // 代勤専門の雇用指示は契約書と名札（アポロ標準になる）に使う。業務報告書（予定）で代勤賃金簿を付けるときも出す（選んだ勤務の分だけ付ける。ユーザー指示）
      const showOp = this.isDocOn('contract') || this.isDocOn('nameplate') || !this.needEmployment || this.activeAutoSheets.length > 0
      let list
      if (periods.length) {
        const overlaps = (e, p) => (!e.startDate || e.startDate <= p.end) && (!e.endDate || e.endDate >= p.start)
        list = emps.filter(e => periods.some(p => overlaps(e, p)))
      } else {
        const today = daysAgo(0)
        list = emps.filter(e => !e.endDate || e.endDate >= today)
      }
      if (!showOp) {
        list = list.filter(e => !e.isOp)
      }
      if (this.isDocOn('reportActual') && !list.some(e => this.empKey(e) === this.empKey(OP_EMPLOYMENT))) {
        list = [...list, OP_EMPLOYMENT]
      }
      return list
    },

    /**
     * その人の勤務を一式に合わせて入れ直す（'all'：画面に出している勤務を全部 ON、'recent'：そのうち開始日が 31 日前以降だけ ON）
     */
    applyEmpDefault(row, mode) {
      const sel = {}
      const since = daysAgo(CHANGE_RECENT_DAYS)
      for (const e of this.empVisible(row)) {
        if (mode !== 'recent' || (e.startDate && e.startDate >= since)) {
          sel[this.empKey(e)] = true
        }
      }
      this.$set(this.empSel, row.staffDailyCleaning, sel)
    },

    /**
     * その人の勤務を全部 ON／OFF にする
     */
    setEmpAll(row, on) {
      if (!on) {
        this.$delete(this.empSel, row.staffDailyCleaning)
        return
      }
      this.applyEmpDefault(row, 'all')
    },

    isEmpOn(row, e) {
      const sel = this.empSel[row.staffDailyCleaning]
      return !!(sel && sel[this.empKey(e)])
    },

    /**
     * その人の勤務が全部 ON か（勤務が無い人は false）
     */
    isEmpAllOn(row) {
      const emps = this.empVisible(row)
      return emps.length > 0 && emps.every(e => this.isEmpOn(row, e))
    },

    /**
     * 勤務のチェック（人のチェックと連動：勤務を 1 つでも ON にしたら人も ON。勤務を全部 OFF にしても人は OFF にしない）
     */
    toggleEmp(row, e) {
      if (!this.empSel[row.staffDailyCleaning]) {
        this.$set(this.empSel, row.staffDailyCleaning, {})
      }
      const sel = this.empSel[row.staffDailyCleaning]
      const key = this.empKey(e)
      if (sel[key]) {
        this.$delete(sel, key)
      } else {
        this.$set(sel, key, true)
        if (!this.isChecked(row)) {
          this.setChecked(row, true, true)
        }
      }
    },

    /**
     * 見出しのチェック：このページの全員の勤務を全部 ON（人も ON）／全部 OFF（人はそのまま）
     */
    toggleAllEmpInPage(on) {
      for (const row of this.list) {
        if (!this.empVisible(row).length) {
          continue
        }
        if (on) {
          this.setEmpAll(row, true)
          if (!this.isChecked(row)) {
            this.setChecked(row, true, true)
          }
        } else {
          this.setEmpAll(row, false)
        }
      }
    },

    // ------------------------------------------------------------
    // 会社印の得意先
    // ------------------------------------------------------------

    openSeal() {
      this.seal.open = true
      this.getClients()
    },

    /**
     * 会社印の設定用の得意先の一覧を取る
     * API: akbsOutput/getClients
     */
    async getClients() {
      this.seal.loading = true
      try {
        const res = await backend.searchData('akbsOutput/getClients', { name: this.seal.name, sealOnly: this.seal.sealOnly })
        const rows = res.data && Array.isArray(res.data.data) ? res.data.data : []
        this.seal.list = rows.map(c => ({ ...c, useSeal: !!c.useSeal, saving: false }))
      } catch (e) {
        console.log(e)
        this.seal.list = []
      } finally {
        this.seal.loading = false
      }
    },

    /**
     * 得意先の会社印あり・なしを保存する（押したらすぐ）
     * API: akbsOutput/saveSeal
     */
    async saveSeal(c, on) {
      c.saving = true
      try {
        await backend.postData('akbsOutput/saveSeal', { cdBillingAddress: c.cdBillingAddress, useSeal: !!on, updateUser: this.$store.getters.user.id })
        c.useSeal = !!on
      } catch (e) {
        console.log(e)
        await dialogs.showErrorDialog('保存エラー', '会社印の設定を保存できませんでした。もう一度お試しください。')
      } finally {
        c.saving = false
      }
    },

    /**
     * 出力実行（ファイルの生成は書類ごとにこれから作る。今は選んだ人と書類を出すだけ）
     */
    async download() {
      if (!this.checkedCount || !this.docCount) {
        return
      }
      // 書類に要る入力
      if (this.isDocOn('report') && !this.reportPeriodOk) {
        await dialogs.showErrorDialog('業務報告書（予定）の期間', '業務報告書（予定）を出すには、開始・終了の年月を入れてください。開始は終了より前にしてください。')
        return
      }
      if (this.isDocOn('reportActual') && !this.actualPeriodOk) {
        await dialogs.showErrorDialog('業務報告書（実績）の期間', '業務報告書（実績）を出すには、開始・終了の年月を入れてください。開始は終了より前にしてください。')
        return
      }
      if (this.isDocOn('contract') && !this.contractPeriodOk) {
        await dialogs.showErrorDialog('雇用契約書の契約期間', '雇用契約書を出すには、契約期間（開始日・終了日・70歳以上の終了日）を入れてください。開始は終了より前にしてください。')
        return
      }
      if (!this.copiesOk) {
        await dialogs.showErrorDialog('枚数', this.notReadyReason)
        return
      }
      // 宛名シールの印字開始位置は初期値が無いので、選ぶまで出さない（ユーザー指示）
      if (this.isDocOn('addrLabel') && this.docInput.labelStart === null) {
        await dialogs.showErrorDialog('宛名シールの印字開始位置', this.notReadyReason)
        this.labelPick = true
        return
      }
      // 業務報告書の終了（一番近い 4 月）と雇用契約書の終了日（1 年後）は元から違うので、ずれは知らせない
      // 勤務ごとの書類（業務報告書・雇用契約書・名札）は、出力する勤務が選ばれていない人の分は作らないだけ（止めない。ユーザー決定）。
      //   1 つも作れなければ runPdfJob の emptyMessage で知らせる
      // 退職届：AKBS にまだ退職日が無い人（承認前に出すとき）がいれば、退職日を入れてもらってから出す（ユーザー指示）
      if (this.isDocOn('retireNotice') && !(await this.confirmRetireDates())) {
        return
      }
      // 「代勤専門の人には代勤賃金簿を…付ける」と札の「代勤賃金簿」の両方が ON なら、代勤専門の人に 2 回付くので確かめる（ユーザー指示）
      if (this.opSheetDouble) {
        const answer = await dialogs.showWarningConfirmDialog('代勤賃金簿が二重になります', `「代勤専門の人には代勤賃金簿を期間の月数分付ける」と、札の「${this.opSheetDouble}」の両方が ON です。\n代勤専門の人には両方が付きます。\n\nこのまま出力しますか。`, 'このまま出力', '戻る')
        if (answer !== 'YES') {
          return
        }
      }
      this.downloading = true
      try {
        // 作った PDF の結果（true：ダウンロードした、false：作れなかった・エラー、undefined：出すものが無かった）
        // 選んだ人（カナ順）を何人かずつに区切り、区切りごとに PDF を作る（1400 人を 1 本にしない。ユーザー決定）
        // 業務報告書が入るときは 100 人、入らないときは 200 人（ユーザー決定）。書類一式と雇用契約書は同じ区切り
        const withReport = this.isDocOn('report') || this.isDocOn('reportActual')
        const chunkSize = withReport ? OUTPUT_CHUNK_WITH_REPORT : OUTPUT_CHUNK
        const people = this.checkedInOrder()
        const chunks = []
        for (let i = 0; i < people.length; i += chunkSize) {
          chunks.push(people.slice(i, i + chunkSize))
        }
        // 業務報告書（実績）を Excel で出すか（書類一式の PDF には入れず、同じ区切りで別のファイルにする）
        const actualExcel = this.isDocOn('reportActual') && this.docInput.actualFormat === 'excel'
        // 書類一式.pdf：固定書類（書類設定で登録した PDF。印は人ごとの値に）と、書類一式の中で作る書類（業務報告書・退職届）を、人ごとに書類設定の順でつなげる
        const bundleDocs = this.docList.filter(k => this.isDocOn(k) && (!this.docConfMap[k].system || BUNDLE_SYSTEM_KEYS.includes(k)) && !(actualExcel && k === 'reportActual'))
        // 宛先A4・長形3号（どちらか片方）は書類一式の中で、その人の束の先頭に入る
        const addrKind = this.isDocOn('addrA4') ? 'a4' : this.isDocOn('addrEnvelope') ? 'trifold' : null
        // 区切りの人数を超えてファイルが分かれるときだけ、先に知らせる（1 本で済むときはダイアログ無し。ユーザー指示）
        //   区切りに関わるのは書類一式・雇用契約書・Excel の実績。宛名シールだけなら全員 1 本なので知らせない
        const chunked = bundleDocs.length > 0 || !!addrKind || this.isDocOn('contract') || actualExcel
        if (chunked && chunks.length > 1) {
          const lines = [
            `選んだ ${people.length} 人を ${chunkSize} 人ずつ、${chunks.length} 本のファイルに分けて順に出します（ファイル名の末尾に _1、_2 … が付きます）。`,
            withReport ? '※業務報告書が入るときは 100 人ずつです。' : '※業務報告書が入らないときは 200 人ずつです。'
          ]
          if (this.isDocOn('contract') && (bundleDocs.length || addrKind)) {
            lines.push('雇用契約書は両面印刷のため、書類一式とは別の PDF にします。')
          }
          if (this.isDocOn('addrLabel')) {
            lines.push('宛名シールは全員で 1 本の別の PDF にします。')
          }
          lines.push('1 本終わるごとにその人のチェックが外れます。途中で止まっても、残っている人をもう一度「出力実行」すれば続きから出せます。')
          lines.push('ブラウザが「複数ファイルのダウンロード」の許可を聞いたら許可してください。')
          const answer = await dialogs.showConfirmDialog('PDF を分けて出します', `${lines.join('\n')}\n\n出力しますか。`)
          if (answer !== 'YES') {
            return
          }
        }
        // 宛名シールは別 PDF で、人数の区切りに関係なく全員で 1 本（先に出す。ユーザー決定）
        let labelResult
        // 「中止」を押した（そこから先は出さない。人のチェックはそのまま＝続きから出せる）
        let cancelled = false
        if (this.isDocOn('addrLabel')) {
          labelResult = await this.downloadLabels(people)
          // 出したらシートの残りの面が変わるので、次に出すときはもう一度選んでもらう
          if (labelResult === true) {
            this.docInput.labelStart = null
          }
          cancelled = labelResult === 'cancelled'
        }
        let stopped = false
        for (let i = 0; i < chunks.length && !stopped && !cancelled; i++) {
          const part = chunks.length > 1 ? { no: i + 1, total: chunks.length } : null
          // 作った PDF の結果（true：ダウンロードした、false：作れなかった・エラー、'cancelled'：中止した、undefined：出すものが無かった）
          const results = []
          if (bundleDocs.length || addrKind) {
            results.push(await this.downloadBundle(bundleDocs, chunks[i], part, addrKind))
          }
          // 雇用契約書：両面印刷するので別の PDF（同じものを 2 部続けて。逆順は効かせない）。書類一式を中止したら作らない
          if (this.isDocOn('contract') && !results.includes('cancelled')) {
            results.push(await this.downloadContract(chunks[i], part))
          }
          // 業務報告書（実績）の Excel：別ファイル（同じ区切り）。前のものを中止したら作らない
          if (actualExcel && !results.includes('cancelled')) {
            results.push(await this.downloadActualExcel(chunks[i], part))
          }
          const ran = results.filter(r => r !== undefined)
          if (!ran.length) {
            continue
          }
          if (ran.includes('cancelled')) {
            cancelled = true
            continue
          }
          if (ran.every(r => r === true)) {
            // この区切りの人のチェックを外す（出力が終わればチェックを外す、の仕様。途中で止まっても残りの人はチェックが付いたまま＝続きから出せる）
            for (const c of chunks[i]) {
              this.$delete(this.checked, c.staffDailyCleaning)
              this.$delete(this.empSel, c.staffDailyCleaning)
            }
          } else {
            // 作れなかった区切りがあればそこで止める（その区切りと残りの人は選び直せるよう残す）
            stopped = true
          }
        }
        // 宛名シールだけを出したとき（区切りごとの書類が無い）は、ダウンロードできていれば人のチェックを外す
        if (!bundleDocs.length && !addrKind && !this.isDocOn('contract') && !actualExcel && labelResult === true) {
          this.clearChecked()
        }
        if (cancelled) {
          await dialogs.showInfoDialog('出力を中止しました', '中止した分から先は出していません。\n出していない人はチェックが付いたままなので、もう一度「出力実行」で続きから出せます。')
        } else if (stopped && chunks.length > 1) {
          await dialogs.showInfoDialog('出力を止めました', '途中で作れなかったので、そこで止めました。\n出せなかった人はチェックが付いたままなので、もう一度「出力実行」で続きから出せます。')
        }
        // まだ作っていない書類（システムが作る書類のうち、業務報告書・雇用契約書・書類一式で作るもの以外）
        const pending = Object.keys(this.docs).filter(k => !['report', 'reportActual', 'contract', ...BUNDLE_SYSTEM_KEYS].includes(k) && this.docConfMap[k] && this.docConfMap[k].system)
        if (pending.length && !cancelled) {
          await dialogs.showInfoDialog('まだ作っていない書類', `次の書類のファイルの生成はこれから作ります。\n${pending.map(k => `・${this.docName(k)}`).join('\n')}`)
        }
      } finally {
        this.downloading = false
      }
    },

    /**
     * チェックした人を、出力の順（カナ → 清掃員コード）に並べて返す
     */
    checkedInOrder() {
      return Object.values(this.checked).sort((a, b) => {
        const ka = String(a.nameKanaStaff || '')
        const kb = String(b.nameKanaStaff || '')
        if (ka !== kb) {
          return ka < kb ? -1 : 1
        }
        return String(a.staffDailyCleaning) < String(b.staffDailyCleaning) ? -1 : 1
      })
    },

    /**
     * 業務報告書（予定・実績）に渡す人と勤務（人はカナ順。勤務は画面に出している（期間に効いている）もののうち選んでいるものだけ）
     * 人の中の並びはバックが 月 → 勤務 → ページ にする
     * @param {Boolean} isActual 実績なら「代勤」の枚（OP_EMPLOYMENT）も渡す
     * @param {Array} people この区切りの人（checked の控え。カナ順）
     */
    reportStaffs(isActual, people) {
      return people.map(c => {
        const sel = this.empSel[c.staffDailyCleaning] || {}
        return {
          staffCode: c.staffDailyCleaning,
          employments: this.empVisible(c).filter(e => sel[this.empKey(e)] && (isActual || !e.isOp)).map(e => ({ cdSite: e.cdSite, cdContract: e.cdContract }))
        }
      }).filter(s => s.employments.length)
    },

    /**
     * 業務報告書（予定）に自動で付ける書類の枚数（人ごと）。1 枚＝1 か月
     * 業務報告書の期間（全員同じ。年齢で変えない）のうち、一覧の札で選んだ雇用指示（開始日〜終了日）が 1 つでも効いている月の数（同じ月に勤務が 2 つあっても 1 枚）。無い人・外した人は入れない
     * opOnly（代勤賃金簿）は代勤専門の雇用指示だけ数える。退職日より後の月は数えない（業務報告書と同じ）
     * @param {*} auto 自動で付ける書類（AUTO_SHEETS の 1 つ）
     * @param {Array} people 区切りの人（checked の控え。employments・dateResignation を持つ）
     * @returns { 清掃員コード: 枚数 }
     */
    autoSheetCopies(auto, people) {
      const from = this.docInput.reportFrom
      const to = this.docInput.reportTo
      const ok = (x) => /^\d{4}-\d{2}$/.test(x || '')
      const result = {}
      if (!ok(from) || !ok(to)) {
        return result
      }
      for (const c of people) {
        // 一覧の札で選んだ雇用指示だけ（外した人には付けない）。実績用だけの「代勤」の枚（startDate なし）は数えない
        const sel = this.empSel[c.staffDailyCleaning] || {}
        const emps = (c.employments || []).filter(e => e.startDate && sel[this.empKey(e)] && (!auto.opOnly || e.isOp))
        if (!emps.length) {
          continue
        }
        // 退職日があればその月まで
        const last = /^\d{4}-\d{2}-\d{2}$/.test(c.dateResignation || '') && c.dateResignation.slice(0, 7) < to ? c.dateResignation.slice(0, 7) : to
        let count = 0
        for (let ym = from; ym <= last; ym = monthAdd(ym, 1)) {
          // その月に雇用指示が 1 日でも効いていれば 1 枚
          if (emps.some(e => e.startDate.slice(0, 7) <= ym && (!e.endDate || e.endDate.slice(0, 7) >= ym))) {
            count++
          }
        }
        if (count) {
          result[String(c.staffDailyCleaning)] = count
        }
      }
      return result
    },

    /**
     * 書類一式.pdf をダウンロードする（固定書類と、システムが作る書類（業務報告書・退職届）を、選んだ人ごとに書類設定の順でつなげる。枚数の入力欄が「必要」の書類はその枚数分。逆順は人ごと）
     * 業務報告書（予定）が ON なら、自動で付ける書類（代勤賃金簿・勤務・交通費報告書）を期間の月数分、その人の束の最後に付ける（activeAutoSheets。ユーザー決定）
     * 別の PDF にするのは両面印刷の雇用契約書だけ
     * @param {Array<String>} keys ON の書類の key（書類設定の並び順）
     * @param {Array} people この区切りの人（checked の控え。カナ順）
     * @param {*} part 区切り { no, total }（1 本だけなら null。名前とファイル名に付ける）
     * @param {String} addrKind 宛先（'a4'：宛先A4、'trifold'：長形3号、null：無し）。あれば人ごとの束の先頭に 1 ページ
     * API: akbsOutput/bundleStart・reportProgress・reportFile
     */
    async downloadBundle(keys, people, part, addrKind) {
      const docs = keys.map(k => {
        const c = this.docConfMap[k]
        return { id: c.id, docKey: c.docKey || null, name: c.name, copies: c.copiesInput ? Number(this.docInput.copies[k]) || 1 : 1 }
      })
      if (docs.some(d => !d.id)) {
        await dialogs.showErrorDialog('書類一式', '書類設定がまだ保存されていない書類があります。書類設定を開いて保存してから出してください。')
        return false
      }
      // 業務報告書（予定）と一緒に、自動で付ける書類（代勤賃金簿・勤務・交通費報告書）を期間の月数分（人ごと）、その人の束の最後に付ける（AUTO_SHEETS の順）
      //   用紙は書類設定の専用の行の PDF。バックには固定書類と同じ形（docKey 無し・id で PDF を読む）で渡す。札の固定書類が ON でもそれとは別に付ける
      if (keys.includes('report')) {
        for (const a of this.activeAutoSheets) {
          const copiesByStaff = this.autoSheetCopies(a, people)
          if (Object.keys(copiesByStaff).length) {
            docs.push({ id: a.conf.id, docKey: null, name: a.conf.name, copies: 0, copiesByStaff, atEnd: true })
          }
        }
      }
      const staffs = people.map(c => ({ staffCode: c.staffDailyCleaning, staffName: c.nameStaff }))
      // 退職届の退職日（AKBS に退職日が無い人の分だけ。confirmRetireDates で入れたもの）
      const retireDates = {}
      if (keys.includes('retireNotice')) {
        for (const c of staffs) {
          const d = this.retireDates[String(c.staffCode)]
          if (d) {
            retireDates[String(c.staffCode)] = d
          }
        }
      }
      // 業務報告書（予定・実績）の期間（別々）と、人ごとの勤務
      const report = { plan: { from: this.docInput.reportFrom, to: this.docInput.reportTo, staffs: [] }, actual: { from: this.docInput.actualFrom, to: this.docInput.actualTo, staffs: [] } }
      if (keys.includes('report')) {
        report.plan.staffs = this.reportStaffs(false, people)
      }
      if (keys.includes('reportActual')) {
        report.actual.staffs = this.reportStaffs(true, people)
      }
      // 名札：出力する勤務の物件（代勤専門の雇用指示も。実績用だけの「代勤」の枚は無い）。選んでいなければバックが終わっていない雇用指示の物件を使う
      const nameplate = { staffs: [] }
      if (keys.includes('nameplate')) {
        nameplate.staffs = people.map(c => {
          const sel = this.empSel[c.staffDailyCleaning] || {}
          return {
            staffCode: c.staffDailyCleaning,
            employments: this.empVisible(c).filter(e => sel[this.empKey(e)] && (!e.isOp || e.startDate)).map(e => ({ cdSite: e.cdSite }))
          }
        })
      }
      return this.runPdfJob({
        name: `書類一式${this.partLabel(part)}`,
        startApi: 'akbsOutput/bundleStart',
        body: { docs, staffs, reverse: !!this.reverse, retireDates, report, nameplate, address: { kind: addrKind || null } },
        unit: '件',
        emptyMessage: '選んだ書類から 1 ページも作れませんでした（PDF が登録されていない・読めない、期間の中で効いている雇用指示が無い、など）。書類設定と期間を確かめてください。',
        fileName: `書類一式_${daysAgo(0).replace(/-/g, '')}${this.partSuffix(part)}.pdf`
      })
    },

    /**
     * 業務報告書（実績）の Excel をダウンロードする（別ファイル。書類一式と同じ区切り。1 シート＝1 人の 1 か月。逆順は効かせない）
     * @param {Array} people この区切りの人（checked の控え。カナ順）
     * @param {*} part 区切り { no, total }（1 本だけなら null）
     * API: akbsOutput/reportExcelStart・reportProgress・reportFile
     */
    async downloadActualExcel(people, part) {
      return this.runPdfJob({
        name: '業務報告書（実績）Excel',
        startApi: 'akbsOutput/reportExcelStart',
        body: { from: this.docInput.actualFrom, to: this.docInput.actualTo, staffs: this.reportStaffs(true, people) },
        unit: '枚',
        fileWord: 'Excel',
        contentType: XLSX_MIME,
        emptyMessage: '期間の中で実績のある勤務が無く、1 枚も作れませんでした。期間と出力する勤務を確かめてください。',
        fileName: `業務報告書（実績）_${daysAgo(0).replace(/-/g, '')}${this.partSuffix(part)}.xlsx`
      })
    },

    /**
     * 宛名シール（12 面）の PDF をダウンロードする（別 PDF。全員で 1 本。1 ページ目は選んだ面から。逆順は効かせない）
     * @param {Array} people 選んだ人（checked の控え。カナ順）
     * API: akbsOutput/labelStart・reportProgress・reportFile
     */
    async downloadLabels(people) {
      const staffs = people.map(c => ({ staffCode: c.staffDailyCleaning, staffName: c.nameStaff }))
      return this.runPdfJob({
        name: '宛名シール',
        startApi: 'akbsOutput/labelStart',
        body: { staffs, startPos: this.docInput.labelStart },
        unit: '人',
        emptyMessage: '選んだ人が清掃員マスタに見つからず、1 枚も作れませんでした。',
        fileName: `宛名シール_${daysAgo(0).replace(/-/g, '')}.pdf`
      })
    },

    // ------------------------------------------------------------
    // 給与奉行（タブ）：入社した人を複数人まとめて CSV に。出した人を控えて、初期値は「まだ出していない人」
    // ------------------------------------------------------------

    /**
     * 給与奉行タブの一覧を検索する（在職者だけ）
     * @param {Number} num 表示するページ（検索ボタンは 1 ページ目、ページの切り替えは指定なし）
     * API: akbsOutput/bugyoList
     */
    async bugyoGetList(num) {
      const b = this.bugyo
      // 取得中はもう一度取りに行かない（getList と同じ）
      if (b.loading) {
        return
      }
      b.loading = true
      if (num) {
        b.page = num
      }
      try {
        const params = {
          name: b.filter.name,
          hireFrom: b.filter.hireFrom || '',
          notExported: b.filter.notExported ? 'true' : 'false',
          page: b.page,
          perPage: b.perPage
        }
        const res = await backend.searchData('akbsOutput/bugyoList', params)
        b.list = res.data && Array.isArray(res.data.data) ? res.data.data : []
        b.records = b.list.length ? Number(b.list[0].totalSize) : 0
      } catch (e) {
        console.log(e)
        b.list = []
        b.records = 0
      } finally {
        b.loading = false
      }
    },

    bugyoIsChecked(row) {
      return !!this.bugyo.checked[row.staffDailyCleaning]
    },

    bugyoSetChecked(row, on) {
      if (on) {
        this.$set(this.bugyo.checked, row.staffDailyCleaning, row)
      } else {
        this.$delete(this.bugyo.checked, row.staffDailyCleaning)
      }
    },

    bugyoToggle(row) {
      this.bugyoSetChecked(row, !this.bugyoIsChecked(row))
    },

    /**
     * 見出しのチェック：このページの全員を選ぶ・外す（入社の人は少ないので、ページをまたいだ全員選択は無い）
     */
    bugyoToggleAllInPage(on) {
      for (const row of this.bugyo.list) {
        this.bugyoSetChecked(row, on)
      }
    },

    /**
     * 所属部門の決め方（CSV の ECHA003 の対応表）をモーダルで出す（文だけのダイアログだと 124 の区が読みにくい。ユーザー指示）
     */
    bugyoDeptHelp() {
      this.bugyo.helpOpen = true
    },

    // ------------------------------------------------------------
    // 契約書返送のタブ（雇用契約書の出力の記録。返送されたら返送済みにする）
    // ------------------------------------------------------------

    /**
     * 契約書返送の一覧を検索する
     * @param {Number} num 表示するページ（検索ボタンは 1 ページ目、ページの切り替えは指定なし）
     * API: akbsOutput/contractReturnList
     */
    async crGetList(num) {
      const c = this.cr
      // 取得中はもう一度取りに行かない（getList と同じ）
      if (c.loading) {
        return
      }
      c.loading = true
      if (num) {
        c.page = num
      }
      try {
        const params = {
          name: c.filter.name,
          outputFrom: c.filter.outputFrom || '',
          notReturned: c.filter.notReturned ? 'true' : 'false',
          page: c.page,
          perPage: c.perPage
        }
        const res = await backend.searchData('akbsOutput/contractReturnList', params)
        const rows = res.data && Array.isArray(res.data.data) ? res.data.data : []
        // 行ごとの保存中・備考の控えは最初から持たせる（あとから足すと画面が追いつかない）
        c.list = rows.map(r => ({ ...r, saving: false, memoBefore: r.memo, memoSaving: false }))
        c.records = c.list.length ? Number(c.list[0].totalSize) : 0
      } catch (e) {
        console.log(e)
        c.list = []
        c.records = 0
      } finally {
        c.loading = false
      }
    },

    /**
     * 返送済み・未返送を切り替える（押した瞬間に保存。未返送に戻すときは確認）
     * @param {*} row 行
     * @param {Boolean} on true：返送済みにする
     * @param {HTMLInputElement} input 押したチェックボックス（やめたとき・失敗したときに見た目を戻す）
     * API: akbsOutput/contractReturnSave
     */
    async crSetReturned(row, on, input) {
      const revert = () => {
        if (input) {
          input.checked = !!row.returnedDate
        }
      }
      if (!on) {
        const answer = await dialogs.showConfirmDialog('返送の取り消し', `${row.nameStaff}（${row.siteName}）の「返送済み」を取り消して未返送に戻します。よろしいですか。`)
        if (answer !== 'YES') {
          revert()
          return
        }
      }
      row.saving = true
      try {
        const res = await backend.postData('akbsOutput/contractReturnSave', { ids: [row.id], returned: on, updateUser: this.$store.getters.user.id })
        const result = res.data && res.data.data
        if (on && !((result && result.rows) || []).length) {
          // バックが何も変えなかった＝ほかの人がもう返送済みにしていた（別の PC・QR 読込）。画面が古いので取り直す
          await this.crGetList()
          return
        }
        this.crApplyReturned(result, on, [row])
      } catch (e) {
        console.log(e)
        revert()
        await dialogs.showErrorDialog('保存エラー', '返送の状態を保存できませんでした。もう一度お試しください。')
      } finally {
        row.saving = false
      }
    },

    /**
     * 保存の結果を画面の行に写す（バックは変えた行だけ返す。一覧は取り直さず、その行だけ変える＝未返送のみのときも行は残って薄くなる）
     * @param {*} result { rows: [{ id, returnedDate, returnedUserName }] }
     * @param {Boolean} on 返送済みにしたか
     * @param {Array} rows 対象にした行
     */
    crApplyReturned(result, on, rows) {
      const byId = {}
      for (const r of (result && result.rows) || []) {
        byId[r.id] = r
      }
      const me = this.$store.getters.user.user_name || ''
      for (const row of rows) {
        const r = byId[row.id]
        if (on) {
          // バックが返さなかった行はすでに返送済みだった行（そのまま）
          if (r) {
            row.returnedDate = r.returnedDate
            row.returnedUserName = r.returnedUserName || me
          }
        } else {
          row.returnedDate = ''
          row.returnedUserName = ''
        }
      }
      // 「返送未確認の契約書がほかにあります」を、変えた人・同じ契約開始日（同じ回の送付）の行で付け直す
      //   （このページにある行で判断。同じ人の行は続けて並ぶので、ページをまたぐことはまず無い）
      const groups = new Set(rows.map(r => `${r.staffDailyCleaning}|${r.startDate}`))
      for (const key of groups) {
        const mine = this.cr.list.filter(r => `${r.staffDailyCleaning}|${r.startDate}` === key)
        const has = mine.some(r => !r.returnedDate)
        for (const r of mine) {
          r.sameStartUnreturned = has
        }
      }
    },

    /**
     * 備考を保存する（欄を離れたとき。変わっていなければ送らない）
     * API: akbsOutput/contractReturnMemo
     */
    async crSaveMemo(row) {
      const memo = String(row.memo || '').trim()
      if (memo === String(row.memoBefore || '').trim()) {
        row.memo = memo
        return
      }
      row.memoSaving = true
      try {
        await backend.postData('akbsOutput/contractReturnMemo', { id: row.id, memo, updateUser: this.$store.getters.user.id })
        row.memo = memo
        row.memoBefore = memo
      } catch (e) {
        console.log(e)
        await dialogs.showErrorDialog('保存エラー', '備考を保存できませんでした。もう一度お試しください。')
        row.memo = row.memoBefore
      } finally {
        row.memoSaving = false
      }
    },

    // ---- QR コード読込（モーダル） ----

    /**
     * モーダルを開く。PC はバーコードリーダー用に入力欄にフォーカス、スマホ・タブレットはすぐカメラを起動（入力欄にフォーカスするとキーボードが出て邪魔）
     */
    crOpenQr() {
      const q = this.cr.qr
      q.open = true
      q.text = ''
      q.cameraError = ''
      q.read = false
      q.results = []
      q.okCount = 0
      q.ngCount = 0
      this.$nextTick(() => {
        if (this.isMobileOrTablet) {
          this.crStartCamera()
        } else if (this.$refs.qrInput) {
          this.$refs.qrInput.focus()
        }
      })
    },

    /**
     * モーダルを閉じる（カメラを止める。1 つでも読んでいれば一覧を取り直す）
     */
    crCloseQr() {
      clearTimeout(this.qrInputTimer)
      this.crStopCamera()
      this.cr.qr.open = false
      if (this.cr.qr.read) {
        this.crGetList()
      }
    },

    /**
     * 入力欄の文字（バーコードリーダーが打ち込んだもの）で返送済みにする
     */
    crReadQrText() {
      clearTimeout(this.qrInputTimer)
      const text = String(this.cr.qr.text || '').trim()
      if (text) {
        this.crReadQr(text)
      }
    },

    /**
     * 入力欄に文字が入ったら、打ち込みが 0.4 秒止まった時点で自動で確定する
     * バーコードリーダーは一瞬で全部打ち込むので、Enter を送らない設定のリーダーでも「確定」を押さずに済む（Enter を送るリーダーは Enter で先に確定する）
     */
    crOnQrInput() {
      clearTimeout(this.qrInputTimer)
      if (!String(this.cr.qr.text || '').trim()) {
        return
      }
      this.qrInputTimer = setTimeout(() => {
        if (!this.cr.qr.busy) {
          this.crReadQrText()
        }
      }, 400)
    },

    /**
     * QR の文字をバックに渡して返送済みにし、結果（清掃員名・物件名）を積む
     * @param {String} text QR の文字
     * API: akbsOutput/contractReturnByQr
     */
    async crReadQr(text) {
      const q = this.cr.qr
      if (q.busy) {
        return
      }
      q.busy = true
      const now = new Date()
      const time = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}:${String(now.getSeconds()).padStart(2, '0')}`
      try {
        const res = await backend.postData('akbsOutput/contractReturnByQr', { text, updateUser: this.$store.getters.user.id })
        const d = (res.data && res.data.data) || {}
        q.results.unshift({ time, error: d.error || '', rows: d.rows || [] })
        if (d.error) {
          q.ngCount++
        } else {
          q.okCount++
          q.read = true
          // 読めた合図：映像の枠を一瞬緑に、スマホは震える。カメラはそのまま次を待つ
          q.flash = true
          setTimeout(() => { q.flash = false }, 600)
          if (navigator.vibrate) {
            navigator.vibrate(100)
          }
        }
      } catch (e) {
        console.log(e)
        q.ngCount++
        q.results.unshift({ time, error: '読み取った内容を保存できませんでした。もう一度読んでください。', rows: [] })
      } finally {
        q.results = q.results.slice(0, 30)
        q.busy = false
        q.text = ''
        // 次をすぐ読めるよう入力欄に戻す（バーコードリーダー用。スマホはキーボードが出るので戻さない）
        if (this.$refs.qrInput && !this.isMobileOrTablet) {
          this.$refs.qrInput.focus()
        }
      }
    },

    /**
     * カメラを起動して QR を探し続ける（スマホは背面カメラ）
     */
    async crStartCamera() {
      const q = this.cr.qr
      q.cameraError = ''
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        q.cameraError = 'このブラウザではカメラを使えません（https でない、または古いブラウザ）。'
        return
      }
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' }, audio: false })
        this.qrStream = stream
        q.camera = true
        await this.$nextTick()
        const video = this.$refs.qrVideo
        video.srcObject = stream
        await video.play()
        this.qrCanvas = document.createElement('canvas')
        this.qrLast = ''
        this.crScanLoop()
      } catch (e) {
        console.log(e)
        q.camera = false
        q.cameraError = 'カメラを使えませんでした（カメラの許可がされていないか、カメラがありません）。'
      }
    },

    /**
     * 映像から QR を探す（0.25 秒ごと）。同じ QR は 3 秒あけないと 2 回目を読まない（ずっと映っていると何度も送るため）
     */
    crScanLoop() {
      const q = this.cr.qr
      if (!q.camera) {
        return
      }
      const video = this.$refs.qrVideo
      if (video && video.readyState >= 2 && !q.busy && video.videoWidth && video.videoHeight) {
        const canvas = this.qrCanvas
        canvas.width = video.videoWidth
        canvas.height = video.videoHeight
        const ctx = canvas.getContext('2d')
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height)
        const img = ctx.getImageData(0, 0, canvas.width, canvas.height)
        const code = jsQR(img.data, canvas.width, canvas.height, { inversionAttempts: 'dontInvert' })
        const text = code ? this.qrText(code) : ''
        if (text && text !== this.qrLast) {
          this.qrLast = text
          setTimeout(() => {
            if (this.qrLast === text) {
              this.qrLast = ''
            }
          }, 3000)
          this.crReadQr(text)
        }
      }
      this.qrTimer = setTimeout(this.crScanLoop, 250)
    },

    /**
     * jsQR の結果から、バックに渡す文字を取り出す
     * 契約書の QR は氏名が Shift-JIS で入っている（複合機が読める形。見本と同じ）ので、jsQR の data（UTF-8 として読んだ文字）は氏名の所で壊れる
     * 返送の判定に要るのは数字と「/」だけなので、バイト列の先頭から ASCII の範囲だけを取る（氏名は捨てる）。ASCII が 1 文字も無ければ data をそのまま
     * @param {Object} code jsQR の結果（data・binaryData）
     */
    qrText(code) {
      const bytes = code.binaryData || []
      let text = ''
      for (const b of bytes) {
        if (b >= 0x80) {
          break
        }
        text += String.fromCharCode(b)
      }
      return text.trim() || String(code.data || '').trim()
    },

    crStopCamera() {
      clearTimeout(this.qrTimer)
      if (this.qrStream) {
        this.qrStream.getTracks().forEach(t => t.stop())
        this.qrStream = null
      }
      const video = this.$refs.qrVideo
      if (video) {
        video.srcObject = null
      }
      this.cr.qr.camera = false
    },

    // ---- 提出先市町村のマスタ（モーダル） ----

    bugyoOpenCity() {
      this.bugyo.city.open = true
      this.bugyo.city.name = ''
      this.bugyoGetCities()
    },

    /**
     * 提出先市町村の一覧を取る（未保存の行は消える）
     * API: akbsOutput/bugyoCityList
     */
    async bugyoGetCities() {
      const city = this.bugyo.city
      city.loading = true
      try {
        const res = await backend.searchData('akbsOutput/bugyoCityList', { name: city.name })
        const rows = res.data && Array.isArray(res.data.data) ? res.data.data : []
        city.list = rows.map(r => ({ ...r, key: r.code, originalCode: r.code, dirty: false, saving: false }))
      } catch (e) {
        console.log(e)
        city.list = []
      } finally {
        city.loading = false
      }
    },

    /**
     * 市町村の行を先頭に足す（コード・名前は空。その行の「保存」で登録）
     */
    bugyoAddCity() {
      const city = this.bugyo.city
      city.seq++
      city.list.unshift({ key: `new${city.seq}`, code: '', name: '', originalCode: '', dirty: true, saving: false })
    },

    /**
     * 市町村の 1 行を保存する（追加・変更。コードを変えたときは元の行を消して入れ直す）
     * API: akbsOutput/bugyoCitySave
     */
    async bugyoSaveCity(c) {
      const code = String(c.code || '').trim()
      if (!/^\d{6}$/.test(code)) {
        await dialogs.showErrorDialog('市町村コード', '市町村コードは数字 6 桁で入れてください。')
        return
      }
      if (!String(c.name || '').trim()) {
        await dialogs.showErrorDialog('市町村名', '市町村名を入れてください。')
        return
      }
      c.saving = true
      try {
        const res = await backend.postData('akbsOutput/bugyoCitySave', { code, name: c.name, originalCode: c.originalCode, updateUser: this.$store.getters.user.id })
        const row = res.data && res.data.data
        if (!row || row.error) {
          await dialogs.showErrorDialog('提出先市町村', (row && row.error) || '保存できませんでした。もう一度お試しください。')
          return
        }
        c.code = row.code
        c.name = row.name
        c.originalCode = row.code
        c.key = row.code
        c.dirty = false
      } catch (e) {
        console.log(e)
        await dialogs.showErrorDialog('提出先市町村', '保存できませんでした。もう一度お試しください。')
      } finally {
        c.saving = false
      }
    },

    /**
     * 市町村の行を消す（保存済みなら確認してすぐ消す。未保存の行は画面から外すだけ）
     * API: akbsOutput/bugyoCityDelete
     */
    async bugyoDeleteCity(c, index) {
      if (c.originalCode) {
        const answer = await dialogs.showConfirmDialog('提出先市町村の削除', `${c.originalCode} ${c.name} を一覧から消します。よろしいですか。\nこの市町村に住む清掃員の CSV は「住民税・提出先」が空になります。`)
        if (answer !== 'YES') {
          return
        }
        try {
          await backend.postData('akbsOutput/bugyoCityDelete', { code: c.originalCode })
        } catch (e) {
          console.log(e)
          await dialogs.showErrorDialog('提出先市町村', '消せませんでした。もう一度お試しください。')
          return
        }
      }
      this.bugyo.city.list.splice(index, 1)
    },

    /**
     * 選んだ人の CSV を出す。出せたらその人は「出力済み」になるので、チェックを外して一覧を取り直す
     * CSV の並びは一覧と同じ（入社日の新しい順 → カナ → 清掃員コード。ユーザー指示）
     */
    async bugyoDownload() {
      const people = Object.values(this.bugyo.checked).sort((a, b) => {
        const da = String(a.dateHire || '')
        const db = String(b.dateHire || '')
        if (da !== db) {
          // 入社日が無い人は最後
          return !da ? 1 : !db ? -1 : (da > db ? -1 : 1)
        }
        const ka = String(a.nameKanaStaff || '')
        const kb = String(b.nameKanaStaff || '')
        if (ka !== kb) {
          return ka < kb ? -1 : 1
        }
        return Number(a.staffDailyCleaning) - Number(b.staffDailyCleaning)
      })
      if (!people.length) {
        return
      }
      const done = people.filter(p => p.exportDate)
      if (done.length) {
        const answer = await dialogs.showConfirmDialog('出力済みの人がいます', `次の人は前に CSV を出しています。もう一度出すと出力日が上書きされます。\n${done.map(p => `・${p.nameStaff}（${p.exportDate}）`).join('\n')}\n\n出力しますか。`)
        if (answer !== 'YES') {
          return
        }
      }
      if (await this.downloadBugyo(people)) {
        this.bugyo.checked = {}
        await this.bugyoGetList()
      }
    },

    /**
     * 給与奉行インポート（清掃員情報）の CSV をダウンロードする（Shift-JIS。全員で 1 本。1 行目は給与奉行の項目コード）。バックが出した人を控える
     * @param {Array} people 選んだ人（一覧と同じ並び。バックはこの順のまま出す）
     * @returns true：ダウンロードした、false：作れなかった
     * API: akbsOutput/bugyoCsv
     */
    async downloadBugyo(people) {
      this.progress = { active: true, percent: null, text: '給与奉行インポート（清掃員情報）の CSV を作っています' }
      try {
        const res = await backend.postBlob2('akbsOutput/bugyoCsv', { staffs: people.map(c => ({ staffCode: c.staffDailyCleaning })), updateUser: this.$store.getters.user.id })
        if (!res || !res.data) {
          throw new Error('CSV が返ってこない')
        }
        const blob = new Blob([res.data], { type: 'text/csv' })
        const a = document.createElement('a')
        a.href = (window.URL || window.webkitURL).createObjectURL(blob)
        a.download = `給与奉行_清掃員情報_${daysAgo(0).replace(/-/g, '')}.csv`
        a.click()
        a.remove()
        return true
      } catch (e) {
        console.log(e)
        await dialogs.showErrorDialog('給与奉行インポート（清掃員情報）', 'CSV を作れませんでした。もう一度お試しください。')
        return false
      } finally {
        this.progress = { active: false, percent: 0, text: '' }
      }
    },

    /**
     * 区切りの表示「（3/14）」（1 本だけなら空）
     */
    partLabel(part) {
      return part ? `（${part.no}/${part.total}）` : ''
    },

    /**
     * 区切りのファイル名の末尾「_3」（1 本だけなら空）
     */
    partSuffix(part) {
      return part ? `_${part.no}` : ''
    },

    /**
     * 退職届を出す人のうち、AKBS にまだ退職日が無い人（退職の承認前）がいれば、確認のモーダルで退職日を入れてもらう
     * 退職日の初期値は URL（遷移元の画面）から来た retireDates。全員入っていれば「出力する」が押せる
     * @returns true：出してよい（全員に退職日がある）、false：戻る
     */
    confirmRetireDates() {
      const rows = this.checkedInOrder()
        .filter(c => !c.dateResignation)
        .map(c => ({ staffCode: String(c.staffDailyCleaning), name: c.nameStaff, date: this.retireDates[String(c.staffDailyCleaning)] || '' }))
      if (!rows.length) {
        return Promise.resolve(true)
      }
      return new Promise(resolve => {
        this.retire = { open: true, rows, resolve }
      })
    },

    /**
     * 退職日の確認のモーダルを閉じる
     * @param {Boolean} ok 「出力する」なら入れた退職日を控えて true を返す。「戻る」は false
     */
    closeRetire(ok) {
      const r = this.retire
      if (ok) {
        if (!this.retireDatesOk) {
          return
        }
        for (const row of r.rows) {
          this.$set(this.retireDates, row.staffCode, row.date)
        }
      }
      this.retire = { open: false, rows: [], resolve: null }
      if (r.resolve) {
        r.resolve(!!ok)
      }
    },

    /**
     * 雇用契約書の PDF をダウンロードする（勤務ごとに 2 ページ × 2 部。両面印刷用に別の PDF。逆順は効かせない）
     * @param {Array} people この区切りの人（checked の控え。カナ順）
     * @param {*} part 区切り { no, total }（1 本だけなら null）
     * API: akbsOutput/contractStart・reportProgress・reportFile
     */
    async downloadContract(people, part) {
      const staffs = people.map(c => {
        const sel = this.empSel[c.staffDailyCleaning] || {}
        return {
          staffCode: c.staffDailyCleaning,
          // 代勤専門（isOp で startDate がある雇用指示）も契約書を出す（ユーザー指示 2026-10-13）。実績用だけの「代勤」の枚（startDate なし）は無い
          employments: this.empVisible(c).filter(e => sel[this.empKey(e)] && (!e.isOp || e.startDate)).map(e => ({ cdSite: e.cdSite, cdContract: e.cdContract }))
        }
      }).filter(s => s.employments.length)
      if (!staffs.length) {
        return undefined
      }
      const d = this.docInput
      return this.runPdfJob({
        name: `雇用契約書${this.partLabel(part)}`,
        startApi: 'akbsOutput/contractStart',
        // updateUser は出力の記録（契約書返送のタブ）の「出力した人」
        body: { from: d.contractFrom, to: d.contractTo, to70: d.contractTo70, staffs, updateUser: this.$store.getters.user.id },
        unit: '通',
        emptyMessage: '選んだ勤務に雇用指示が無いか、契約期間が作れず（退職日が開始日より前）、1通も作れませんでした。',
        fileName: `雇用契約書_${d.contractFrom.replace(/-/g, '')}-${d.contractTo.replace(/-/g, '')}${this.partSuffix(part)}.pdf`
      })
    },

    /**
     * PDF（か Excel）を作ってダウンロードする（作る → 進み具合を聞く（3 秒ごと）→ できたら取る、の 3 段。進み具合は白いマスクに %）
     * @param {*} job { name: 書類の名前, startApi: 作り始める API, body: その body, unit: 「枚」「通」, emptyMessage: 1 つも作れなかったときの文, fileName,
     *                  fileWord: 文に出すファイルの呼び方（無ければ「PDF」）, contentType: 受け取るファイルの種類（無ければ PDF） }
     * @returns true：ダウンロードした、false：1 つも作れなかった・エラー、'cancelled'：「中止」で止めた
     * API: startApi・akbsOutput/reportProgress・akbsOutput/reportFile
     */
    async runPdfJob(job) {
      const name = job.name
      const fileWord = job.fileWord || 'PDF'
      this.progress = { active: true, percent: 0, text: `${name}を作っています`, jobId: null, cancelling: false }
      try {
        const started = await backend.postData(job.startApi, job.body)
        const jobId = started.data && started.data.data && started.data.data.jobId
        if (!jobId) {
          throw new Error('jobId が無い')
        }
        // ジョブが分かったら「中止」を押せる
        this.progress.jobId = jobId
        let status = 'running'
        let skipped = []
        while (status === 'running') {
          await new Promise(resolve => setTimeout(resolve, 3000))
          const res = await backend.searchData('akbsOutput/reportProgress', { jobId })
          const p = res.data && res.data.data
          if (!p) {
            throw new Error('進み具合が取れない')
          }
          status = p.status
          skipped = Array.isArray(p.skipped) ? p.skipped : []
          this.progress.percent = p.percent
          if (this.progress.cancelling) {
            // 中止を押したあとは「中止しています」のまま（バックの文で上書きしない）
          } else if (p.text) {
            // バックが今していること（「600 枚を PDF にしています」など）を伝えてきたらそのまま
            this.progress.text = `${name}：${p.text}`
          } else if (p.total > 1) {
            // 最後の 1 は PDF にまとめる分（枚数が多いと長い）。それと分かる文にする
            this.progress.text = p.done >= p.total - 1
              ? `${name}：${p.total - 1} ${job.unit}を 1 つの ${fileWord} にまとめています（多いと時間がかかります）`
              : `${name}を作っています（${p.done} / ${p.total - 1} ${job.unit}）`
          }
        }
        // 受け取りに入ったら中止できない
        this.progress.jobId = null
        if (status === 'cancelled') {
          return 'cancelled'
        }
        if (status === 'empty') {
          await dialogs.showErrorDialog(name, job.emptyMessage)
          return false
        }
        if (status !== 'done') {
          await dialogs.showErrorDialog(name, `${fileWord} を作れませんでした。もう一度お試しください。`)
          return false
        }
        this.progress.text = `${fileWord} を受け取っています`
        const file = await backend.getBlob('akbsOutput/reportFile', { jobId })
        const blob = new Blob([file.data], { type: job.contentType || 'application/pdf' })
        const a = document.createElement('a')
        a.href = (window.URL || window.webkitURL).createObjectURL(blob)
        a.download = job.fileName
        a.click()
        a.remove()
        // 作れなかった分（雇用指示なし・契約期間が作れない）があれば知らせる
        if (skipped.length) {
          await dialogs.showInfoDialog(`${name}：作れなかった分`, `次の分は作れませんでした。\n\n${skipped.map(s => `・${s}`).join('\n')}`)
        }
        return true
      } catch (e) {
        console.log(e)
        await dialogs.showErrorDialog(name, `${fileWord} を作れませんでした。もう一度お試しください。`)
        return false
      } finally {
        this.progress = { active: false, percent: 0, text: '', jobId: null, cancelling: false }
      }
    },

    /**
     * 作っている PDF を中止する（白いマスクの「中止」）。バックは次の区切り（人・分けた PDF の 1 個）で止まる。できた分も受け取らない
     * 止まったら runPdfJob が 'cancelled' を返し、download がその区切りの人のチェックを残したまま終わる
     * API: akbsOutput/reportCancel
     */
    async cancelPdfJob() {
      const jobId = this.progress.jobId
      if (!jobId || this.progress.cancelling) {
        return
      }
      this.progress.cancelling = true
      this.progress.text = '中止しています（描き始めた分が終わるまで少し待ちます）'
      try {
        await backend.postData('akbsOutput/reportCancel', { jobId })
      } catch (e) {
        console.log(e)
      }
    },

    // ------------------------------------------------------------
    // 書類設定（固定の書類）
    // ------------------------------------------------------------

    /**
     * 書類設定を読む（出力画面の札と書類設定の画面の両方の元）。表が空なら既定（コードの並び）
     * API: akbsOutput/getDocs
     */
    async getDocs() {
      this.setting.loading = true
      try {
        const res = await backend.searchData('akbsOutput/getDocs', {})
        // 給与奉行インポートの行は「給与奉行」タブに移したので、表に残っていても出さない（SQL で消す前でも動くように）
        const rows = (res.data && Array.isArray(res.data.data) ? res.data.data : []).filter(r => r.docKey !== 'bugyo')
        this.docConf = rows.length ? rows.map(toDocConf) : defaultDocConf()
        // 業務報告書に自動で付ける書類の行がまだ表に無ければ既定の行を最後に足す（書類設定で PDF を登録して保存すると表に入る）
        for (const a of AUTO_SHEETS) {
          if (!this.docConf.some(c => c.autoSheet === a.key)) {
            this.docConf.push(defaultAutoSheetConf(a.key))
          }
        }
      } catch (e) {
        console.log(e)
        this.docConf = defaultDocConf()
      } finally {
        this.setting.loading = false
      }
      this.setting.docs = this.docConf.map(toSettingDoc)
      this.cleanDocSelection()
    },

    /**
     * 出力画面で ON の書類のうち、無くなった・使わなくなったものの選択を外す（保存で key が u＋id に変わることもある）
     */
    cleanDocSelection() {
      for (const k of Object.keys(this.docs)) {
        if (!this.docConfMap[k] || !this.docConfMap[k].enabled) {
          this.$delete(this.docs, k)
        }
      }
    },

    /**
     * 書類の行を足す（名前は空。その行の「保存」で登録。並びは最後）
     */
    addSettingDoc() {
      this.setting.seq++
      this.setting.docs.push({ key: `new${this.setting.seq}`, id: null, docKey: null, name: '', system: false, autoSheet: '', sets: [], enabled: true, copiesInput: false, files: [], dirty: true, saving: false })
    },

    /**
     * 1 行を保存する（名前・一式・使う・枚数の入力欄・ファイル。ファイルはこの行の分だけ base64 で送る）
     * 保存した行だけ置き換える（ほかの行の未保存の変更は残す）。出力画面の札にもその場で反映
     * API: akbsOutput/saveDoc
     */
    async saveSettingDoc(d, index) {
      if (!String(d.name || '').trim()) {
        await dialogs.showErrorDialog('書類名', '書類名を入れてください。')
        return
      }
      d.saving = true
      try {
        // ファイルは画面の並びの順。新しく選んだものだけ中身（base64）を付ける。残りは番号だけ（バックが今の中身を使う）
        const files = []
        for (const f of d.files) {
          files.push(f.newFile ? { no: f.no, name: f.newFile.name, data: await readFileAsDataUrl(f.newFile) } : { no: f.no })
        }
        // 業務報告書に自動で付ける書類は一式・枚数・使うを持たない（PDF だけ）
        const doc = d.autoSheet
          ? { id: d.id || null, docKey: d.autoSheet, name: String(d.name).trim(), setKeys: [], enabled: true, copiesInput: false, files }
          : { id: d.id || null, docKey: d.docKey || null, name: String(d.name).trim(), setKeys: d.sets, enabled: !!d.enabled, copiesInput: !!d.copiesInput, files }
        const res = await backend.postData('akbsOutput/saveDoc', { doc, updateUser: this.$store.getters.user.id })
        const row = res.data && res.data.data
        if (!row) {
          throw new Error('保存した行が返ってこない')
        }
        const conf = toDocConf(row)
        // 書類設定の行と、出力画面の元（docConf）を置き換える（新しい行は最後に足す）
        //   既定の行（id なし）を保存したときは key（システムの書類・自動で付ける書類は doc_key）で見つける（id では見つからず二重になる）
        this.setting.docs.splice(index, 1, toSettingDoc(conf))
        const pos = this.docConf.findIndex(c => (c.id && c.id === conf.id) || (c.docKey && c.key === conf.key))
        if (pos >= 0) {
          this.docConf.splice(pos, 1, conf)
        } else {
          this.docConf.push(conf)
        }
        this.cleanDocSelection()
      } catch (e) {
        console.log(e)
        d.saving = false
        await dialogs.showErrorDialog('書類設定', `「${d.name}」を保存できませんでした。PDF 以外のファイルが混ざっていないか確かめて、もう一度お試しください。`)
      }
    },

    /**
     * 書類の行を消す（確認してすぐ保存。システムが作る書類は消せない）
     * API: akbsOutput/deleteDoc
     */
    async removeSettingDoc(index) {
      const d = this.setting.docs[index]
      const answer = await dialogs.showConfirmDialog('書類の削除', `「${d.name || '（名前なし）'}」を消します。よろしいですか。\n出力画面の札からも消えます。`)
      if (answer !== 'YES') {
        return
      }
      if (d.id) {
        try {
          await backend.postData('akbsOutput/deleteDoc', { id: d.id })
        } catch (e) {
          console.log(e)
          await dialogs.showErrorDialog('書類設定', '削除できませんでした。もう一度お試しください。')
          return
        }
        this.docConf = this.docConf.filter(c => c.id !== d.id)
        this.cleanDocSelection()
      }
      this.setting.docs.splice(index, 1)
    },

    /**
     * 並びを 1 つ上・下へ（出力画面の札の順）。すぐ保存する（登録済みの行の id を上から順に送る。未保存の行は保存のときに最後に付く）
     * API: akbsOutput/saveDocOrder
     */
    async moveSettingDoc(index, dir) {
      const to = index + dir
      if (to < 0 || to >= this.setting.docs.length) {
        return
      }
      const docs = this.setting.docs
      const d = docs[index]
      docs.splice(index, 1)
      docs.splice(to, 0, d)
      const ids = docs.filter(x => x.id).map(x => x.id)
      try {
        await backend.postData('akbsOutput/saveDocOrder', { ids, updateUser: this.$store.getters.user.id })
        // 出力画面の元も同じ並びに（まだ保存していない既定の行（id なし。業務報告書に付ける代勤賃金簿など）は落とさず最後に残す）
        const byId = {}
        for (const c of this.docConf) {
          byId[c.id] = c
        }
        this.docConf = [...ids.map(id => byId[id]).filter(c => c), ...this.docConf.filter(c => !c.id)]
      } catch (e) {
        console.log(e)
        await dialogs.showErrorDialog('書類設定', '並びを保存できませんでした。もう一度お試しください。')
        await this.getDocs()
      }
    },

    /**
     * 選んだファイルが置けるか（PDF・Word・Excel の種類で、PDF はセキュリティ（暗号化）が無いもの。違えば知らせて false）
     */
    async isAllowedFile(file) {
      const ext = extOf(file.name)
      if (!FILE_EXTS.includes(ext)) {
        await dialogs.showErrorDialog('ファイルの種類', `「${file.name}」は置けない種類です。PDF・Word・Excel だけ置けます（出力に使うのは PDF だけ）。`)
        return false
      }
      // 暗号化された PDF は出力で白紙になるので止める（国税庁の様式などに多い）
      if (ext === 'pdf' && (await isEncryptedPdf(file))) {
        await dialogs.showErrorDialog('PDF のセキュリティ', `「${file.name}」にはセキュリティ（暗号化）が設定されていて、出力で使うと白紙になります。\nChrome か Edge でこの PDF を開き、「印刷」→「PDF に保存」で保存し直したファイルを置いてください（文字の情報は残ります）。`)
        return false
      }
      return true
    },

    /**
     * 1 つのファイルを差し替える（保存で S3 の同じ番号に上書き）
     */
    async pickSettingFile(d, f, event) {
      const file = event.target.files && event.target.files[0]
      event.target.value = ''
      if (!file || !(await this.isAllowedFile(file))) {
        return
      }
      f.newFile = file
      f.isPdf = extOf(file.name) === 'pdf'
      d.dirty = true
    },

    /**
     * ファイルを足す（複数選べる。保存で S3 へ。並びは最後）
     */
    async addSettingFiles(d, event) {
      const files = Array.from(event.target.files || [])
      event.target.value = ''
      for (const file of files) {
        if (!(await this.isAllowedFile(file))) {
          continue
        }
        d.files.push({ no: null, fileName: file.name, isPdf: extOf(file.name) === 'pdf', fileSize: '', fileDate: '', fileUser: '', newFile: file })
        d.dirty = true
      }
    },

    /**
     * ファイルを 1 つ消す（保存で確定。登録済みのものは確認してから）
     */
    async removeSettingFile(d, index) {
      const f = d.files[index]
      if (f.no && !f.newFile) {
        const answer = await dialogs.showConfirmDialog('ファイルの削除', `「${f.fileName}」をこの書類から外します。よろしいですか（保存で確定）。`)
        if (answer !== 'YES') {
          return
        }
      }
      d.files.splice(index, 1)
      d.dirty = true
    },

    /**
     * ファイルの並びを 1 つ上・下へ（出力でつなげる順）
     */
    moveSettingFile(d, index, dir) {
      const to = index + dir
      if (to < 0 || to >= d.files.length) {
        return
      }
      const f = d.files[index]
      d.files.splice(index, 1)
      d.files.splice(to, 0, f)
      d.dirty = true
    },

    /**
     * 登録済みの Word・Excel（原本）をダウンロードする（ブラウザでは開けないので）
     * API: akbsOutput/docFile
     */
    async downloadSettingFile(d, f) {
      try {
        const file = await backend.getBlob('akbsOutput/docFile', { id: d.id, no: f.no })
        const a = document.createElement('a')
        a.href = (window.URL || window.webkitURL).createObjectURL(new Blob([file.data]))
        a.download = f.fileName || d.name
        a.click()
        a.remove()
      } catch (e) {
        console.log(e)
        await dialogs.showErrorDialog('ダウンロード', 'ファイルを取れませんでした。もう一度お試しください。')
      }
    },

    /**
     * 登録済みの PDF を別のタブで開いて確認する（S3 から取ってブラウザの PDF 表示で開く。ユーザー指示「ダウンロードではなく確認」）
     * タブは押した直後に開いておく（取り終わってから開くとポップアップとして止められる）
     * API: akbsOutput/docFile
     */
    async openSettingFile(d, f) {
      const tab = window.open('', '_blank')
      if (tab) {
        tab.document.title = f.fileName
        tab.document.body.innerHTML = '<p style="font-family:sans-serif;padding:16px">PDF を読み込んでいます…</p>'
      }
      try {
        const file = await backend.getBlob('akbsOutput/docFile', { id: d.id, no: f.no })
        const url = (window.URL || window.webkitURL).createObjectURL(new Blob([file.data], { type: 'application/pdf' }))
        if (tab) {
          tab.location.href = url
        } else {
          window.open(url, '_blank')
        }
      } catch (e) {
        console.log(e)
        if (tab) {
          tab.close()
        }
        await dialogs.showErrorDialog('確認', 'ファイルを取れませんでした。もう一度お試しください。')
      }
    },

    // ------------------------------------------------------------
    // 備考
    // ------------------------------------------------------------

    /**
     * 「出力に関する備考」を保存する（欄を離れたとき。変わっていなければ何もしない）
     * API: akbsOutput/saveMemo
     */
    async saveMemo(row) {
      const memo = String(row.memo || '').trim()
      if (memo === String(row.memoBefore || '').trim()) {
        row.memo = memo
        return
      }
      row.memoSaving = true
      try {
        await backend.postData('akbsOutput/saveMemo', { staffCode: row.staffDailyCleaning, memo, updateUser: this.$store.getters.user.id })
        row.memo = memo
        row.memoBefore = memo
      } catch (e) {
        console.log(e)
        await dialogs.showErrorDialog('保存エラー', '備考を保存できませんでした。もう一度お試しください。')
        row.memo = row.memoBefore
      } finally {
        row.memoSaving = false
      }
    },

    /**
     * 顔写真を選んだ：JPEG・PNG だけ受け付け、その場で登録・差し替え（バックで長辺 480px に縮めて S3 へ）
     * API: akbsOutput/savePhoto
     */
    async pickPhoto(row, event) {
      const file = event.target.files && event.target.files[0]
      event.target.value = ''
      if (!file) {
        return
      }
      if (!['jpg', 'jpeg', 'png'].includes(extOf(file.name))) {
        await dialogs.showErrorDialog('顔写真', `「${file.name}」は置けない種類です。JPEG か PNG の写真を選んでください。`)
        return
      }
      row.photoSaving = true
      try {
        const data = await readFileAsDataUrl(file)
        await backend.postData('akbsOutput/savePhoto', { staffCode: row.staffDailyCleaning, name: file.name, data, updateUser: this.$store.getters.user.id })
        row.hasPhoto = true
      } catch (e) {
        console.log(e)
        await dialogs.showErrorDialog('顔写真', '顔写真を保存できませんでした。画像のファイルか確かめて、もう一度お試しください。')
      } finally {
        row.photoSaving = false
      }
    },

    /**
     * 顔写真を消す（確認あり。表の行だけ消え、名札は写真なしになる）
     * API: akbsOutput/deletePhoto
     */
    async deletePhoto(row) {
      const answer = await dialogs.showConfirmDialog('顔写真の削除', `${row.nameStaff} の顔写真を消します。よろしいですか。\n野村・三菱の名札は写真の枠が空になります。`)
      if (answer !== 'YES') {
        return
      }
      row.photoSaving = true
      try {
        await backend.postData('akbsOutput/deletePhoto', { staffCode: row.staffDailyCleaning })
        row.hasPhoto = false
      } catch (e) {
        console.log(e)
        await dialogs.showErrorDialog('顔写真', '顔写真を消せませんでした。もう一度お試しください。')
      } finally {
        row.photoSaving = false
      }
    },

    // ------------------------------------------------------------
    // 表示
    // ------------------------------------------------------------

    /**
     * 登録済み一覧でその清掃員を開くリンク（別タブ）
     */
    staffLink(row) {
      return this.$router.resolve({ name: 'AkbsRegisteredList', query: { staffCode: row.staffDailyCleaning } }).href
    },

    isRetired(row) {
      return !!row.dateResignation && row.dateResignation < daysAgo(0)
    },

    age(birthday) {
      const parts = String(birthday || '').split('-')
      if (parts.length !== 3) {
        return ''
      }
      const y = Number(parts[0])
      const m = Number(parts[1])
      const d = Number(parts[2])
      const now = new Date()
      let age = now.getFullYear() - y
      if (now.getMonth() + 1 < m || (now.getMonth() + 1 === m && now.getDate() < d)) {
        age--
      }
      return isNaN(age) ? '' : age
    },

    slash(day) {
      return day ? String(day).replace(/-/g, '/') : ''
    },

    getSessionStorage() {
      let item = null
      try {
        item = JSON.parse(sessionStorage.getItem(SESSION_STORAGE_NAME))
      } catch (e) {
        return
      }
      if (item && item.filter) {
        this.filter = { ...createFilter(), ...item.filter }
      }
    },

    setSessionStorage() {
      sessionStorage.setItem(SESSION_STORAGE_NAME, JSON.stringify({ filter: this.filter }))
    }
  }
}
</script>

<style scoped>
/* ヘッダーの切り替え（登録済み一覧の軸のタブと同じ） */
.axis-tabs {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  gap: 6px;
  padding: 5px;
  border-radius: 6px;
  background: #fff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
}

.axis-tab {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 120px;
  height: 32px;
  padding: 0 16px;
  border-radius: 4px;
  background: #9ca3af;
  color: #fff;
  font-size: 0.875rem;
  font-weight: bold;
  white-space: nowrap;
  cursor: pointer;
}

.axis-tab:hover {
  background: #6b7280;
}

.axis-tab.active {
  background: #1d4ed8;
}

.axis-tab.active:hover {
  background: #1e40af;
}

/* 絞り込みの欄（ほかの一覧より高め。目に入りやすく押しやすいように。絞っている欄は黄色） */
.filter-input {
  height: 40px;
  box-sizing: border-box;
}

select.filter-input {
  padding-top: 0;
  padding-bottom: 0;
  line-height: 38px;
}

.filter-input.filled {
  font-weight: bold;
  background-color: #efdf8a;
}

/* エリアのボタン（絞り込みの欄と同じ高さ。選んでいれば黄色） */
.area-btn {
  display: inline-flex;
  align-items: center;
  padding: 0 8px 0 10px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  background: #fff;
  font-size: 0.875rem;
  color: #374151;
  white-space: nowrap;
}

.area-btn.filled {
  font-weight: bold;
  background-color: #efdf8a;
}

/* エリアの選択の一覧（ボタンの下に重ねる。ヘッダー・本文より前に出す） */
.area-panel {
  position: fixed;
  z-index: 60;
  width: 300px;
  border: 1px solid #c7d2fe;
  border-radius: 6px;
  background: #fff;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
}

.area-panel-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 10px;
  border-bottom: 1px solid #e5e7eb;
  background: #eef2ff;
  color: #312e81;
  font-size: 0.8rem;
  font-weight: bold;
}

.area-panel-head .link-btn:disabled {
  color: #9ca3af;
  text-decoration: none;
  cursor: default;
}

.area-panel-list {
  max-height: 320px;
  overflow: auto;
  padding: 4px 0;
}

.area-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 12px;
  font-size: 0.85rem;
  color: #1f2937;
  cursor: pointer;
}

.area-item:hover {
  background: #f8faff;
}

.area-item.is-on {
  background: #e0e7ff;
  font-weight: bold;
}

.area-panel-foot {
  display: flex;
  justify-content: flex-end;
  padding: 6px 10px;
  border-top: 1px solid #e5e7eb;
}

/* 「●以降に●歳以上になる」の欄（日付・歳・文字を1つの枠に） */
.age-filter {
  display: flex;
  align-items: center;
  gap: 4px;
  height: 40px;
  padding: 0 8px;
  border-radius: 4px;
  background: #fff;
  border: 1px solid #d1d5db;
  font-size: 0.8rem;
  color: #374151;
  white-space: nowrap;
}

.age-filter.filled {
  background-color: #efdf8a;
  font-weight: bold;
}

.age-filter .age-date {
  width: 150px;
}

.age-filter .age-date >>> input {
  height: 32px;
  padding: 0 6px;
  font-size: 0.8rem;
}

.age-filter .age-date >>> .mt-1 {
  margin-top: 0;
}

.age-filter .age-input {
  width: 56px;
  height: 32px;
  padding: 0 6px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  text-align: right;
  font-size: 0.8rem;
}

/* 出力する書類の枠（上）。選んだ人数と出力実行のボタンもここ */
.doc-box {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 8px 14px;
  border-radius: 6px;
  border: 1px solid #c7d2fe;
  background: #eef2ff;
}

.doc-box-head {
  display: flex;
  align-items: center;
  gap: 12px;
}

.doc-box-title {
  font-weight: bold;
  color: #312e81;
}

.doc-box-note {
  font-size: 0.8rem;
  color: #6b7280;
}

/* 印刷用に逆順（雇用契約書以外の書類全部に効く。ON のときはピンク） */
.reverse-input {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 28px;
  padding: 0 10px;
  border-radius: 4px;
  border: 1px solid #d1d5db;
  background: #fff;
  font-size: 0.8rem;
  font-weight: bold;
  color: #374151;
  white-space: nowrap;
  cursor: pointer;
}

.reverse-input.is-on {
  background: #fce7f3;
  border-color: #f472b6;
  color: #9d174d;
}

/* チェックの色もピンクに（@tailwindcss/forms のチェックは color（currentColor）で塗られるので、accent-color ではなく color で変える） */
.reverse-input input[type='checkbox'] {
  color: #db2777;
  accent-color: #db2777;
}

.reverse-input input[type='checkbox']:focus {
  --tw-ring-color: #f9a8d4;
}

/* 「（雇用契約書・宛名シール以外適用）」は小さく細く */
.reverse-input .reverse-note {
  margin-left: 2px;
  font-size: 0.7rem;
  font-weight: normal;
}

/* 1行目：一式のボタンを横に並べ、間を空けて宛先 */
.doc-sets {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}

.addr-group {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-left: 32px;
  padding-left: 14px;
  border-left: 1px solid #c7d2fe;
}

.addr-group-name {
  font-size: 0.75rem;
  color: #6b7280;
  white-space: nowrap;
}

/* 宛先の札（書類ではないので橙系。選んだ書類の緑・「出力実行」の青と分ける） */
.addr-chip {
  height: 26px;
  padding: 0 10px;
  border-radius: 999px;
  border: 1px solid #fcd34d;
  background: #fffbeb;
  color: #92400e;
  font-size: 0.8rem;
  white-space: nowrap;
}

.addr-chip:hover {
  background: #fef3c7;
}

.addr-chip.is-on {
  background: #b45309;
  border-color: #b45309;
  color: #fff;
  font-weight: bold;
}

.set-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-width: 120px;
  height: 28px;
  padding: 0 8px;
  border-radius: 4px;
  border: 1px solid #86efac;
  background: #fff;
  color: #166534;
  font-size: 0.8rem;
  font-weight: bold;
  white-space: nowrap;
}

.set-btn:hover {
  background: #dcfce7;
}

/* 全部 ON は緑（選んだ書類の青緑と区別がつき、「出力実行」の青とも被らない） */
.set-btn.all {
  background: #15803d;
  border-color: #15803d;
  color: #fff;
}

.set-check {
  display: inline-block;
  width: 14px;
  text-align: center;
}

/* 2行目以降：書類の札を行ごとに */
/* 書類の札（左）と雇用契約書の説明（右の空き） */
.doc-chips-area {
  display: flex;
  align-items: center;
  gap: 32px;
}

.doc-chips {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding-top: 2px;
}

.contract-note {
  margin-top: -2px;
  text-align: right;
  color: #dc2626;
  font-size: 0.8rem;
  font-weight: bold;
  line-height: 1.5;
}

/* 「検索」は取得中などの途中は押せない（グレー）。更新の矢印も同じ間は薄くして押せない見た目に */
.search-btn:disabled {
  background-color: #9ca3af;
}

.refresh-off {
  opacity: 0.4;
  cursor: not-allowed;
}

/* 出力実行は検索（青）と違う色（緑）。押せないときはグレー */
.doc-box-head .run-btn {
  min-width: 9rem;
  white-space: nowrap;
  background-color: #15803d;
}

.doc-box-head .run-btn:hover {
  background-color: #166534;
}

.doc-box-head .run-btn:disabled {
  background-color: #9ca3af;
}

.doc-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
}

.doc-chip {
  height: 26px;
  padding: 0 10px;
  border-radius: 999px;
  border: 1px solid #d1d5db;
  background: #fff;
  color: #374151;
  font-size: 0.8rem;
  white-space: nowrap;
}

.doc-chip:hover {
  border-color: #5eead4;
  background: #f0fdfa;
}

/* 選んだ書類は青緑（「出力実行」の青と被らないように） */
.doc-chip.is-on {
  background: #0f766e;
  border-color: #0f766e;
  color: #fff;
  font-weight: bold;
}

/* 書類に要る入力（ON のときだけ出す。足りなければ薄い赤） */
.doc-inputs {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  padding-top: 4px;
  border-top: 1px dashed #c7d2fe;
}

/* 書類に要る入力の枠：1 段目に題（期間・枚数の名前）、2 段目に入力欄（ユーザー指示）。雇用契約書はその下に ※ の 3 段目 */
.doc-input {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  padding: 4px 10px;
  border-radius: 4px;
  background: #fff;
  border: 1px solid #c7d2fe;
  font-size: 0.8rem;
  color: #374151;
}

.doc-input.missing {
  background: #fee2e2;
  border-color: #fca5a5;
}

.doc-input-name {
  font-weight: bold;
  color: #312e81;
}

/* 題の行（題と、その横の小さな補足「物件・契約コードごと」） */
.doc-input-title {
  display: flex;
  align-items: baseline;
  gap: 8px;
}

.doc-input-small {
  font-size: 0.7rem;
  color: #6b7280;
}

/* 期間の横の案内は小さく（欄そのものを目立たせる） */
.doc-input-hint {
  font-size: 0.7rem;
  color: #6b7280;
}

.doc-input.missing .doc-input-hint {
  color: #b91c1c;
}

/* 業務報告書の期間は大きく目立たせる（見落とすと別の期間で出てしまう） */
.doc-input.doc-input-period {
  padding: 8px 14px;
  font-size: 1rem;
  border-width: 2px;
}

.doc-input.doc-input-period .doc-input-name {
  font-size: 1.05rem;
}

.doc-input-fields {
  display: flex;
  align-items: center;
  gap: 6px;
}

.doc-input-note {
  font-size: 0.75rem;
  color: #374151;
}

.doc-input.missing .doc-input-note {
  color: #b91c1c;
}

/* 雇用契約書の契約期間の日付欄（WmsDateField） */
.doc-input .date-field {
  width: 120px;
}

.doc-input .date-field >>> input {
  height: 36px;
  font-size: 1rem;
  font-weight: bold;
  text-align: center;
  padding-left: 4px;
  padding-right: 4px;
}

.doc-input .date-field >>> .mt-1 {
  margin-top: 0;
}

.doc-input-sub {
  margin-left: 12px;
  font-size: 0.8rem;
  color: #374151;
}

/* 業務報告書の期間の横の月数 */
.doc-input-months {
  margin-left: 8px;
  font-weight: bold;
  color: #312e81;
}

.month-input {
  height: 36px;
  padding: 0 8px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 1.05rem;
  font-weight: bold;
  background: #fff;
}

/* 空の年月・枚数の欄は赤くして気付いてもらう */
.month-input.empty,
.sheets-input.empty {
  background: #fca5a5;
  border-color: #dc2626;
}

/* 宛名シールの印字開始位置：枠には「N 面目から」と「選び直す」だけ。シートの図（2 列 × 6 段。縦のまま）は選ぶときだけ枠に重ねて出す */
.label-input {
  position: relative;
}

.label-start {
  font-size: 1rem;
  font-weight: bold;
  color: #312e81;
  line-height: 36px;
}

/* 印字開始位置を選ぶまでは赤で「未選択」（ほかの空の欄と同じ濃い赤） */
.label-start.label-start-none {
  color: #b91c1c;
}

/* 業務報告書の期間の下：代勤専門の人に代勤賃金簿を付けるかの切り替え（seal-toggle。ON は藍色） */
.op-sheets-row {
  margin-top: 4px;
}

.op-sheets-row .op-sheets {
  background: #fff;
}

/* 書類設定に代勤賃金簿の PDF が無いときの知らせ（空の欄と同じ濃い赤） */
.op-sheets-row .op-sheets-none {
  color: #b91c1c;
  font-weight: bold;
}

/* 業務報告書（実績）の形式（PDF／Excel）。同じ種類の切り替えボタンは幅をそろえる。選んでいる方は藍色 */
.fmt-caption {
  margin-right: 6px;
  font-weight: bold;
  color: #374151;
}

.fmt-btn {
  width: 72px;
  margin-right: 4px;
  padding: 2px 0;
  border: 1px solid #d1d5db;
  border-radius: 999px;
  background: #fff;
  color: #6b7280;
  font-size: 0.8rem;
  text-align: center;
  cursor: pointer;
}

.fmt-btn.is-on {
  background: #4338ca;
  border-color: #4338ca;
  color: #fff;
  font-weight: bold;
}

/* Excel のときの説明（1シート＝1人のひと月分）。目立つように赤（代勤賃金簿の知らせと同じ色） */
.fmt-note {
  margin-left: 6px;
  color: #b91c1c;
  font-weight: bold;
}

.label-pop {
  position: absolute;
  left: 8px;
  top: 100%;
  z-index: 60;
  margin-top: 4px;
  padding: 8px 10px 10px;
  border: 1px solid #a5b4fc;
  border-radius: 6px;
  background: #fff;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
}

.label-pop-title {
  margin-bottom: 6px;
  font-size: 0.75rem;
  color: #4b5563;
}

.label-grid {
  display: grid;
  grid-template-columns: repeat(2, 44px);
  gap: 3px;
  padding: 4px;
  border: 1px solid #9ca3af;
  border-radius: 4px;
  background: #fff;
}

.label-cell {
  height: 24px;
  border: 1px solid #c7d2fe;
  border-radius: 3px;
  background: #f5f7ff;
  color: #4338ca;
  font-size: 0.8rem;
  font-weight: bold;
}

.label-cell:hover {
  background: #e0e7ff;
}

.label-cell.is-on {
  background: #4338ca;
  border-color: #4338ca;
  color: #fff;
}

/* 枚数の欄は期間の年月の欄と同じ大きさ（枠は期間と同じ .doc-input-period） */
.sheets-input {
  width: 72px;
  height: 36px;
  padding: 0 8px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  text-align: right;
  font-size: 1rem;
  font-weight: bold;
}

.checked-count {
  padding: 4px 12px;
  border-radius: 999px;
  background: #e5e7eb;
  color: #6b7280;
  font-weight: bold;
  font-size: 0.875rem;
}

.checked-count.is-on {
  background: #0f766e;
  color: #fff;
}

.link-btn {
  font-size: 0.8rem;
  color: #4338ca;
  text-decoration: underline;
}

/* 一覧の表 */
.staff-table {
  min-width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 0.85rem;
  color: #1f2937;
}

.staff-table th,
.staff-table td {
  padding: 6px 10px;
  text-align: center;
  vertical-align: middle;
  white-space: nowrap;
  border-bottom: 1px solid #e5e7eb;
}

.staff-table thead th {
  position: sticky;
  top: 0;
  z-index: 1;
  background: #e0e7ff;
  color: #3730a3;
  font-weight: bold;
  font-size: 0.75rem;
  border-bottom-color: #c7d2fe;
}

.staff-table .text-left {
  text-align: left;
}

.staff-table th.check,
.staff-table td.check {
  width: 36px;
}

.staff-table td.code {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  color: #4b5563;
}

/* エリア名は小さめ（複数あると長くなるので） */
.staff-table td.area-cell {
  font-size: 0.75rem;
  color: #4b5563;
}

.staff-table tbody tr {
  cursor: pointer;
}

.staff-table tbody tr:hover td {
  background: #f8faff;
}

/* 選んだ行は薄い青 */
.staff-table tbody tr.checked td {
  background: #dbeafe;
}

/* 退職した人は名前以外を薄く */
.staff-table tbody tr.retired td {
  color: #9ca3af;
}

/* ---- 給与奉行のタブ ---- */
.bugyo-head {
  flex-wrap: wrap;
}

/* 「未出力の清掃員のみ」（ヘッダーの絞り込み）：seal-toggle（ON は藍色の地に白い太字）をほかの欄と同じ高さに */
.bugyo-chk {
  height: 40px;
  padding: 0 12px;
  background: #fff;
  font-size: 0.8rem;
  user-select: none;
}

.bugyo-table .birthday {
  white-space: nowrap;
}

/* 名前の列は中身（一番長い名前）の幅に縮める。余った幅は物件名・出力の列へ */
.bugyo-table th.name,
.bugyo-table td.name {
  width: 1%;
  white-space: nowrap;
}

/* 出力済みの行は薄い字＋薄いグレーの地（まだの人が目に入るように。ユーザー指示） */
.bugyo-table tbody tr.exported td {
  color: #6b7280;
}

.bugyo-table tbody tr.exported:not(.checked) td {
  background-color: #f3f4f6;
}

/* 閲覧だけの人は行を押しても何も起きない */
.bugyo-table tbody tr.view {
  cursor: default;
}

/* 「所属部門の決め方」はアイコン付きのリンク */
.dept-help-btn {
  display: inline-flex;
  align-items: center;
}

.bugyo-no-auth {
  display: block;
  margin-top: 2px;
  font-weight: bold;
  color: #dc2626;
}

.bugyo-table .export-done {
  white-space: nowrap;
}

.bugyo-table .export-user {
  margin-left: 6px;
  font-size: 0.75rem;
  color: #9ca3af;
}

.bugyo-table .export-not {
  font-weight: bold;
  color: #dc2626;
}

/* ---- 契約書返送のタブ ---- */
/* 行を押しても何も起きない（チェック列は無い。返送・備考は行の中の欄で即保存） */
.cr-table tbody tr {
  cursor: default;
}

/* 返送済みの行は薄い字＋薄いグレーの地（未返送の行が目に入るように。ユーザー指示） */
.cr-table tbody tr.returned td {
  color: #6b7280;
  background-color: #f3f4f6;
}

.cr-table .export-done,
.cr-table .export-user {
  white-space: nowrap;
}

.cr-table .export-user {
  margin-left: 6px;
  font-size: 0.75rem;
  color: #9ca3af;
}

/* 契約コードは物件名の右に小さく */
.cr-table .cr-contract {
  margin-left: 6px;
  padding: 0 6px;
  border: 1px solid #c7d2fe;
  border-radius: 3px;
  background: #f5f7ff;
  color: #4338ca;
  font-size: 0.75rem;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
}

/* 返送済みの切り替え（seal-toggle。ON は藍色）と、その下に日付と人 */
.cr-table .cr-ret {
  white-space: nowrap;
}

.cr-table .cr-ret-info {
  margin-top: 2px;
  font-size: 0.75rem;
  color: #6b7280;
  white-space: nowrap;
}

/* 返送済みの行でも同じ人に未返送があれば赤で（行は薄いグレーなので色は濃いめ） */
.cr-table tbody tr.returned td .cr-other-unreturned {
  margin-top: 2px;
  font-size: 0.75rem;
  font-weight: bold;
  color: #dc2626;
  white-space: nowrap;
}

/* QR コード読込（モーダル） */
.qr-box {
  width: 640px;
}

.qr-body {
  flex: 1;
  min-height: 0;
  overflow: auto;
  padding: 10px 14px 14px;
}

/* スマホ・タブレットの契約書返送：ヘッダーの高さが端末で変わるので絶対配置にせず、ふつうに下に流す（画面全体がスクロール） */
.cr-main-mobile {
  width: 100%;
  padding: 4px 0 16px;
}

.cr-main-mobile > .h-full {
  height: auto;
}

.cr-main-mobile .overflow-auto {
  max-height: 70vh;
}

.qr-input-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.qr-input {
  flex: 1;
  min-width: 0;
}

.qr-error {
  margin-top: 8px;
  font-weight: bold;
  color: #dc2626;
}

.qr-video-wrap {
  margin-top: 10px;
  text-align: center;
}

.qr-video {
  display: block;
  width: 100%;
  max-height: 320px;
  border-radius: 6px;
  background: #111;
  object-fit: cover;
}

.qr-video-note {
  margin-top: 4px;
  font-size: 0.75rem;
  color: #6b7280;
}

/* 読めた瞬間に映像の枠を緑に */
.qr-video-wrap.flash .qr-video {
  box-shadow: 0 0 0 4px #22c55e;
}

/* 読んだ数 */
.qr-count {
  margin-top: 10px;
  font-size: 0.85rem;
  color: #374151;
}

.qr-count b {
  font-size: 1.1rem;
  color: #15803d;
}

.qr-count-ng {
  margin-left: 6px;
  color: #b91c1c;
}

/* 読んだ結果（新しいものが上） */
.qr-results {
  margin-top: 10px;
  max-height: 260px;
  overflow: auto;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
}

.qr-empty {
  padding: 14px;
  text-align: center;
  color: #9ca3af;
}

.qr-result {
  display: flex;
  gap: 10px;
  padding: 6px 10px;
  border-bottom: 1px solid #f3f4f6;
  font-size: 0.85rem;
}

.qr-result.ok {
  background: #f0fdf4;
}

.qr-result.ng {
  background: #fef2f2;
  color: #b91c1c;
}

.qr-result-time {
  flex: none;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  color: #6b7280;
}

.qr-result-body {
  flex: 1;
  min-width: 0;
}

.qr-result-state {
  margin-left: 8px;
  padding: 0 6px;
  border-radius: 999px;
  background: #15803d;
  color: #fff;
  font-size: 0.75rem;
  font-weight: bold;
}

.qr-result-state.already {
  background: #6b7280;
}

/* カメラが動いている間は「カメラを止める」を緑に（出力実行と同じ色） */
.qr-body .run-btn {
  background-color: #15803d;
}

.qr-body .run-btn:hover {
  background-color: #166534;
}

.staff-table .kana {
  font-size: 0.7rem;
  color: #6b7280;
  line-height: 1.1;
}

.staff-table .name-link {
  font-weight: bold;
  color: #1d4ed8;
  font-size: 0.95rem;
}

.staff-table .name-link:hover {
  text-decoration: underline;
}

.staff-table .tag {
  margin-left: 6px;
  padding: 1px 6px;
  border-radius: 4px;
  font-size: 0.7rem;
  font-weight: bold;
}

.staff-table .tag.retired {
  background: #e5e7eb;
  color: #4b5563;
}

.staff-table .tag.cancelled {
  background: #fee2e2;
  color: #b91c1c;
}

.staff-table .age {
  margin-left: 4px;
  font-size: 0.75rem;
  color: #6b7280;
}

.staff-table .photo-yes {
  font-weight: bold;
  color: #15803d;
}

.staff-table .photo-no {
  color: #9ca3af;
}

/* 写真が要るのに無い */
.staff-table .photo-need {
  font-weight: bold;
  color: #dc2626;
}

/* 顔写真の登録・差し替え・削除（あり／なしの下に小さく） */
.staff-table td.photo {
  white-space: nowrap;
}

.photo-btns {
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-top: 2px;
  font-size: 0.75rem;
}

.photo-btns label.link-btn {
  cursor: pointer;
}

/* 出力する勤務の札（物件名・契約コード・開始日）。ON は藍 */
.staff-table th.emp,
.staff-table td.emp {
  text-align: left;
  min-width: 260px;
  white-space: normal;
}

/* 見出しの補足は 2 段目に（ユーザー指示 2026-10-09） */
.th-note {
  display: block;
  margin-top: 2px;
  font-weight: normal;
  font-size: 0.7rem;
  color: #6b7280;
}

.emp-none {
  font-size: 0.75rem;
  color: #9ca3af;
}

/* 出力する勤務：1勤務1行のチェックボックス（行のチェックと同じ見た目）。チェックした行は太字 */
.emp-list {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.emp-item {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  max-width: 100%;
  color: #374151;
  font-size: 0.75rem;
  white-space: nowrap;
  cursor: pointer;
}

.emp-item:hover {
  color: #1d4ed8;
}

/* 見るだけ（業務報告書・雇用契約書が OFF）：チェック無し、色も変えない */
.emp-item.view {
  cursor: default;
}

.emp-item.view:hover {
  color: #374151;
}

.emp-item.is-on {
  font-weight: bold;
  color: #1f2937;
}

.emp-item .emp-site {
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
}

.emp-item .emp-con {
  font-size: 0.7rem;
  color: #4b5563;
}

/* 「代勤」の枚（実績だけ）は勤務と見分けられるように色を変える */
.emp-item.op .emp-site {
  color: #92400e;
}

.emp-item.op.is-on .emp-site {
  color: #78350f;
}

/* 出力中の白いマスク */
.progress-mask {
  position: fixed;
  inset: 0;
  z-index: 80;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.75);
  cursor: wait;
}

.progress-box {
  width: 420px;
  padding: 20px 24px;
  border-radius: 8px;
  background: #fff;
  border: 1px solid #c7d2fe;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  text-align: center;
}

.progress-text {
  font-weight: bold;
  color: #312e81;
}

.progress-bar {
  margin-top: 12px;
  height: 14px;
  border-radius: 999px;
  background: #e5e7eb;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: #4338ca;
  transition: width 0.3s;
}

.progress-percent {
  margin-top: 6px;
  font-size: 1.25rem;
  font-weight: bold;
  color: #1f2937;
}

/* 作っている PDF の「中止」（マスクの中。押したら押せなくなり「中止しています…」） */
.progress-cancel {
  margin-top: 14px;
  height: 36px;
  padding: 0 28px;
  border-radius: 6px;
  border: 1px solid #b91c1c;
  background: #fff;
  color: #b91c1c;
  font-weight: bold;
  cursor: pointer;
}

.progress-cancel:hover:not(:disabled) {
  background: #fef2f2;
}

.progress-cancel:disabled {
  border-color: #d1d5db;
  color: #9ca3af;
  cursor: wait;
}

/* 会社印の得意先のボタン（ヘッダーの右） */
.seal-btn {
  display: inline-flex;
  align-items: center;
  height: 28px;
  padding: 0 10px;
  border-radius: 4px;
  border: 1px solid #a5b4fc;
  background: #fff;
  color: #3730a3;
  font-size: 0.8rem;
  font-weight: bold;
  white-space: nowrap;
}

.seal-btn:hover {
  background: #e0e7ff;
}

/* 会社印の得意先のモーダル */
.seal-overlay {
  position: fixed;
  inset: 0;
  z-index: 70;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.35);
}

.seal-box {
  display: flex;
  flex-direction: column;
  width: 720px;
  max-width: 96vw;
  max-height: 86vh;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
}

.seal-head {
  position: relative;
  padding: 12px 16px 8px;
  border-bottom: 1px solid #e5e7eb;
}

.seal-title {
  font-size: 1rem;
  font-weight: bold;
  color: #312e81;
}

.seal-desc {
  margin-top: 2px;
  font-size: 0.8rem;
  color: #4b5563;
}

.seal-close {
  position: absolute;
  top: 8px;
  right: 12px;
  font-size: 1.25rem;
  color: #6b7280;
}

.seal-filter {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 16px;
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
}

.seal-limit {
  line-height: 1.3;
}

.seal-list {
  flex: 1;
  min-height: 200px;
  overflow: auto;
}

.seal-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.85rem;
}

.seal-table th,
.seal-table td {
  padding: 6px 12px;
  border-bottom: 1px solid #e5e7eb;
  text-align: center;
  white-space: nowrap;
}

.seal-table th {
  position: sticky;
  top: 0;
  background: #e0e7ff;
  color: #3730a3;
  font-size: 0.75rem;
}

.seal-table .text-left {
  text-align: left;
}

.seal-table td.code {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  color: #4b5563;
}

.seal-table tr.is-on td {
  background: #eef2ff;
}

.seal-toggle {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 2px 10px;
  border-radius: 999px;
  border: 1px solid #d1d5db;
  color: #6b7280;
  font-size: 0.8rem;
  cursor: pointer;
}

.seal-toggle.is-on {
  background: #4338ca;
  border-color: #4338ca;
  color: #fff;
  font-weight: bold;
}

/* ---------- 退職日の確認（モーダル。会社印のモーダルの見た目を使う） ---------- */
.retire-box {
  width: 560px;
}

/* 提出先市町村（モーダル）。書類設定と同じく行ごとに入力して保存。列の幅を決めて右の「保存」が切れないように */
.city-box {
  width: 600px;
}

.city-table {
  table-layout: fixed;
}

.city-table th,
.city-table td {
  padding: 4px 10px;
}

.city-table .code-col {
  width: 130px;
}

.city-table th.save,
.city-table td.save {
  width: 150px;
}

.city-table .code-input {
  width: 100px;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
}

.city-table .setting-input {
  width: 100%;
}

.city-table tr.dirty td {
  background: #fffbeb;
}

/* 所属部門の決め方（モーダル）。区の多い行（124）が読めるように、区は 1 つずつ札にして折り返す */
.dept-box {
  width: 640px;
}

.dept-table td {
  white-space: normal;
  vertical-align: top;
}

.dept-table td.code {
  font-weight: bold;
  color: #312e81;
}

.dept-table .dept-name {
  font-weight: bold;
  white-space: nowrap;
}

.dept-area {
  display: inline-block;
  margin: 2px 6px 2px 0;
  padding: 1px 8px;
  border-radius: 999px;
  background: #eef2ff;
  color: #312e81;
  font-size: 0.8rem;
  white-space: nowrap;
}

.dept-notes {
  padding: 10px 12px 12px;
  font-size: 0.8rem;
  color: #6b7280;
  line-height: 1.6;
}

/* 見出しは赤く大きく（承認前に出すことを強く知らせる。ユーザー指示） */
.retire-title {
  font-size: 1.25rem;
  color: #dc2626;
}

.retire-list {
  min-height: 0;
  max-height: 50vh;
}

.seal-table td.retire-date {
  padding-top: 3px;
  padding-bottom: 3px;
}

.retire-date .date-field {
  display: inline-block;
  width: 130px;
}

.retire-date .date-field >>> input {
  height: 32px;
  font-weight: bold;
  text-align: center;
}

/* 退職日が空の欄は赤く（期間の欄と同じ） */
.retire-date .date-field.empty >>> input {
  background: #fca5a5;
  border-color: #dc2626;
}

.retire-foot {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  padding: 10px 16px;
  border-top: 1px solid #e5e7eb;
  background: #f9fafb;
}

/* 「出力する」は出力実行と同じ緑。押せないときはグレー */
.retire-foot .run-btn {
  background-color: #15803d;
}

.retire-foot .run-btn:hover {
  background-color: #166534;
}

.retire-foot .run-btn:disabled {
  background-color: #9ca3af;
}

/* ---------- 書類設定 ---------- */
.setting-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.85rem;
  color: #1f2937;
}

.setting-table th,
.setting-table td {
  padding: 8px 12px;
  border-bottom: 1px solid #e5e7eb;
  text-align: center;
  vertical-align: middle;
  white-space: nowrap;
}

.setting-table th {
  position: sticky;
  top: 0;
  z-index: 1;
  background: #e0e7ff;
  color: #3730a3;
  font-size: 0.75rem;
  border-bottom-color: #c7d2fe;
}

.setting-table .text-left {
  text-align: left;
}

/* システムが作る書類の行は薄い藍（「システム」の札と同じ系統）。ファイルをアップする書類は白のまま */
.setting-table tr.system td {
  background: #eef2ff;
}

/* 業務報告書に付ける代勤賃金簿の行も薄い藍（札に出ない特別な行。PDF は固定書類と同じく上げられる） */
.setting-table tr.op-sheet td {
  background: #eef2ff;
}

/* 保存していない行は薄い黄色（システムの行の色より優先）、使わない行は薄く */
.setting-table tr.dirty td {
  background: #fefce8;
}

.setting-table tr.off td {
  color: #9ca3af;
}

.setting-table th.order,
.setting-table td.order {
  width: 60px;
}

.setting-table td.save {
  white-space: nowrap;
}

/* 保存と削除は左寄せで並べ、削除の幅は常に確保して保存ボタンの位置をそろえる */
.save-cell {
  display: flex;
  align-items: center;
  gap: 12px;
}

.del-slot {
  display: inline-block;
  width: 36px;
  text-align: left;
}

/* 行の保存は緑（出力実行と同じ）。押せないときはグレー */
.setting-table .run-btn {
  background-color: #15803d;
}

.setting-table .run-btn:hover {
  background-color: #166534;
}

.setting-table .run-btn:disabled {
  background-color: #9ca3af;
}

.order-btn {
  padding: 0 4px;
  color: #6b7280;
  font-size: 0.7rem;
}

.order-btn:hover:not(:disabled) {
  color: #1d4ed8;
}

.order-btn:disabled {
  color: #d1d5db;
  cursor: default;
}

.setting-table th.name,
.setting-table td.name {
  text-align: left;
}

/* 書類名の欄は固定の幅（ファイルの列が幅いっぱいなので、割合だと潰れる） */
.setting-input {
  width: 300px;
  height: 32px;
  padding: 0 8px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 0.9rem;
  font-weight: bold;
}

.setting-input.empty {
  background: #fee2e2;
  border-color: #fca5a5;
}

/* 一式の札（押すと ON／OFF。ON は青緑） */
.setting-table td.sets {
  text-align: left;
}

.set-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-right: 6px;
  padding: 2px 10px;
  border-radius: 999px;
  border: 1px solid #d1d5db;
  color: #6b7280;
  font-size: 0.8rem;
  cursor: pointer;
  user-select: none;
}

.set-chip input {
  display: none;
}

.set-chip.is-on {
  background: #0f766e;
  border-color: #0f766e;
  color: #fff;
  font-weight: bold;
}

/* ファイル：今のファイル・差し替え・ボタン */
.setting-table th.file,
.setting-table td.file {
  width: 100%;
  text-align: left;
}

/* ファイル列の見出し（赤の太文字。出力に使うのは PDF だけ） */
.setting-table th.file .file-note {
  font-weight: bold;
  color: #dc2626;
}

/* ファイルの 1 行（並び・名前・大きさなど・差し替え・ダウンロード・削除） */
.file-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 2px 0;
}

.file-row.is-new .file-name {
  color: #b45309;
}

/* 原本（Word・Excel）は出力に使わないので薄く、札を付ける */
.file-row.is-source .file-name {
  color: #6b7280;
  font-weight: normal;
}

.file-row .file-order {
  white-space: nowrap;
}

.file-row .file-name {
  font-weight: bold;
}

.file-row .file-meta {
  font-size: 0.75rem;
  color: #6b7280;
}

.file-none {
  color: #b91c1c;
  font-size: 0.8rem;
}

.file-new {
  color: #b45309;
  font-size: 0.75rem;
  font-weight: bold;
}

.file-hint {
  margin-top: 4px;
  font-size: 0.75rem;
  color: #6b7280;
}

/* 「ファイルを追加」の列（細く。ボタンは行の縦の真ん中） */
.setting-table th.add,
.setting-table td.add {
  width: 1%;
  white-space: nowrap;
}

.file-pick {
  display: inline-flex;
  align-items: center;
  height: 26px;
  padding: 0 10px;
  border-radius: 4px;
  border: 1px solid #a5b4fc;
  background: #fff;
  color: #3730a3;
  font-size: 0.8rem;
  font-weight: bold;
  cursor: pointer;
}

.file-pick:hover {
  background: #eef2ff;
}

.file-pick.small {
  height: 22px;
  padding: 0 6px;
  font-size: 0.75rem;
  font-weight: normal;
}

.link-btn.danger {
  color: #b91c1c;
}

/* システムが作る書類の印（名前の横）と、ファイルの列の文 */
.tag-system {
  margin-left: 8px;
  padding: 1px 8px;
  border-radius: 4px;
  background: #e0e7ff;
  color: #3730a3;
  font-size: 0.7rem;
  font-weight: bold;
}

.file-system {
  color: #6b7280;
  font-size: 0.8rem;
}

/* 備考は幅いっぱい。欄を離れたら保存 */
.staff-table th.memo,
.staff-table td.memo {
  width: 100%;
  min-width: 260px;
  text-align: left;
}

.memo-input {
  display: block;
  width: 100%;
  min-height: 28px;
  padding: 3px 6px;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  font-size: 0.8rem;
  line-height: 1.4;
  /* 改行した分だけ高さが伸びる（ほかの画面と同じ CSS）。手で伸ばすつまみは無し */
  field-sizing: content;
  resize: none;
  background: #fff;
}

.memo-input:focus {
  border-color: #818cf8;
  outline: none;
}


/* ページネーション（申請一覧・登録済み一覧と同じ見た目。出力・給与奉行・契約書返送の 3 つ） */
#pagenation >>> ul,
#bugyoPagenation >>> ul,
#crPagenation >>> ul {
  display: flex !important;
  color: rgb(6, 125, 194);
  font-weight: bold !important;
  cursor: pointer;
}

#pagenation >>> ul > li,
#bugyoPagenation >>> ul > li,
#crPagenation >>> ul > li {
  padding: 0 10px;
}

#pagenation >>> ul > .active,
#bugyoPagenation >>> ul > .active,
#crPagenation >>> ul > .active {
  background: rgb(6, 125, 194);
  color: white;
}

#pagenation >>> p,
#bugyoPagenation >>> p,
#crPagenation >>> p {
  color: gray;
  font-size: small;
}
</style>
